/**
 * Data Source Configuration Component
 *
 * Manages data source connections dynamically from configuration.
 * NO HARDCODED DATA - all sources come from DataSourceContext.
 */

import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Database,
  Download,
  Eye,
  EyeOff,
  File,
  FileCheck,
  Filter,
  GitMerge,
  History,
  Info,
  Loader2,
  MousePointerClick,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ApiError,
  apiDownloadBlob,
  apiUpload,
  type ColumnMappingPreviewResponse,
  type ColumnSchemaField,
  getColumnMappingPreview,
  type UploadFileResponse,
  type ValidateExcelDataSourceResponse,
  validateExcelDataSource,
} from "@/api/client";
import type {
  CreateDataSourceRequestApi,
  DataSourceInstanceApi,
  DataSourceListItemApi,
  DataSourceTypeApi,
  NominalCategoryLabelsResponseApi,
  UpdateDataSourceRequestApi,
} from "@/api/models/dataSourceApi";
import {
  createDataSource,
  deleteDataSource,
  getAllDataSources,
  getDataSourceById,
  getDataSourceRefreshStatus,
  getDataSourceTypes,
  getDataSourceTypeWithInstances,
  getNominalCategoryLabels,
  getRefreshSchedule,
  triggerDataSourceRefresh,
  updateDataSource as updateDataSourceApi,
  updateRefreshSchedule,
} from "@/api/services/dataSourceService";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApp } from "@/contexts/AppContext";
import { useDataSource } from "@/contexts/DataSourceContext";
import { useTerminology } from "@/hooks/useTerminology";
import { formatRelativeSyncTime } from "@/lib/formatting";
import {
  type CloudProvider,
  type DatabaseType,
  type DataSourceConfig,
  DataSourceMappingConfig,
  type DataSourceType,
  type ExcelAmountFormat,
  type ExcelColumnMap,
  type ExcelDateFormat,
  type ExcelDecimalSeparator,
  type ExcelFileMode,
  type ExcelFormatDeclaration,
  type ExcelNegativeFormat,
  type ExcelPathType,
  type ExcelThousandsSeparator,
  type ExcelValidationBehaviour,
  type ExcelWorksheetConfig,
  type SyncSchedule,
} from "@/types/dataSource";
import {
  apiScheduleToSyncSchedule,
  getScheduleIndicator,
  syncScheduleToApiRequest,
} from "@/utils/syncScheduleHelpers";
import { SyncHistoryModal } from "./SyncHistoryModal";
import { SyncScheduleModal } from "./SyncScheduleModal";

// ── Generic Source Type ──────────────────────────────────────────────────────
// Maps the stored product name to a generic category label shown in the UI.
// The stored type is always the product name (e.g. "Access Financials") — this
// is display-only. Separates "what category is this" from "which product is it".
/**
 * Returns sensible pre-checked transaction type defaults for each source category.
 * All types remain available — this just sets the starting selection intelligently.
 */
function getDefaultTransactionTypes(genericType: string) {
  switch (genericType) {
    case "Finance":
      // Finance systems commonly hold the full ledger set — sales, purchase, cashbook core
      // Sales/purchase orders and payroll are possible but less universal, left for user to enable
      return {
        salesLedger: true,
        purchaseLedger: true,
        cashbook: true,
        bankAccounts: false,
        salesOrders: false,
        purchaseOrders: false,
        payroll: false,
      };
    case "Payroll":
      // Payroll systems: payroll is the primary data type — nothing else pre-assumed
      return {
        salesLedger: false,
        purchaseLedger: false,
        cashbook: false,
        bankAccounts: false,
        salesOrders: false,
        purchaseOrders: false,
        payroll: true,
      };
    case "Banking":
      // Banking/open banking: bank accounts and cashbook transactions are the natural fit
      return {
        salesLedger: false,
        purchaseLedger: false,
        cashbook: true,
        bankAccounts: true,
        salesOrders: false,
        purchaseOrders: false,
        payroll: false,
      };
    case "Excel":
      // Excel is the universal catch-all — nothing pre-assumed, user knows what's in their file
      return {
        salesLedger: false,
        purchaseLedger: false,
        cashbook: false,
        bankAccounts: false,
        salesOrders: false,
        purchaseOrders: false,
        payroll: false,
      };
    default:
      return {
        salesLedger: false,
        purchaseLedger: false,
        cashbook: false,
        bankAccounts: false,
        salesOrders: false,
        purchaseOrders: false,
        payroll: false,
      };
  }
}

function getGenericSourceType(type: string): string {
  if (type === "Access Financials" || type === "Access Dimensions Cloud")
    return "Finance";
  if (type === "Paycircle") return "Payroll";
  if (type === "Plaid") return "Banking";
  if (type === "Excel Spreadsheet") return "Excel";
  return type;
}

// ── Transaction Type Definitions (3×3 grid) ──────────────────────────────────
type TxTypeKey =
  | "salesLedger"
  | "purchaseLedger"
  | "cashbook"
  | "salesOrders"
  | "purchaseOrders"
  | "payroll"
  | "bankAccounts"
  | "crm"
  | "budgetPlanning"
  | "projectCosting";

interface TxTypeDefinition {
  key: TxTypeKey;
  label: string;
  description: string;
  /** Which generic source types this transaction type is relevant for */
  relevantFor: string[];
  /** R2/R3 = greyed out, not yet available */
  release?: "R2" | "R3";
}

const TX_TYPE_GRID: TxTypeDefinition[][] = [
  // Row 1 — Finance Core
  // Excel is relevant for ALL R1 types — it is the catch-all for any data source without an API
  [
    {
      key: "salesLedger",
      label: "Sales Ledger",
      description: "Invoices & customer receipts",
      relevantFor: ["Finance", "Excel"],
    },
    {
      key: "salesOrders",
      label: "Sales Orders",
      description: "Committed future customer receipts",
      relevantFor: ["Finance", "Excel"],
    },
    {
      key: "payroll",
      label: "Payroll",
      description: "Pay runs & employer obligations",
      relevantFor: ["Finance", "Payroll", "Excel"],
    },
  ],
  // Row 2 — Purchase & Payroll
  [
    {
      key: "purchaseLedger",
      label: "Purchase Ledger",
      description: "Bills & supplier payments",
      relevantFor: ["Finance", "Excel"],
    },
    {
      key: "purchaseOrders",
      label: "Purchase Orders",
      description: "Committed future supplier payments",
      relevantFor: ["Finance", "Excel"],
    },
    {
      key: "budgetPlanning",
      label: "Budget & Planning",
      description: "Budget vs forecast comparison",
      relevantFor: ["Finance", "Excel"],
      release: "R3",
    },
  ],
  // Row 3 — Cash & Banking
  [
    {
      key: "cashbook",
      label: "Cashbook",
      description: "Posted & recurring bank transactions",
      relevantFor: ["Finance", "Banking", "Excel"],
    },
    {
      key: "bankAccounts",
      label: "Bank Accounts",
      description: "Account balances & bank feeds",
      relevantFor: ["Finance", "Banking", "Excel"],
    },
    {
      key: "crm",
      label: "CRM",
      description: "Pipeline & opportunity cash flow",
      relevantFor: ["Finance", "Excel"],
      release: "R2",
    },
  ],
];

// ── Field Mapping Schema per Transaction Type ─────────────────────────────────
// Defines what fields the forecaster expects from each transaction type,
// and the pre-populated field names for known products.
interface FieldMappingRow {
  forecasterId: string; // Internal forecaster field ID
  forecasterLabel: string; // Human label for the forecaster field
  description: string; // What this field is used for
  required: boolean;
}

interface ProductFieldMapping {
  [productFieldName: string]: string; // product field name → forecaster field ID
}

interface TxTypeMappingSchema {
  fields: FieldMappingRow[];
  productDefaults: Record<string, ProductFieldMapping>; // product name → mapping
}

const TX_TYPE_MAPPING_SCHEMAS: Partial<Record<TxTypeKey, TxTypeMappingSchema>> =
  {
    salesLedger: {
      fields: [
        {
          forecasterId: "transaction_date",
          forecasterLabel: "Transaction Date",
          description: "Date the invoice was raised",
          required: true,
        },
        {
          forecasterId: "due_date",
          forecasterLabel: "Due Date",
          description: "Expected payment receipt date",
          required: true,
        },
        {
          forecasterId: "amount",
          forecasterLabel: "Amount",
          description: "Invoice net amount (base currency)",
          required: true,
        },
        {
          forecasterId: "currency",
          forecasterLabel: "Currency",
          description: "ISO currency code (e.g. GBP)",
          required: false,
        },
        {
          forecasterId: "counterparty",
          forecasterLabel: "Customer Name",
          description: "Customer / debtor name",
          required: true,
        },
        {
          forecasterId: "reference",
          forecasterLabel: "Reference",
          description: "Invoice or transaction reference",
          required: false,
        },
        {
          forecasterId: "status",
          forecasterLabel: "Status",
          description: "Outstanding, Part-paid, Paid, Overdue",
          required: true,
        },
        {
          forecasterId: "category",
          forecasterLabel: "Category",
          description: "Nominal code or cost centre for grouping",
          required: false,
        },
      ],
      productDefaults: {
        "Access Financials": {
          invoice_date: "transaction_date",
          payment_due: "due_date",
          net_amount: "amount",
          currency_code: "currency",
          customer_name: "counterparty",
          inv_reference: "reference",
          status: "status",
          nominal_code: "category",
        },
        "Access Dimensions Cloud": {
          TransactionDate: "transaction_date",
          DueDate: "due_date",
          Amount: "amount",
          Currency: "currency",
          CustomerName: "counterparty",
          Reference: "reference",
          Status: "status",
          NominalCode: "category",
        },
        "Sage Business Cloud": {
          InvoiceDate: "transaction_date",
          DueDate: "due_date",
          NetAmount: "amount",
          Currency: "currency",
          CustomerName: "counterparty",
          InvoiceNumber: "reference",
          Status: "status",
          NominalCode: "category",
        },
        Xero: {
          DateString: "transaction_date",
          DueDateString: "due_date",
          AmountDue: "amount",
          CurrencyCode: "currency",
          ContactName: "counterparty",
          InvoiceNumber: "reference",
          Status: "status",
          AccountCode: "category",
        },
        "QuickBooks Online": {
          TxnDate: "transaction_date",
          DueDate: "due_date",
          Balance: "amount",
          CurrencyRef: "currency",
          CustomerRef: "counterparty",
          DocNumber: "reference",
          PaymentStatus: "status",
          AccountRef: "category",
        },
      },
    },
    purchaseLedger: {
      fields: [
        {
          forecasterId: "account_code",
          forecasterLabel: "Account Code",
          description: "Supplier account code",
          required: false,
        },
        {
          forecasterId: "supplier_name",
          forecasterLabel: "Supplier Name",
          description: "Supplier name",
          required: true,
        },
        {
          forecasterId: "description",
          forecasterLabel: "Description",
          description: "Transaction description / narrative",
          required: false,
        },
        {
          forecasterId: "gross",
          forecasterLabel: "Gross",
          description: "Gross amount (incl. VAT)",
          required: true,
        },
        {
          forecasterId: "transaction_type",
          forecasterLabel: "Transaction Type",
          description: "Invoice / Payment / CreditNote / etc.",
          required: true,
        },
        {
          forecasterId: "outstanding",
          forecasterLabel: "Outstanding",
          description: "Outstanding amount (if different from Gross)",
          required: true,
        },
        {
          forecasterId: "transaction_date",
          forecasterLabel: "Transaction Date",
          description: "Date of the transaction",
          required: true,
        },
        {
          forecasterId: "due_date",
          forecasterLabel: "Due Date",
          description: "Expected payment date",
          required: true,
        },
        {
          forecasterId: "anticipated_date",
          forecasterLabel: "Anticipated Payment Date",
          description: "Anticipated payment date (if known)",
          required: false,
        },
        {
          forecasterId: "category",
          forecasterLabel: "Category",
          description: "Nominal category / cost group",
          required: false,
        },
      ],
      productDefaults: {
        "Access Financials": {
          account_code: "account_code",
          supplier_name: "supplier_name",
          description: "description",
          gross: "gross",
          transaction_type: "transaction_type",
          outstanding: "outstanding",
          invoice_date: "transaction_date",
          payment_due: "due_date",
          anticipated_date: "anticipated_date",
          nominal_code: "category",
        },
        "Access Dimensions Cloud": {
          AccountCode: "account_code",
          SupplierName: "supplier_name",
          Description: "description",
          Gross: "gross",
          TransactionType: "transaction_type",
          Outstanding: "outstanding",
          TransactionDate: "transaction_date",
          DueDate: "due_date",
          AnticipatedPaymentDate: "anticipated_date",
          NominalCode: "category",
        },
        "Sage Business Cloud": {
          AccountCode: "account_code",
          SupplierName: "supplier_name",
          Description: "description",
          Gross: "gross",
          TransactionType: "transaction_type",
          Outstanding: "outstanding",
          TransactionDate: "transaction_date",
          DueDate: "due_date",
          AnticipatedPaymentDate: "anticipated_date",
          NominalCode: "category",
        },
        Xero: {
          AccountCode: "account_code",
          ContactName: "supplier_name",
          Description: "description",
          Total: "gross",
          Type: "transaction_type",
          AmountDue: "outstanding",
          DateString: "transaction_date",
          DueDateString: "due_date",
          PlannedPaymentDate: "anticipated_date",
          AccountCode2: "category",
        },
        "QuickBooks Online": {
          AccountCode: "account_code",
          VendorRef: "supplier_name",
          Description: "description",
          TotalAmt: "gross",
          TransactionType: "transaction_type",
          Balance: "outstanding",
          TxnDate: "transaction_date",
          DueDate: "due_date",
          ShipDate: "anticipated_date",
          AccountRef: "category",
        },
      },
    },
    cashbook: {
      fields: [
        {
          forecasterId: "transaction_date",
          forecasterLabel: "Transaction Date",
          description: "Date the transaction posted",
          required: true,
        },
        {
          forecasterId: "description",
          forecasterLabel: "Description",
          description: "Transaction narrative",
          required: false,
        },
        {
          forecasterId: "reference",
          forecasterLabel: "Reference",
          description: "Payment reference or cheque no.",
          required: false,
        },
        {
          forecasterId: "debit",
          forecasterLabel: "Debit Amount",
          description: "Money out (leave blank if credit)",
          required: false,
        },
        {
          forecasterId: "credit",
          forecasterLabel: "Credit Amount",
          description: "Money in (leave blank if debit)",
          required: false,
        },
        {
          forecasterId: "category",
          forecasterLabel: "Category",
          description: "Cost category or nominal group",
          required: false,
        },
      ],
      productDefaults: {
        "Access Financials": {
          transaction_date: "transaction_date",
          description: "description",
          reference: "reference",
          debit_amount: "debit",
          credit_amount: "credit",
          nominal_code: "category",
        },
        "Access Dimensions Cloud": {
          TransactionDate: "transaction_date",
          Narrative: "description",
          Reference: "reference",
          DebitAmount: "debit",
          CreditAmount: "credit",
          NominalCode: "category",
        },
        "Sage Business Cloud": {
          TransactionDate: "transaction_date",
          Details: "description",
          Reference: "reference",
          DebitAmount: "debit",
          CreditAmount: "credit",
          NominalCode: "category",
        },
        Xero: {
          Date: "transaction_date",
          Particulars: "description",
          Reference: "reference",
          DebitAmount: "debit",
          CreditAmount: "credit",
          AccountCode: "category",
        },
      },
    },
    salesOrders: {
      fields: [
        {
          forecasterId: "order_number",
          forecasterLabel: "Order Number",
          description: "Unique order reference",
          required: true,
        },
        {
          forecasterId: "account_code",
          forecasterLabel: "Account Code",
          description: "Customer account code",
          required: false,
        },
        {
          forecasterId: "account_name",
          forecasterLabel: "Customer Name",
          description: "Customer / client name",
          required: true,
        },
        {
          forecasterId: "description",
          forecasterLabel: "Description",
          description: "Order description / narrative",
          required: false,
        },
        {
          forecasterId: "gross_outstanding",
          forecasterLabel: "Gross Outstanding",
          description: "Gross outstanding amount",
          required: true,
        },
        {
          forecasterId: "order_date",
          forecasterLabel: "Order Date",
          description: "Date the order was placed",
          required: true,
        },
        {
          forecasterId: "required_date",
          forecasterLabel: "Required Date",
          description: "Expected cash receipt date",
          required: true,
        },
        {
          forecasterId: "analysis_code",
          forecasterLabel: "Analysis Code",
          description: "Analysis code for categorisation",
          required: false,
        },
        {
          forecasterId: "category",
          forecasterLabel: "Category",
          description: "Nominal category / cost group",
          required: false,
        },
      ],
      productDefaults: {
        "Access Financials": {
          order_number: "order_number",
          account_code: "account_code",
          account_name: "account_name",
          description: "description",
          gross_outstanding: "gross_outstanding",
          order_date: "order_date",
          required_date: "required_date",
          analysis_code: "analysis_code",
          category: "category",
        },
        "Access Dimensions Cloud": {
          OrderNumber: "order_number",
          AccountCode: "account_code",
          AccountName: "account_name",
          Detail: "description",
          GrossOutstanding: "gross_outstanding",
          OrderDate: "order_date",
          RequiredDate: "required_date",
          AnalysisCode: "analysis_code",
          Category: "category",
        },
      },
    },
    purchaseOrders: {
      fields: [
        {
          forecasterId: "order_number",
          forecasterLabel: "Order Number",
          description: "Unique purchase order reference",
          required: true,
        },
        {
          forecasterId: "account_code",
          forecasterLabel: "Account Code",
          description: "Supplier account code",
          required: false,
        },
        {
          forecasterId: "account_name",
          forecasterLabel: "Supplier Name",
          description: "Supplier / vendor name",
          required: true,
        },
        {
          forecasterId: "description",
          forecasterLabel: "Description",
          description: "Order description / narrative",
          required: false,
        },
        {
          forecasterId: "gross_outstanding",
          forecasterLabel: "Gross Outstanding",
          description: "Gross outstanding amount",
          required: true,
        },
        {
          forecasterId: "order_date",
          forecasterLabel: "Order Date",
          description: "Date the PO was raised",
          required: true,
        },
        {
          forecasterId: "required_date",
          forecasterLabel: "Required Date",
          description: "Expected cash payment date",
          required: true,
        },
        {
          forecasterId: "analysis_code",
          forecasterLabel: "Analysis Code",
          description: "Analysis code for categorisation",
          required: false,
        },
        {
          forecasterId: "category",
          forecasterLabel: "Category",
          description: "Nominal category / cost group",
          required: false,
        },
      ],
      productDefaults: {
        "Access Financials": {
          order_number: "order_number",
          account_code: "account_code",
          account_name: "account_name",
          description: "description",
          gross_outstanding: "gross_outstanding",
          order_date: "order_date",
          required_date: "required_date",
          analysis_code: "analysis_code",
          category: "category",
        },
        "Access Dimensions Cloud": {
          PONumber: "order_number",
          AccountCode: "account_code",
          AccountName: "account_name",
          Detail: "description",
          GrossOutstanding: "gross_outstanding",
          OrderDate: "order_date",
          RequiredDate: "required_date",
          AnalysisCode: "analysis_code",
          Category: "category",
        },
      },
    },
    payroll: {
      fields: [
        {
          forecasterId: "employee_id",
          forecasterLabel: "Employee ID",
          description: "Unique employee reference",
          required: true,
        },
        {
          forecasterId: "employee_name",
          forecasterLabel: "Employee Name",
          description: "Full name",
          required: false,
        },
        {
          forecasterId: "pay_period",
          forecasterLabel: "Pay Period",
          description: "Month / week reference",
          required: true,
        },
        {
          forecasterId: "pay_date",
          forecasterLabel: "Pay Date",
          description: "Date cash leaves the bank",
          required: true,
        },
        {
          forecasterId: "gross_pay",
          forecasterLabel: "Gross Pay",
          description: "Gross salary / wages",
          required: true,
        },
        {
          forecasterId: "employer_ni",
          forecasterLabel: "Employer NI",
          description: "Employer National Insurance",
          required: false,
        },
        {
          forecasterId: "pension",
          forecasterLabel: "Pension",
          description: "Employer pension contribution",
          required: false,
        },
        {
          forecasterId: "net_pay",
          forecasterLabel: "Net Pay",
          description: "Net cash paid to employee",
          required: true,
        },
      ],
      productDefaults: {
        Paycircle: {
          employee_id: "employee_id",
          employee_name: "employee_name",
          pay_period: "pay_period",
          payment_date: "pay_date",
          gross_pay: "gross_pay",
          employer_ni: "employer_ni",
          pension_contribution: "pension",
          net_pay: "net_pay",
        },
      },
    },
    bankAccounts: {
      fields: [
        {
          forecasterId: "account_name",
          forecasterLabel: "Account Name",
          description: "Descriptive name for the account",
          required: true,
        },
        {
          forecasterId: "bank_name",
          forecasterLabel: "Bank Name",
          description: "Name of the bank",
          required: true,
        },
        {
          forecasterId: "sort_code",
          forecasterLabel: "Sort Code",
          description: "Bank sort code (UK)",
          required: false,
        },
        {
          forecasterId: "account_number",
          forecasterLabel: "Account Number",
          description: "Bank account number",
          required: false,
        },
        {
          forecasterId: "currency",
          forecasterLabel: "Currency",
          description: "ISO currency code e.g. GBP, EUR",
          required: true,
        },
        {
          forecasterId: "balance_date",
          forecasterLabel: "Balance Date",
          description: "Date the balance was recorded",
          required: true,
        },
        {
          forecasterId: "closing_balance",
          forecasterLabel: "Closing Balance",
          description: "Account balance on that date",
          required: true,
        },
      ],
      productDefaults: {
        Plaid: {
          name: "account_name",
          institution_name: "bank_name",
          sort_code: "sort_code",
          account_id: "account_number",
          iso_currency_code: "currency",
          balance_date: "balance_date",
          current_balance: "closing_balance",
        },
      },
    },
  };

// Label map for mapping tab headers
const TX_TYPE_LABELS: Partial<Record<TxTypeKey, string>> = {
  salesLedger: "Sales Ledger",
  purchaseLedger: "Purchase Ledger",
  cashbook: "Cashbook",
  salesOrders: "Sales Orders",
  purchaseOrders: "Purchase Orders",
  payroll: "Payroll",
  bankAccounts: "Bank Accounts",
};

// ── Excel preview data — simulated rows shown in the mapping step ──
const EXCEL_PREVIEWS: Partial<
  Record<TxTypeKey, { headers: string[]; rows: string[][] }>
> = {
  purchaseOrders: {
    headers: [
      "PO Number",
      "Supplier Name",
      "PO Date",
      "Expected Delivery",
      "Net Value",
      "VAT Amount",
      "Status",
      "Project Code",
    ],
    rows: [
      [
        "PO-2026-0892",
        "Jewson Leicester",
        "02/01/2026",
        "16/01/2026",
        "4,500.00",
        "900.00",
        "Approved",
        "BR-082",
      ],
      [
        "PO-2026-0893",
        "Travis Perkins Leicester",
        "03/01/2026",
        "17/01/2026",
        "3,800.00",
        "760.00",
        "Approved",
        "BR-083",
      ],
      [
        "PO-2026-0894",
        "Leicester Aggregates",
        "05/01/2026",
        "19/01/2026",
        "3,200.00",
        "640.00",
        "Approved",
        "BR-082",
      ],
      [
        "PO-2026-0895",
        "Midlands Plant Hire",
        "06/01/2026",
        "08/01/2026",
        "1,800.00",
        "360.00",
        "Approved",
        "BR-084",
      ],
      [
        "PO-2026-0896",
        "Heights Scaffolding Leicester",
        "07/01/2026",
        "14/01/2026",
        "2,800.00",
        "560.00",
        "Approved",
        "BR-085",
      ],
      [
        "PO-2026-0897",
        "Sparks Electrical Ltd",
        "08/01/2026",
        "22/01/2026",
        "4,200.00",
        "840.00",
        "Pending",
        "BR-083",
      ],
      [
        "PO-2026-0898",
        "Flowtech Plumbing",
        "09/01/2026",
        "23/01/2026",
        "3,600.00",
        "720.00",
        "Pending",
        "BR-086",
      ],
      [
        "PO-2026-0899",
        "Midlands Roofing Supplies",
        "10/01/2026",
        "24/01/2026",
        "2,500.00",
        "500.00",
        "Approved",
        "BR-084",
      ],
      [
        "PO-2026-0900",
        "Tool Station Leicester",
        "12/01/2026",
        "15/01/2026",
        "1,200.00",
        "240.00",
        "Approved",
        "BR-081",
      ],
      [
        "PO-2026-0901",
        "Precision Joinery Leicester",
        "13/01/2026",
        "27/01/2026",
        "3,200.00",
        "640.00",
        "Pending",
        "BR-085",
      ],
      [
        "PO-2026-0902",
        "Jewson Leicester",
        "14/01/2026",
        "28/01/2026",
        "4,100.00",
        "820.00",
        "Approved",
        "BR-087",
      ],
      [
        "PO-2026-0903",
        "Travis Perkins Leicester",
        "15/01/2026",
        "29/01/2026",
        "3,500.00",
        "700.00",
        "Approved",
        "BR-082",
      ],
      [
        "PO-2026-0904",
        "Leicester Aggregates",
        "16/01/2026",
        "30/01/2026",
        "2,900.00",
        "580.00",
        "Pending",
        "BR-083",
      ],
      [
        "PO-2026-0905",
        "Midlands Plant Hire",
        "17/01/2026",
        "20/01/2026",
        "1,600.00",
        "320.00",
        "Approved",
        "BR-086",
      ],
      [
        "PO-2026-0906",
        "Heights Scaffolding Leicester",
        "19/01/2026",
        "02/02/2026",
        "2,600.00",
        "520.00",
        "Pending",
        "BR-087",
      ],
    ],
  },
  salesOrders: {
    headers: [
      "Order Number",
      "Customer Name",
      "Order Date",
      "Expected Invoice Date",
      "Contract Value",
      "Stage",
      "Project Manager",
    ],
    rows: [
      [
        "SO-2026-0341",
        "Leicester City Council",
        "03/01/2026",
        "28/03/2026",
        "180,000.00",
        "In Progress",
        "S. Mitchell",
      ],
      [
        "SO-2026-0342",
        "University of Leicester",
        "05/01/2026",
        "30/04/2026",
        "145,000.00",
        "Confirmed",
        "M. Patel",
      ],
      [
        "SO-2026-0343",
        "Leicester Royal Infirmary",
        "06/01/2026",
        "31/05/2026",
        "210,000.00",
        "In Progress",
        "S. Mitchell",
      ],
      [
        "SO-2026-0344",
        "Midlands Property Group",
        "07/01/2026",
        "28/02/2026",
        "95,000.00",
        "In Progress",
        "D. Chen",
      ],
      [
        "SO-2026-0345",
        "Heritage Homes Leicester",
        "08/01/2026",
        "31/03/2026",
        "78,500.00",
        "Confirmed",
        "M. Patel",
      ],
      [
        "SO-2026-0346",
        "Leicester Shopping Centre Mgmt",
        "09/01/2026",
        "30/04/2026",
        "62,000.00",
        "In Progress",
        "S. Mitchell",
      ],
      [
        "SO-2026-0347",
        "DMU (De Montfort University)",
        "10/01/2026",
        "31/05/2026",
        "124,000.00",
        "Confirmed",
        "D. Chen",
      ],
      [
        "SO-2026-0348",
        "Fosse Park Developments",
        "12/01/2026",
        "28/02/2026",
        "88,000.00",
        "In Progress",
        "M. Patel",
      ],
      [
        "SO-2026-0349",
        "Leicester City Council",
        "13/01/2026",
        "30/06/2026",
        "240,000.00",
        "Confirmed",
        "S. Mitchell",
      ],
      [
        "SO-2026-0350",
        "Midlands Property Group",
        "14/01/2026",
        "31/03/2026",
        "72,000.00",
        "In Progress",
        "D. Chen",
      ],
      [
        "SO-2026-0351",
        "University of Leicester",
        "15/01/2026",
        "30/04/2026",
        "98,000.00",
        "Confirmed",
        "M. Patel",
      ],
      [
        "SO-2026-0352",
        "Heritage Homes Leicester",
        "16/01/2026",
        "31/05/2026",
        "54,000.00",
        "In Progress",
        "S. Mitchell",
      ],
      [
        "SO-2026-0353",
        "Fosse Park Developments",
        "17/01/2026",
        "28/02/2026",
        "115,000.00",
        "Confirmed",
        "D. Chen",
      ],
      [
        "SO-2026-0354",
        "Leicester Royal Infirmary",
        "19/01/2026",
        "31/03/2026",
        "160,000.00",
        "In Progress",
        "M. Patel",
      ],
      [
        "SO-2026-0355",
        "DMU (De Montfort University)",
        "20/01/2026",
        "30/06/2026",
        "85,000.00",
        "Confirmed",
        "S. Mitchell",
      ],
    ],
  },
  salesLedger: {
    headers: [
      "Invoice No",
      "Customer",
      "Invoice Date",
      "Due Date",
      "Net Amount",
      "VAT",
      "Status",
      "Reference",
    ],
    rows: [
      [
        "INV-2026-1847",
        "Leicester City Council",
        "02/01/2026",
        "02/03/2026",
        "60,000.00",
        "12,000.00",
        "Outstanding",
        "SO-0341",
      ],
      [
        "INV-2026-1848",
        "University of Leicester",
        "03/01/2026",
        "03/03/2026",
        "48,333.00",
        "9,666.60",
        "Outstanding",
        "SO-0342",
      ],
      [
        "INV-2026-1849",
        "Leicester Royal Infirmary",
        "05/01/2026",
        "05/03/2026",
        "45,000.00",
        "9,000.00",
        "Overdue",
        "SO-0343",
      ],
      [
        "INV-2026-1850",
        "Midlands Property Group",
        "06/01/2026",
        "05/02/2026",
        "47,500.00",
        "9,500.00",
        "Overdue",
        "SO-0344",
      ],
      [
        "INV-2026-1851",
        "Heritage Homes Leicester",
        "07/01/2026",
        "06/02/2026",
        "39,250.00",
        "7,850.00",
        "Outstanding",
        "SO-0345",
      ],
      [
        "INV-2026-1852",
        "Leicester Shopping Centre Mgmt",
        "08/01/2026",
        "22/02/2026",
        "31,000.00",
        "6,200.00",
        "Outstanding",
        "SO-0346",
      ],
      [
        "INV-2026-1853",
        "DMU (De Montfort University)",
        "09/01/2026",
        "09/03/2026",
        "41,333.00",
        "8,266.60",
        "Outstanding",
        "SO-0347",
      ],
      [
        "INV-2026-1854",
        "Fosse Park Developments",
        "10/01/2026",
        "09/02/2026",
        "44,000.00",
        "8,800.00",
        "Overdue",
        "SO-0348",
      ],
      [
        "INV-2026-1855",
        "Leicester City Council",
        "12/01/2026",
        "12/03/2026",
        "80,000.00",
        "16,000.00",
        "Outstanding",
        "SO-0349",
      ],
      [
        "INV-2026-1856",
        "Midlands Property Group",
        "13/01/2026",
        "12/02/2026",
        "36,000.00",
        "7,200.00",
        "Outstanding",
        "SO-0350",
      ],
      [
        "INV-2026-1857",
        "University of Leicester",
        "14/01/2026",
        "14/03/2026",
        "49,000.00",
        "9,800.00",
        "Outstanding",
        "SO-0351",
      ],
      [
        "INV-2026-1858",
        "Heritage Homes Leicester",
        "15/01/2026",
        "14/02/2026",
        "27,000.00",
        "5,400.00",
        "Overdue",
        "SO-0352",
      ],
      [
        "INV-2026-1859",
        "Fosse Park Developments",
        "16/01/2026",
        "16/03/2026",
        "57,500.00",
        "11,500.00",
        "Outstanding",
        "SO-0353",
      ],
      [
        "INV-2026-1860",
        "Leicester Royal Infirmary",
        "17/01/2026",
        "17/03/2026",
        "53,333.00",
        "10,666.60",
        "Outstanding",
        "SO-0354",
      ],
      [
        "INV-2026-1861",
        "DMU (De Montfort University)",
        "19/01/2026",
        "19/03/2026",
        "42,500.00",
        "8,500.00",
        "Outstanding",
        "SO-0355",
      ],
    ],
  },
  purchaseLedger: {
    headers: [
      "Account Code",
      "Supplier Name",
      "Description",
      "Gross",
      "Transaction Type",
      "Outstanding",
      "Transaction Date",
      "Due Date",
      "Anticipated Payment Date",
      "Category",
    ],
    rows: [
      [
        "SUPP1",
        "Jewson Leicester",
        "Pipe fittings order",
        "5,400.00",
        "Invoice",
        "5,400.00",
        "03/01/2026",
        "02/02/2026",
        "02/02/2026",
        "Materials",
      ],
      [
        "SUPP2",
        "Travis Perkins Leicester",
        "Timber supply Jan",
        "4,560.00",
        "Invoice",
        "4,560.00",
        "04/01/2026",
        "03/02/2026",
        "03/02/2026",
        "Materials",
      ],
      [
        "SUPP3",
        "Leicester Aggregates",
        "Sand and gravel delivery",
        "3,840.00",
        "Invoice",
        "3,840.00",
        "05/01/2026",
        "04/02/2026",
        "04/02/2026",
        "Materials",
      ],
      [
        "SUPP4",
        "Sparks Electrical Ltd",
        "Electrical first fix — Phase 2",
        "5,040.00",
        "Invoice",
        "5,040.00",
        "06/01/2026",
        "05/02/2026",
        "05/02/2026",
        "Subcontract",
      ],
      [
        "SUPP5",
        "Flowtech Plumbing",
        "Plumbing install — Plot 4",
        "4,320.00",
        "Invoice",
        "4,320.00",
        "07/01/2026",
        "06/02/2026",
        "06/02/2026",
        "Subcontract",
      ],
      [
        "SUPP1",
        "Jewson Leicester",
        "Payment — Dec invoices",
        "-8,500.00",
        "Payment",
        "0.00",
        "10/01/2026",
        "",
        "",
        "Materials",
      ],
      [
        "SUPP6",
        "Heights Scaffolding Leicester",
        "Scaffold hire — Jan",
        "3,360.00",
        "Invoice",
        "3,360.00",
        "08/01/2026",
        "07/02/2026",
        "07/02/2026",
        "Subcontract",
      ],
      [
        "SUPP7",
        "Precision Joinery Leicester",
        "Kitchen units — Plot 3",
        "3,840.00",
        "Invoice",
        "3,840.00",
        "09/01/2026",
        "08/02/2026",
        "08/02/2026",
        "Subcontract",
      ],
      [
        "SUPP8",
        "Midlands Plant Hire",
        "Excavator hire w/c 06/01",
        "2,160.00",
        "Invoice",
        "2,160.00",
        "10/01/2026",
        "09/02/2026",
        "09/02/2026",
        "Plant Hire",
      ],
      [
        "SUPP3",
        "Leicester Aggregates",
        "Credit — damaged goods return",
        "-960.00",
        "CreditNote",
        "0.00",
        "11/01/2026",
        "",
        "",
        "Materials",
      ],
      [
        "SUPP9",
        "Tool Station Leicester",
        "Hand tools and fixings",
        "1,440.00",
        "Invoice",
        "1,440.00",
        "13/01/2026",
        "12/02/2026",
        "12/02/2026",
        "Equipment",
      ],
      [
        "SUPP1",
        "Jewson Leicester",
        "Bricks and blocks — Phase 3",
        "4,920.00",
        "Invoice",
        "4,920.00",
        "14/01/2026",
        "13/02/2026",
        "13/02/2026",
        "Materials",
      ],
      [
        "SUPP2",
        "Travis Perkins Leicester",
        "Insulation supply",
        "4,200.00",
        "Invoice",
        "4,200.00",
        "15/01/2026",
        "14/02/2026",
        "14/02/2026",
        "Materials",
      ],
      [
        "SUPP4",
        "Sparks Electrical Ltd",
        "Electrical second fix — Plot 2",
        "4,680.00",
        "Invoice",
        "4,680.00",
        "16/01/2026",
        "15/02/2026",
        "15/02/2026",
        "Subcontract",
      ],
      [
        "SUPP10",
        "Office Essentials Leicester",
        "Stationery and toner",
        "540.00",
        "Invoice",
        "540.00",
        "19/01/2026",
        "18/02/2026",
        "18/02/2026",
        "Admin",
      ],
    ],
  },
  payroll: {
    headers: [
      "Employee ID",
      "Employee Name",
      "Pay Period",
      "Pay Date",
      "Gross Pay",
      "Employer NI",
      "Pension",
      "Net Pay",
    ],
    rows: [
      [
        "EMP001",
        "James Smith",
        "Jan 2026",
        "28/01/2026",
        "3,450.00",
        "414.00",
        "172.50",
        "2,863.50",
      ],
      [
        "EMP002",
        "Mary Johnson",
        "Jan 2026",
        "28/01/2026",
        "2,850.00",
        "342.00",
        "142.50",
        "2,365.50",
      ],
      [
        "EMP003",
        "John Williams",
        "Jan 2026",
        "28/01/2026",
        "4,200.00",
        "504.00",
        "210.00",
        "3,486.00",
      ],
      [
        "EMP004",
        "Patricia Brown",
        "Jan 2026",
        "28/01/2026",
        "2,650.00",
        "318.00",
        "132.50",
        "2,199.50",
      ],
      [
        "EMP005",
        "Robert Jones",
        "Jan 2026",
        "28/01/2026",
        "3,100.00",
        "372.00",
        "155.00",
        "2,573.00",
      ],
      [
        "EMP006",
        "Jennifer Garcia",
        "Jan 2026",
        "28/01/2026",
        "2,950.00",
        "354.00",
        "147.50",
        "2,448.50",
      ],
      [
        "EMP007",
        "Michael Miller",
        "Jan 2026",
        "28/01/2026",
        "3,800.00",
        "456.00",
        "190.00",
        "3,154.00",
      ],
      [
        "EMP008",
        "Linda Davis",
        "Jan 2026",
        "28/01/2026",
        "2,700.00",
        "324.00",
        "135.00",
        "2,241.00",
      ],
      [
        "EMP009",
        "William Wilson",
        "Jan 2026",
        "28/01/2026",
        "5,200.00",
        "624.00",
        "260.00",
        "4,316.00",
      ],
      [
        "EMP010",
        "Elizabeth Moore",
        "Jan 2026",
        "28/01/2026",
        "3,300.00",
        "396.00",
        "165.00",
        "2,739.00",
      ],
      [
        "EMP011",
        "David Taylor",
        "Jan 2026",
        "28/01/2026",
        "2,800.00",
        "336.00",
        "140.00",
        "2,324.00",
      ],
      [
        "EMP012",
        "Barbara Anderson",
        "Jan 2026",
        "28/01/2026",
        "3,600.00",
        "432.00",
        "180.00",
        "2,988.00",
      ],
      [
        "EMP013",
        "Richard Thomas",
        "Jan 2026",
        "28/01/2026",
        "4,500.00",
        "540.00",
        "225.00",
        "3,735.00",
      ],
      [
        "EMP014",
        "Susan Jackson",
        "Jan 2026",
        "28/01/2026",
        "2,600.00",
        "312.00",
        "130.00",
        "2,158.00",
      ],
      [
        "EMP015",
        "Joseph White",
        "Jan 2026",
        "28/01/2026",
        "3,200.00",
        "384.00",
        "160.00",
        "2,656.00",
      ],
    ],
  },
  cashbook: {
    headers: [
      "Transaction Date",
      "Description",
      "Reference",
      "Debit Amount",
      "Credit Amount",
      "Category",
    ],
    rows: [
      [
        "02/01/2026",
        "BACS — Jewson Leicester",
        "BACS-0441",
        "4,500.00",
        "",
        "Materials",
      ],
      [
        "03/01/2026",
        "Customer Receipt — Leicester City Council",
        "RCPT-1847",
        "",
        "60,000.00",
        "Sales Ledger",
      ],
      [
        "05/01/2026",
        "BACS — Travis Perkins Leicester",
        "BACS-0442",
        "3,800.00",
        "",
        "Materials",
      ],
      [
        "06/01/2026",
        "Customer Receipt — Midlands Prop Group",
        "RCPT-1850",
        "",
        "47,500.00",
        "Sales Ledger",
      ],
      [
        "07/01/2026",
        "Direct Debit — Leicester Vehicle Leasing",
        "DD-00128",
        "800.00",
        "",
        "Vehicles",
      ],
      [
        "08/01/2026",
        "BACS — Sparks Electrical Ltd",
        "BACS-0443",
        "4,200.00",
        "",
        "Subcontract",
      ],
      [
        "09/01/2026",
        "Customer Receipt — Heritage Homes",
        "RCPT-1851",
        "",
        "39,250.00",
        "Sales Ledger",
      ],
      [
        "10/01/2026",
        "BACS — Flowtech Plumbing",
        "BACS-0444",
        "3,600.00",
        "",
        "Subcontract",
      ],
      [
        "12/01/2026",
        "CHAPS — University of Leicester",
        "RCPT-1848",
        "",
        "48,333.00",
        "Sales Ledger",
      ],
      [
        "13/01/2026",
        "BACS — Heights Scaffolding Leicester",
        "BACS-0445",
        "2,800.00",
        "",
        "Subcontract",
      ],
      [
        "14/01/2026",
        "BACS — Midlands Plant Hire",
        "BACS-0446",
        "1,800.00",
        "",
        "Plant Hire",
      ],
      [
        "15/01/2026",
        "Customer Receipt — DMU",
        "RCPT-1853",
        "",
        "41,333.00",
        "Sales Ledger",
      ],
      [
        "16/01/2026",
        "BACS — Leicester Aggregates",
        "BACS-0447",
        "3,200.00",
        "",
        "Materials",
      ],
      [
        "19/01/2026",
        "Payroll Run — January 2026",
        "PAY-012026",
        "48,320.00",
        "",
        "Payroll",
      ],
      [
        "20/01/2026",
        "Customer Receipt — Fosse Park Dev",
        "RCPT-1854",
        "",
        "44,000.00",
        "Sales Ledger",
      ],
    ],
  },
  bankAccounts: {
    headers: [
      "Account Name",
      "Bank Name",
      "Sort Code",
      "Account Number",
      "Currency",
      "Balance Date",
      "Closing Balance",
    ],
    rows: [
      [
        "Main Operating Account",
        "HSBC",
        "40-22-18",
        "12345678",
        "GBP",
        "31/01/2026",
        "247,000.00",
      ],
      [
        "Payroll Account",
        "Barclays",
        "20-44-91",
        "87654321",
        "GBP",
        "31/01/2026",
        "52,400.00",
      ],
      [
        "Project Reserve",
        "HSBC",
        "40-22-18",
        "11223344",
        "GBP",
        "31/01/2026",
        "84,200.00",
      ],
      [
        "Euro Account",
        "HSBC",
        "40-22-18",
        "44332211",
        "EUR",
        "31/01/2026",
        "18,500.00",
      ],
      [
        "Credit Card Clearing",
        "Barclays",
        "20-44-91",
        "99887766",
        "GBP",
        "31/01/2026",
        "3,240.00",
      ],
      [
        "VAT Reserve Account",
        "HSBC",
        "40-22-18",
        "55443322",
        "GBP",
        "31/01/2026",
        "24,800.00",
      ],
      [
        "Plant & Equipment Fund",
        "HSBC",
        "40-22-18",
        "66554433",
        "GBP",
        "31/01/2026",
        "41,500.00",
      ],
    ],
  },
};

// ── Column type validation ──────────────────────────────────────────────────
// Uses the backend's SchemaFieldType (Text/Date/Currency/Number) as the
// source of truth — no frontend-specific type map needed.
type SchemaFieldType = "Text" | "Date" | "Currency" | "Number";

// Sniff the apparent primitive type of a column from its preview values
function sniffColumnType(values: string[]): SchemaFieldType {
  const nonEmpty = values.filter((v) => v.trim() !== "");
  if (nonEmpty.length === 0) return "Text";
  const dateRe =
    /^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2}|\d{2}-\d{2}-\d{4}|\d{5})$/;
  const currencyRe = /^-?[£$€]?[\d,]+(\.\d{1,2})?$|-?\([\d,]+(\.\d{1,2})?\)$/;
  const numberRe = /^-?\d+(\.\d+)?$/;
  const dateCount = nonEmpty.filter((v) => dateRe.test(v.trim())).length;
  const currencyCount = nonEmpty.filter((v) =>
    currencyRe.test(v.replace(/\s/g, "")),
  ).length;
  const numberCount = nonEmpty.filter((v) => numberRe.test(v.trim())).length;
  const ratio = (count: number) => count / nonEmpty.length;
  if (ratio(dateCount) >= 0.6) return "Date";
  if (ratio(currencyCount) >= 0.6) return "Currency";
  if (ratio(numberCount) >= 0.6) return "Number";
  return "Text";
}

// Return a warning string if the mapped column's preview data doesn't match
// the expected primitive type from the backend schema — null if OK
function getColumnTypeWarning(
  fieldType: SchemaFieldType,
  colLetter: string,
  preview: { headers: string[]; rows: string[][] } | undefined,
): string | null {
  if (!preview || !colLetter) return null;
  if (fieldType === "Text") return null;
  const colIndex = colLetter.toUpperCase().charCodeAt(0) - 65;
  if (colIndex < 0 || colIndex >= preview.headers.length) return null;
  const values = preview.rows.map((r) => r[colIndex] ?? "");
  // If every preview value is blank we have no data to sniff — skip warning
  if (values.every((v) => v.trim() === "")) return null;
  const sniffed = sniffColumnType(values);
  if (sniffed === fieldType) return null;
  // Currency and Number are close enough — don't warn across them
  if (
    (fieldType === "Currency" || fieldType === "Number") &&
    (sniffed === "Currency" || sniffed === "Number")
  )
    return null;
  const expectedLabel: Record<SchemaFieldType, string> = {
    Date: "a date",
    Currency: "a number or amount",
    Number: "a number",
    Text: "text",
  };
  const sniffedLabel: Record<SchemaFieldType, string> = {
    Date: "dates",
    Currency: "numbers",
    Number: "numbers",
    Text: "text",
  };
  return `Column ${colLetter} looks like ${sniffedLabel[sniffed]} — expected ${expectedLabel[fieldType]}. Check your mapping.`;
}

// ── Preview value parser ────────────────────────────────────────────────────
// Parses a raw preview cell value according to the user's declared format
// settings. Used to render interpreted values in the preview table so the
// user can immediately spot if something looks wrong before confirming.
//
// For currency fields:  strips symbols, thousands separators, handles bracket/
//                       trailing-minus/CR-DR notation, returns signed number string
// For date fields:      normalises to DD/MM/YYYY display regardless of input format
// For all other fields: returns value unchanged
function parsePreviewValue(
  raw: string,
  fieldType: SchemaFieldType,
  fmt: ExcelFormatDeclaration | null,
): { display: string; interpreted: boolean } {
  if (!fmt || raw.trim() === "") return { display: raw, interpreted: false };

  if (fieldType === "Currency") {
    let v = raw.trim();
    // Strip currency symbol
    if (fmt.hasCurrencySymbol)
      v = v
        .replace(/^[£$€¥]/, "")
        .replace(/[£$€¥]$/, "")
        .trim();
    // Handle negative formats
    let negative = false;
    switch (fmt.negativeFormat) {
      case "brackets":
        if (/^\(.*\)$/.test(v)) {
          v = v.slice(1, -1);
          negative = true;
        }
        break;
      case "trailing_minus":
        if (v.endsWith("-")) {
          v = v.slice(0, -1);
          negative = true;
        }
        break;
      case "cr_dr_suffix": {
        const upper = v.toUpperCase();
        if (upper.endsWith(" DR") || upper.endsWith("DR")) {
          v = v.replace(/\s?DR$/i, "");
          negative = true;
        } else if (upper.endsWith(" CR") || upper.endsWith("CR")) {
          v = v.replace(/\s?CR$/i, "");
        }
        break;
      }
      case "minus_sign":
        if (v.startsWith("-")) {
          v = v.slice(1);
          negative = true;
        }
        break;
      case "colour_only":
        // Cannot detect from value alone — return unchanged, caller shows warning
        return { display: raw, interpreted: false };
    }
    // Strip thousands separator
    if (fmt.thousandsSeparator === "comma") v = v.replace(/,/g, "");
    else if (fmt.thousandsSeparator === "period") v = v.replace(/\./g, "");
    // Normalise decimal separator
    if (fmt.decimalSeparator === "comma") v = v.replace(",", ".");
    const num = parseFloat(v);
    if (isNaN(num)) return { display: raw, interpreted: false };
    const signed = negative ? -num : num;
    const formatted = signed.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return { display: formatted, interpreted: true };
  }

  if (fieldType === "Date") {
    const v = raw.trim();
    let day = "",
      month = "",
      year = "";
    switch (fmt.dateFormat) {
      case "DD/MM/YYYY": {
        const p = v.split("/");
        if (p.length === 3) {
          [day, month, year] = p;
        }
        break;
      }
      case "MM/DD/YYYY": {
        const p = v.split("/");
        if (p.length === 3) {
          [month, day, year] = p;
        }
        break;
      }
      case "YYYY-MM-DD": {
        const p = v.split("-");
        if (p.length === 3) {
          [year, month, day] = p;
        }
        break;
      }
      case "DD-MM-YYYY": {
        const p = v.split("-");
        if (p.length === 3) {
          [day, month, year] = p;
        }
        break;
      }
      case "excel_serial": {
        const serial = parseInt(v);
        if (!isNaN(serial)) {
          const d = new Date(Date.UTC(1900, 0, serial - 1));
          day = String(d.getUTCDate()).padStart(2, "0");
          month = String(d.getUTCMonth() + 1).padStart(2, "0");
          year = String(d.getUTCFullYear());
        }
        break;
      }
      default:
        return { display: v, interpreted: false };
    }
    if (day && month && year) {
      return {
        display: `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`,
        interpreted: true,
      };
    }
    return { display: raw, interpreted: false };
  }

  return { display: raw, interpreted: false };
}
// ───────────────────────────────────────────────────────────────────────────

// ── Excel / CSV template generation ────────────────────────────────────────

/** Triggers a browser download for a given blob + filename */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================================

interface DataSourceConfigurationProps {
  onNavigate?: (page: string, data?: { highlightSourceType?: string }) => void;
}

// Map API type IDs to frontend display names
const API_TYPE_ID_TO_DISPLAY_NAME: Record<string, DataSourceType> = {
  af0: "Access Financials",
  adc0: "Access Dimensions Cloud",
  apc0: "Paycircle",
  ex0: "Excel Spreadsheet",
  plaid0: "Plaid",
};

// Map frontend display names to API type IDs
const DISPLAY_NAME_TO_API_TYPE_ID: Record<DataSourceType, string> = {
  "Access Financials": "af0",
  "Access Dimensions Cloud": "adc0",
  Paycircle: "apc0",
  "Excel Spreadsheet": "ex0",
  Plaid: "plaid0",
};

export function DataSourceConfiguration({
  onNavigate,
}: DataSourceConfigurationProps = {}) {
  const { getCashMovementTerms } = useTerminology();
  const { mode } = useApp();
  const queryClient = useQueryClient();
  const { dataSources, addDataSource, updateDataSource, removeDataSource } =
    useDataSource();

  // Live mode API state
  const [apiDataSourceTypes, setApiDataSourceTypes] = useState<
    DataSourceTypeApi[]
  >([]);
  const [apiInstances, setApiInstances] = useState<DataSourceInstanceApi[]>([]);
  const [liveDataSources, setLiveDataSources] = useState<DataSourceConfig[]>(
    [],
  );
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [isLoadingDataSources, setIsLoadingDataSources] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [editApiTypeId, setEditApiTypeId] = useState<string | null>(null);
  const [editInstanceName, setEditInstanceName] = useState<string>("");

  // Nominal category labels from Financials API
  const [nominalCategoryLabels, setNominalCategoryLabels] =
    useState<NominalCategoryLabelsResponseApi | null>(null);
  const [isLoadingNominalCategoryLabels, setIsLoadingNominalCategoryLabels] =
    useState(false);

  /**
   * Get display label for a nominal code level.
   * Uses user-defined labels from Financials API when available, with fallback.
   */
  const getNominalCategoryLabel = (level: number): string => {
    if (nominalCategoryLabels) {
      const key = `category${level}` as keyof NominalCategoryLabelsResponseApi;
      const label = nominalCategoryLabels[key];
      if (label) {
        return label;
      }
    }
    return `Nominal Code - Level ${level}`;
  };

  /**
   * Map API data source status to frontend status
   */
  const mapApiStatusToFrontend = (
    status: string,
  ): "connected" | "disconnected" | "error" | "deleting" => {
    const statusLower = status.toLowerCase();
    if (statusLower === "pendingdeletion") return "deleting";
    if (
      statusLower === "connected" ||
      statusLower === "active" ||
      statusLower === "pending"
    )
      return "connected";
    if (
      statusLower === "error" ||
      statusLower === "failed" ||
      statusLower === "schedulefailure"
    )
      return "error";
    return "disconnected";
  };

  /**
   * Map API data source to frontend DataSourceConfig
   */
  const mapApiDataSourceToConfig = (
    apiSource: DataSourceListItemApi,
    index: number,
  ): DataSourceConfig => {
    return {
      id: String(apiSource.id),
      name: apiSource.name,
      type:
        API_TYPE_ID_TO_DISPLAY_NAME[apiSource.typeId] ||
        (apiSource.typeName as DataSourceType),
      priority: index + 1,
      connectionDetails: {},
      status: mapApiStatusToFrontend(apiSource.status),
      lastSync: apiSource.lastSyncDateTime,
      recordCount: apiSource.recordsSynced ?? 0,
      dateAdded: "",
      scheduleDescription: apiSource.scheduleDescription,
      parentCategory: {
        isPrimary: apiSource.isPrimaryCategorySource,
        categoryField: undefined,
      },
    };
  };

  // Use live data sources from API in live mode, demo data sources in demo mode
  const displayDataSources = useMemo(() => {
    const sources = mode === "live" ? liveDataSources : dataSources;

    if (mode !== "live") return sources;

    return [...sources].sort((a, b) => {
      const aIsPrimary = a.parentCategory?.isPrimary === true;
      const bIsPrimary = b.parentCategory?.isPrimary === true;

      if (aIsPrimary !== bIsPrimary) {
        return aIsPrimary ? -1 : 1;
      }

      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
  }, [mode, liveDataSources, dataSources]);
  const isLoadingList = mode === "live" && isLoadingDataSources;

  // Dialog states
  const [syncScheduleDialogOpen, setSyncScheduleDialogOpen] = useState(false);
  const [configureDialogOpen, setConfigureDialogOpen] = useState(false);
  const postSaveActionRef = useRef<"none" | "openSync">("none");
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [addSourceDialogOpen, setAddSourceDialogOpen] = useState(false);

  const [newlyAddedSource, setNewlyAddedSource] =
    useState<DataSourceConfig | null>(null);
  const [changeParentDialogOpen, setChangePrimaryDialogOpen] = useState(false);
  const [newParentSource, setNewPrimarySource] =
    useState<DataSourceConfig | null>(null);
  const [changeParentDialogOpenForAdd, setChangePrimaryDialogOpenForAdd] =
    useState(false);
  const [newParentSourceForAdd, setNewPrimarySourceForAdd] =
    useState<DataSourceConfig | null>(null);

  // Selected source for dialogs
  const [selectedSource, setSelectedSource] = useState<DataSourceConfig | null>(
    null,
  );
  const [disconnectSource, setDisconnectSource] =
    useState<DataSourceConfig | null>(null);
  const [reconnectSource, setReconnectSource] =
    useState<DataSourceConfig | null>(null);
  const [deleteSource, setDeleteSource] = useState<DataSourceConfig | null>(
    null,
  );
  const [syncHistorySource, setSyncHistorySource] =
    useState<DataSourceConfig | null>(null);

  // Mapping layer state — lives inside the Configure dialog
  const [configureActiveTab, setConfigureActiveTab] = useState("connection");
  const [mappingDirty, setMappingDirty] = useState(false);
  // Per-transaction-type field mappings: { salesLedger: { invoice_date: 'transaction_date', ... }, ... }
  const [fieldMappings, setFieldMappings] = useState<
    Partial<Record<TxTypeKey, Record<string, string>>>
  >({});
  const [mappingSearchQuery, setMappingSearchQuery] = useState<string>("");

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTypeConfirmOpen, setDeleteTypeConfirmOpen] = useState(false);
  const [deleteTypeInput, setDeleteTypeInput] = useState("");
  const [cannotDeleteLastOpen, setCannotDeleteLastOpen] = useState(false);

  // Sync state - per-source tracking for background sync
  const [syncingSources, setSyncingSources] = useState<Set<string>>(new Set());
  const syncPollingRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  );

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(
    null,
  );

  // New source form state
  const [newSourceName, setNewSourceName] = useState("");
  // Generic source category (Finance / Payroll / Banking / Excel) — drives product dropdown + connection flow
  const [newSourceGenericType, setNewSourceGenericType] = useState<
    "Finance" | "Payroll" | "Banking" | "Excel" | ""
  >("");
  // Specific product within the generic type (e.g. "Access Financials" within "Finance")
  const [newSourceProduct, setNewSourceProduct] = useState("");
  const [newSourceType, setNewSourceType] = useState<DataSourceType | "">("");
  const [newSourceInstance, setNewSourceInstance] = useState("");
  const [newSourceApiKey, setNewSourceApiKey] = useState("");
  const [newSourceClientId, setNewSourceClientId] = useState("");
  const [newSourceClientSecret, setNewSourceClientSecret] = useState("");
  const [newSourceBureauId, setNewSourceBureauId] = useState("");
  const [newSourceAuthEndpoint, setNewSourceAuthEndpoint] = useState("");
  const [newSourcePathType, setNewSourcePathType] =
    useState<ExcelPathType>("upload");
  const [newSourceCloudProvider, setNewSourceCloudProvider] = useState<
    CloudProvider | ""
  >("");
  const [newSourceFilePath, setNewSourceFilePath] = useState("");
  const [newSourceFilePassword, setNewSourceFilePassword] = useState("");
  const [showWizardPassword, setShowWizardPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  // Real file data parsed from the uploaded file — replaces EXCEL_PREVIEWS in both wizard and edit panel
  const [parsedFileData, setParsedFileData] = useState<{
    headers: string[];
    rows: string[][];
  } | null>(null);
  // Column mapping preview from backend API (schema + headers + sample rows + auto-mappings)
  const [columnMappingPreview, setColumnMappingPreview] =
    useState<ColumnMappingPreviewResponse | null>(null);
  // Expand/collapse state for the preview table (wizard + edit panel share the same toggle)
  const [previewExpanded, setPreviewExpanded] = useState(true);

  // Excel wizard — extended state for inline column mapping (Package 1 foundation)
  // newSourceWizardStep drives the Excel wizard sub-steps:
  //   'config'  → file upload + sheet config (Step 4)
  //   'format'  → format declaration (Step 4b — new)
  //   'mapping' → transaction type + column mapping (Steps 5+6)
  const [newSourceWizardStep, setNewSourceWizardStep] = useState<
    "config" | "format" | "mapping"
  >("config");
  const [newSourceSheetName, setNewSourceSheetName] = useState("");
  const [newSourceHasHeaderRow, setNewSourceHasHeaderRow] = useState(true);
  const [newSourceHeaderRow, setNewSourceHeaderRow] = useState(1);
  const [newSourceDataStartRow, setNewSourceDataStartRow] = useState(2);
  const [newSourceDateFormat, setNewSourceDateFormat] =
    useState<ExcelDateFormat>("DD/MM/YYYY");
  const [newSourceDateFormatOther, setNewSourceDateFormatOther] = useState("");
  const [newSourceAmountFormat, setNewSourceAmountFormat] =
    useState<ExcelAmountFormat>("negative_outflow");
  const [newSourceValidationBehaviour, setNewSourceValidationBehaviour] =
    useState<ExcelValidationBehaviour>("skip_and_flag");
  // Format declaration state — user declares how their file's numbers and dates are formatted
  const [newSourceThousandsSeparator, setNewSourceThousandsSeparator] =
    useState<ExcelThousandsSeparator>("comma");
  const [newSourceDecimalSeparator, setNewSourceDecimalSeparator] =
    useState<ExcelDecimalSeparator>("period");
  const [newSourceNegativeFormat, setNewSourceNegativeFormat] =
    useState<ExcelNegativeFormat>("minus_sign");
  const [newSourceHasCurrencySymbol, setNewSourceHasCurrencySymbol] =
    useState(false);
  // Per-transaction-type column mappings built during the wizard
  const [newSourceWizardFieldMappings, setNewSourceWizardFieldMappings] =
    useState<Partial<Record<TxTypeKey, ExcelColumnMap[]>>>({});

  // Validation results — null = not yet run, object = results available
  type ValidationIssue = {
    rowNumber: number;
    columnLetter: string;
    fieldLabel: string;
    expectedType: string;
    cellValue: string;
  };
  const [validationResults, setValidationResults] = useState<{
    invalidRows: ValidationIssue[];
    totalRows: number;
  } | null>(null);
  const [isValidatingData, setIsValidatingData] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Splitter modal state
  const [splitterModalOpen, setSplitterModalOpen] = useState(false);
  const [splitterColumn, setSplitterColumn] = useState("");
  const [splitterFieldId, setSplitterFieldId] = useState("");
  const [splitterNegativeIs, setSplitterNegativeIs] = useState<
    "incoming" | "outgoing"
  >("outgoing");
  const [splitterAlreadyHandled, setSplitterAlreadyHandled] = useState<
    Set<string>
  >(new Set());

  // One-directional confirmation modal state
  const [oneDirModalOpen, setOneDirModalOpen] = useState(false);
  const [oneDirMappedSide, setOneDirMappedSide] = useState<
    "incoming" | "outgoing" | null
  >(null);

  // Template download modal state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState<
    "individual" | "combined"
  >("individual");
  const [templateIndividualType, setTemplateIndividualType] = useState<
    TxTypeKey | ""
  >("");
  const [templateCombinedTypes, setTemplateCombinedTypes] = useState<
    Set<TxTypeKey>
  >(new Set());

  // Example row detection modal state
  const [exampleRowModalOpen, setExampleRowModalOpen] = useState(false);

  // Auto-map state
  const [autoMapApplied, setAutoMapApplied] = useState(false);
  const [autoMapCount, setAutoMapCount] = useState(0);

  // Upload metadata from API response — stores sheet names, row counts, column counts
  const [uploadMetadata, setUploadMetadata] = useState<Record<
    string,
    string
  > | null>(null);
  // Uploaded file ID from the API — needed for server-side validation
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  // Upload simulation state
  const [uploadStage, setUploadStage] = useState<
    "idle" | "uploading" | "validating" | "success"
  >("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [validationStep, setValidationStep] = useState(0);
  const [newSourceDatabaseType, setNewSourceDatabaseType] = useState<
    DatabaseType | ""
  >("");
  const [newSourceConnectionString, setNewSourceConnectionString] =
    useState("");
  const [newSourceTables, setNewSourceTables] = useState<string[]>([]);
  const [newSourceIsPrimary, setNewSourceIsPrimary] = useState(false);
  const [newSourceCategoryField, setNewSourceCategoryField] = useState("");

  // Transaction types selection — defaults are set intelligently when source type is chosen
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState(
    getDefaultTransactionTypes(""),
  );

  // Demo data: Available instances for each source type
  const availableInstances = {
    "Access Financials": [
      {
        id: "FIN-2847",
        name: "BuildRight Construction Ltd - Production (FIN-2847)",
        isConnected: true, // Already connected
      },
      {
        id: "FIN-2848",
        name: "BuildRight Construction Ltd - Training (FIN-2848)",
        isConnected: false,
      },
      {
        id: "FIN-3021",
        name: "BuildRight Group Holdings - Production (FIN-3021)",
        isConnected: false,
      },
    ],
    "Access Dimensions Cloud": [
      {
        id: "DIM-1892",
        name: "BuildRight Property Division - Production (DIM-1892)",
        isConnected: false,
      },
      {
        id: "DIM-2104",
        name: "BuildRight Services Ltd - Production (DIM-2104)",
        isConnected: false,
      },
    ],
    Paycircle: [
      {
        id: "PAY-5678",
        name: "BuildRight Construction Ltd - Production (PAY-5678)",
        isConnected: true, // Already connected
      },
    ],
    Plaid: [
      {
        id: "PLAID-9012",
        name: "BuildRight Construction Ltd - Current Account",
        isConnected: false,
      },
    ],
  };

  // Fetch data source types from API in live mode
  useEffect(() => {
    if (mode === "live") {
      const fetchDataSourceTypes = async () => {
        setIsLoadingTypes(true);
        try {
          const response = await getDataSourceTypes();
          setApiDataSourceTypes(response.types);
        } catch (error) {
          console.error("Failed to fetch data source types:", error);
          toast.error("Error", {
            description: "Failed to load data source types. Please try again.",
          });
        } finally {
          setIsLoadingTypes(false);
        }
      };
      fetchDataSourceTypes();
    }
  }, [mode, toast]);

  const refreshLiveDataSources = async (): Promise<
    DataSourceConfig[] | undefined
  > => {
    setIsLoadingDataSources(true);
    try {
      const response = await getAllDataSources();
      const mappedSources = response.data.map((source, index) =>
        mapApiDataSourceToConfig(source, index),
      );
      setLiveDataSources(mappedSources);
      return mappedSources;
    } catch (error) {
      console.error("Failed to fetch data sources:", error);
      toast.error("Error", {
        description: "Failed to load data sources. Please try again.",
      });
      return undefined;
    } finally {
      setIsLoadingDataSources(false);
    }
  };

  // Fetch data sources from API in live mode
  useEffect(() => {
    if (mode === "live") {
      refreshLiveDataSources();
    }
  }, [mode]);

  // Auto-refresh polling when any data source is in 'deleting' state
  const deletionPollStartRef = useRef<number | null>(null);
  const deletionTimeoutWarningShownRef = useRef(false);

  useEffect(() => {
    const hasDeletingSource =
      mode === "live" && liveDataSources.some((ds) => ds.status === "deleting");

    if (!hasDeletingSource) {
      deletionPollStartRef.current = null;
      deletionTimeoutWarningShownRef.current = false;
      return;
    }

    if (deletionPollStartRef.current === null) {
      deletionPollStartRef.current = Date.now();
    }

    const DELETION_POLL_TIMEOUT_MS = 2 * 60 * 1000;

    const deletionPollInterval = setInterval(async () => {
      const elapsed = Date.now() - (deletionPollStartRef.current ?? Date.now());
      if (elapsed >= DELETION_POLL_TIMEOUT_MS) {
        if (!deletionTimeoutWarningShownRef.current) {
          deletionTimeoutWarningShownRef.current = true;
          toast.success("Deletion taking longer than expected", {
            description:
              "The data source deletion is taking longer than normal. It will continue in the background — please check back shortly.",
          });
        }
        clearInterval(deletionPollInterval);
        return;
      }

      try {
        const response = await getAllDataSources();
        const mappedSources = response.data.map((source, index) =>
          mapApiDataSourceToConfig(source, index),
        );
        setLiveDataSources(mappedSources);
      } catch (error) {
        console.error("Failed to poll data sources during deletion:", error);
      }
    }, 5000);

    return () => clearInterval(deletionPollInterval);
  }, [mode, liveDataSources, toast]);

  // Fetch instances when source type changes in live mode
  useEffect(() => {
    if (
      mode === "live" &&
      newSourceType &&
      newSourceType !== "Excel Spreadsheet"
    ) {
      const typeId =
        DISPLAY_NAME_TO_API_TYPE_ID[newSourceType as DataSourceType];
      if (typeId) {
        const fetchInstances = async () => {
          setIsLoadingInstances(true);
          setApiInstances([]);
          try {
            const response = await getDataSourceTypeWithInstances(typeId);
            setApiInstances(response.instances);
          } catch (error) {
            console.error("Failed to fetch instances:", error);
            toast.error("Error", {
              description: "Failed to load instances. Please try again.",
            });
          } finally {
            setIsLoadingInstances(false);
          }
        };
        fetchInstances();
      }
    } else {
      setApiInstances([]);
    }
  }, [mode, newSourceType, toast]);

  // Fetch nominal category labels when instance changes (for new source dialog)
  useEffect(() => {
    if (
      mode === "live" &&
      addSourceDialogOpen &&
      newSourceInstance &&
      (newSourceType === "Access Financials" ||
        newSourceType === "Access Dimensions Cloud")
    ) {
      setIsLoadingNominalCategoryLabels(true);
      setNominalCategoryLabels(null);

      getNominalCategoryLabels(newSourceInstance)
        .then((labels) => setNominalCategoryLabels(labels))
        .catch((err) => {
          console.error("Failed to fetch nominal category labels:", err);
          setNominalCategoryLabels(null);
        })
        .finally(() => setIsLoadingNominalCategoryLabels(false));
    } else if (!addSourceDialogOpen) {
      setNominalCategoryLabels(null);
      setIsLoadingNominalCategoryLabels(false);
    }
  }, [mode, addSourceDialogOpen, newSourceInstance, newSourceType]);

  // Fetch nominal category labels when edit dialog opens with an instance
  useEffect(() => {
    if (
      mode === "live" &&
      configureDialogOpen &&
      selectedSource?.connectionDetails?.instanceId &&
      (selectedSource?.type === "Access Financials" ||
        selectedSource?.type === "Access Dimensions Cloud")
    ) {
      setIsLoadingNominalCategoryLabels(true);
      setNominalCategoryLabels(null);

      getNominalCategoryLabels(selectedSource.connectionDetails.instanceId)
        .then((labels) => setNominalCategoryLabels(labels))
        .catch((err) => {
          console.error("Failed to fetch nominal category labels:", err);
          setNominalCategoryLabels(null);
        })
        .finally(() => setIsLoadingNominalCategoryLabels(false));
    } else if (!configureDialogOpen) {
      setNominalCategoryLabels(null);
      setIsLoadingNominalCategoryLabels(false);
    }
  }, [
    mode,
    configureDialogOpen,
    selectedSource?.connectionDetails?.instanceId,
    selectedSource?.type,
  ]);

  const clearSyncPolling = useCallback((sourceId: string) => {
    const interval = syncPollingRefs.current.get(sourceId);
    if (interval) {
      clearInterval(interval);
      syncPollingRefs.current.delete(sourceId);
    }
  }, []);

  const addSyncingSource = useCallback((sourceId: string) => {
    setSyncingSources((prev) => new Set(prev).add(sourceId));
  }, []);

  const removeSyncingSource = useCallback((sourceId: string) => {
    setSyncingSources((prev) => {
      const next = new Set(prev);
      next.delete(sourceId);
      return next;
    });
  }, []);

  /**
   * Start polling for sync completion on a given source.
   * Used both when triggering a manual sync and when detecting an in-progress refresh on mount.
   * @param sourceId - The data source ID to poll
   * @param knownRefreshId - Optional refresh ID to track in latestRefresh when currentRefresh is null
   */
  const startSyncPolling = useCallback(
    (sourceId: string, knownRefreshId?: string) => {
      // Don't start duplicate polling for the same source
      if (syncPollingRefs.current.has(sourceId)) return;

      let nullCurrentCount = 0;
      const maxNullPolls = 3;

      const pollingInterval = setInterval(async () => {
        try {
          const statusResponse = await getDataSourceRefreshStatus(
            Number(sourceId),
          );
          const current = statusResponse.currentRefresh;

          if (current) {
            nullCurrentCount = 0;

            if (current.status === "Completed") {
              clearSyncPolling(sourceId);
              removeSyncingSource(sourceId);
              await refreshLiveDataSources();
              queryClient.invalidateQueries({ queryKey: ["forecast"] });
              queryClient.invalidateQueries({
                queryKey: ["admin-dashboard-data-sources"],
              });
              queryClient.invalidateQueries({
                queryKey: ["admin-dashboard-recent-syncs"],
              });
            } else if (current.status === "Failed") {
              clearSyncPolling(sourceId);
              removeSyncingSource(sourceId);
            }
          } else {
            nullCurrentCount++;
            const latest = statusResponse.latestRefresh;

            if (
              latest &&
              knownRefreshId &&
              latest.refreshId === knownRefreshId
            ) {
              if (latest.status === "Failed" || latest.status === "Cancelled") {
                clearSyncPolling(sourceId);
                removeSyncingSource(sourceId);
              } else if (latest.status === "Completed") {
                clearSyncPolling(sourceId);
                removeSyncingSource(sourceId);
                await refreshLiveDataSources();
                queryClient.invalidateQueries({ queryKey: ["forecast"] });
                queryClient.invalidateQueries({
                  queryKey: ["admin-dashboard-data-sources"],
                });
                queryClient.invalidateQueries({
                  queryKey: ["admin-dashboard-recent-syncs"],
                });
              } else if (nullCurrentCount >= maxNullPolls) {
                clearSyncPolling(sourceId);
                removeSyncingSource(sourceId);
              }
            } else if (nullCurrentCount >= maxNullPolls) {
              clearSyncPolling(sourceId);
              removeSyncingSource(sourceId);
            }
          }
        } catch {
          clearSyncPolling(sourceId);
          removeSyncingSource(sourceId);
        }
      }, 3000);
      syncPollingRefs.current.set(sourceId, pollingInterval);
    },
    [
      clearSyncPolling,
      removeSyncingSource,
      refreshLiveDataSources,
      queryClient,
    ],
  );

  /**
   * On mount (live mode), check each data source for in-progress refreshes
   * so the Sync Now button correctly shows "Syncing..." if a sync was started
   * before the user navigated away and is still running.
   */
  useEffect(() => {
    if (mode !== "live" || liveDataSources.length === 0) return;

    let cancelled = false;

    const checkInProgressRefreshes = async () => {
      const connectedSources = liveDataSources.filter(
        (ds) => ds.status === "connected",
      );
      const results = await Promise.allSettled(
        connectedSources.map((ds) =>
          getDataSourceRefreshStatus(Number(ds.id)).then((status) => ({
            sourceId: ds.id,
            status,
          })),
        ),
      );

      if (cancelled) return;

      for (const result of results) {
        if (result.status !== "fulfilled") continue;
        const { sourceId, status } = result.value;
        const current = status.currentRefresh;

        if (
          current &&
          current.status !== "Completed" &&
          current.status !== "Failed" &&
          current.status !== "Cancelled"
        ) {
          addSyncingSource(sourceId);
          startSyncPolling(sourceId, current.refreshId);
        }
      }
    };

    checkInProgressRefreshes();

    return () => {
      cancelled = true;
    };
    // Only run once when liveDataSources first loads (length transitions from 0 to >0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, liveDataSources.length > 0]);

  // Fetch column mapping preview from backend when uploadedFileId + transaction type are available
  useEffect(() => {
    if (mode !== "live") return;
    const activeTxKey = (
      Object.keys(selectedTransactionTypes) as TxTypeKey[]
    ).find((k) => (selectedTransactionTypes as Record<TxTypeKey, boolean>)[k]);
    if (!uploadedFileId || !activeTxKey) return;

    let cancelled = false;
    const fetchPreview = async () => {
      try {
        const preview = await getColumnMappingPreview(
          uploadedFileId,
          activeTxKey,
        );
        if (cancelled) return;
        setColumnMappingPreview(preview);
        // Only populate file preview data if the backend successfully read the file
        if (preview.headers && preview.sampleRows) {
          setParsedFileData({
            headers: preview.headers,
            rows: preview.sampleRows,
          });
        }
        // Apply auto-mappings from backend (only if preview data was returned)
        if (preview.autoMappings) {
          const autoMapped: ExcelColumnMap[] = preview.schema
            .filter((f) => preview.autoMappings?.[f.forecasterId])
            .map((f) => ({
              forecasterId: f.forecasterId,
              forecasterLabel: f.forecasterLabel,
              columnLetter: preview.autoMappings![f.forecasterId]!,
              required: f.required,
            }));
          if (autoMapped.length > 0) {
            setNewSourceWizardFieldMappings((prev) => ({
              ...prev,
              [activeTxKey]: autoMapped,
            }));
          }
        }
      } catch {
        // Preview not critical — fail silently
      }
    };
    fetchPreview();
    return () => {
      cancelled = true;
    };
  }, [mode, uploadedFileId, selectedTransactionTypes]);

  /**
   * Handle Sync Now - runs in background, no modal
   */
  const handleSyncNow = async (source: DataSourceConfig) => {
    addSyncingSource(source.id);

    if (mode !== "live") {
      // Demo mode: simulate background sync
      setTimeout(() => {
        updateDataSource(source.id, {
          lastSync: new Date().toISOString(),
          status: "connected",
        });
        removeSyncingSource(source.id);
      }, 3000);
      return;
    }

    try {
      const triggerResponse = await triggerDataSourceRefresh(Number(source.id));
      startSyncPolling(source.id, triggerResponse.refreshId);
    } catch {
      removeSyncingSource(source.id);
    }
  };

  /**
   * Handle Sync Schedule
   */
  const handleSyncSchedule = async (source: DataSourceConfig) => {
    if (mode === "live") {
      try {
        const scheduleResponse = await getRefreshSchedule(Number(source.id));
        const syncSchedule = scheduleResponse.schedule
          ? apiScheduleToSyncSchedule(scheduleResponse.schedule)
          : undefined;
        setSelectedSource({ ...source, syncSchedule });
      } catch {
        setSelectedSource(source);
      }
    } else {
      setSelectedSource(source);
    }
    setSyncScheduleDialogOpen(true);
  };

  /**
   * Save sync schedule
   */
  const handleSaveSyncSchedule = async (schedule: SyncSchedule) => {
    if (!selectedSource) return;

    if (mode === "live") {
      try {
        const apiRequest = syncScheduleToApiRequest(schedule);
        await updateRefreshSchedule(Number(selectedSource.id), apiRequest);
        await refreshLiveDataSources();

        if (!schedule.enabled || schedule.frequencyMinutes === 0) {
          toast.success("Automated Sync Disabled", {
            description: `Automated sync disabled for ${selectedSource.name}`,
          });
        } else {
          toast.success("Sync Schedule Updated", {
            description: `Schedule for ${selectedSource.name} has been updated.`,
          });
        }
      } catch (error) {
        toast.error("Error", {
          description:
            error instanceof ApiError
              ? error.message
              : "Failed to update sync schedule. Please try again.",
        });
      }
    } else {
      updateDataSource(selectedSource.id, {
        syncSchedule: schedule,
      });

      if (!schedule.enabled || schedule.frequencyMinutes === 0) {
        toast.success("Automated Sync Disabled", {
          description: `Automated sync disabled for ${selectedSource.name}`,
        });
      } else {
        toast.success("Sync Schedule Updated", {
          description: `Schedule for ${selectedSource.name} has been updated.`,
        });
      }
    }
  };

  /**
   * Handle Configure
   */
  const handleConfigure = async (source: DataSourceConfig) => {
    if (mode === "live") {
      setSelectedSource(source);

      if (
        source.type === "Access Financials" ||
        source.type === "Access Dimensions Cloud"
      ) {
        setIsLoadingNominalCategoryLabels(true);
        setNominalCategoryLabels(null);
      } else {
        setIsLoadingNominalCategoryLabels(false);
        setNominalCategoryLabels(null);
      }

      setConfigureDialogOpen(true);
      setIsLoadingConfig(true);
      setEditApiTypeId(null);
      setEditInstanceName("");
      try {
        const fullSource = await getDataSourceById(Number(source.id));
        setEditApiTypeId(fullSource.typeId);
        let resolvedInstanceName = "";
        if (fullSource.instanceId) {
          try {
            const typeWithInstances = await getDataSourceTypeWithInstances(
              fullSource.typeId,
            );
            const matchedInstance = typeWithInstances.instances.find(
              (inst) => inst.id === fullSource.instanceId,
            );
            resolvedInstanceName =
              matchedInstance?.name ?? fullSource.instanceId;
          } catch {
            resolvedInstanceName = fullSource.instanceId;
          }
        }
        setEditInstanceName(resolvedInstanceName);

        if (!fullSource.instanceId) {
          setIsLoadingNominalCategoryLabels(false);
        }

        const config = fullSource.configuration ?? {};
        const transactionTypes = {
          salesLedger:
            config.SalesLedger === true || config.SalesLedger === "True",
          purchaseLedger:
            config.PurchaseLedger === true || config.PurchaseLedger === "True",
          cashbook: config.Cashbook === true || config.Cashbook === "True",
          bankAccounts:
            config.BankAccounts === true || config.BankAccounts === "True",
          salesOrders:
            config.SalesOrders === true || config.SalesOrders === "True",
          purchaseOrders:
            config.PurchaseOrders === true || config.PurchaseOrders === "True",
        };
        let categoryField: string | undefined;
        if (
          fullSource.isPrimaryCategorySource &&
          config.CategoryLevel != null
        ) {
          categoryField = `Nominal Code - Level ${config.CategoryLevel}`;
        }
        setSelectedSource({
          ...source,
          name: fullSource.name,
          connectionDetails: {
            ...source.connectionDetails,
            instanceId: fullSource.instanceId ?? undefined,
            transactionTypes,
          },
          parentCategory: {
            isPrimary: fullSource.isPrimaryCategorySource,
            categoryField,
          },
        });
      } catch (error) {
        console.error("Failed to fetch data source configuration:", error);
        toast.error("Error", {
          description:
            error instanceof ApiError
              ? error.message
              : "Failed to load data source configuration.",
        });
        setConfigureDialogOpen(false);
        setSelectedSource(null);
        setIsLoadingNominalCategoryLabels(false);
      } finally {
        setIsLoadingConfig(false);
      }
    } else {
      setSelectedSource(source);
      setMappingDirty(false);
      // Seed per-type field mappings from product defaults
      const genericType = getGenericSourceType(source.type);
      const txTypes = source.connectionDetails?.transactionTypes ?? {};
      const initialFieldMappings: Partial<
        Record<TxTypeKey, Record<string, string>>
      > = {};
      (Object.keys(txTypes) as TxTypeKey[]).forEach((key) => {
        if (txTypes[key]) {
          const schema = TX_TYPE_MAPPING_SCHEMAS[key];
          if (schema) {
            const defaults = schema.productDefaults[source.type] ?? {};
            initialFieldMappings[key] = { ...defaults };
          }
        }
      });
      // For Finance sources without explicit txTypes set, seed the active ones
      if (genericType === "Finance" && Object.keys(txTypes).length === 0) {
        (["salesLedger", "purchaseLedger", "cashbook"] as TxTypeKey[]).forEach(
          (key) => {
            const schema = TX_TYPE_MAPPING_SCHEMAS[key];
            if (schema) {
              initialFieldMappings[key] = {
                ...(schema.productDefaults[source.type] ?? {}),
              };
            }
          },
        );
      }
      setFieldMappings(initialFieldMappings);
      setConfigureActiveTab("connection");
      setConfigureDialogOpen(true);
    }
  };

  /**
   * Handle Parent Category Source Toggle
   */
  const handleParentCategoryToggle = (checked: boolean) => {
    if (!selectedSource) return;

    if (checked) {
      // Check if another source is already parent
      const currentParent = displayDataSources.find(
        (ds) => ds.parentCategory?.isPrimary && ds.id !== selectedSource.id,
      );

      if (currentParent) {
        // Show confirmation dialog to switch
        setNewPrimarySource(selectedSource);
        setChangePrimaryDialogOpen(true);
      } else {
        // No existing parent, just enable it
        setSelectedSource({
          ...selectedSource,
          parentCategory: {
            isPrimary: true,
            categoryField: getCategoryFieldDefault(selectedSource.type),
          },
        });
      }
    } else {
      // Disable parent
      setSelectedSource({
        ...selectedSource,
        parentCategory: {
          isPrimary: false,
          categoryField: undefined,
        },
      });
    }
  };

  /**
   * Get default category field based on source type
   */
  const getCategoryFieldDefault = (type: DataSourceType): string => {
    switch (type) {
      case "Access Financials":
      case "Access Dimensions Cloud":
        return "Nominal Code - Level 3";
      case "Excel Spreadsheet":
        return "Category";
      default:
        return "Account_Code";
    }
  };

  /**
   * Handle parent category toggle for new source
   */
  const handleToggleNewSourceParent = (checked: boolean) => {
    if (checked) {
      // Check if another source is already primary
      const currentParent = displayDataSources.find(
        (ds) => ds.parentCategory?.isPrimary,
      );

      if (currentParent) {
        // Show confirmation dialog to switch
        setNewPrimarySourceForAdd(currentParent);
        setChangePrimaryDialogOpenForAdd(true);
      } else {
        // No existing primary, just enable it
        setNewSourceIsPrimary(true);
        setNewSourceCategoryField(
          getCategoryFieldDefault(newSourceType as DataSourceType),
        );
      }
    } else {
      // Disable primary
      setNewSourceIsPrimary(false);
      setNewSourceCategoryField("");
    }
  };

  /**
   * Confirm switch to new primary source
   */
  const confirmSwitchParent = () => {
    if (!newParentSource) return;

    // Find current primary and disable it
    const currentParent = displayDataSources.find(
      (ds) => ds.parentCategory?.isPrimary && ds.id !== newParentSource.id,
    );
    if (currentParent) {
      updateDataSource(currentParent.id, {
        parentCategory: {
          isPrimary: false,
          categoryField: undefined,
        },
      });
    }

    // Enable new primary
    setSelectedSource({
      ...newParentSource,
      parentCategory: {
        isPrimary: true,
        categoryField: getCategoryFieldDefault(newParentSource.type),
      },
    });

    setChangePrimaryDialogOpen(false);
    setNewPrimarySource(null);

    toast.success("Parent Category Source Changed", {
      description: `${newParentSource.name} is now the parent category source.`,
    });
  };

  /**
   * Confirm switch to new primary source for Add dialog
   */
  const confirmSwitchParentForAdd = () => {
    if (!newParentSourceForAdd) return;

    // Disable the current primary
    updateDataSource(newParentSourceForAdd.id, {
      parentCategory: {
        isPrimary: false,
        categoryField: undefined,
      },
    });

    // Enable primary for the new source being added
    setNewSourceIsPrimary(true);
    setNewSourceCategoryField(
      getCategoryFieldDefault(newSourceType as DataSourceType),
    );

    setChangePrimaryDialogOpenForAdd(false);
    setNewPrimarySourceForAdd(null);

    toast.success("Parent Category Source Will Change", {
      description: `${newParentSourceForAdd.name} will be disabled as primary when you add this source.`,
    });
  };

  /**
   * Build API configuration object for edit based on selected source state
   */
  const buildEditApiConfiguration = (
    typeId: string,
  ): Record<string, unknown> | null => {
    const config: Record<string, unknown> = {};

    if (typeId === "af0" || typeId === "adc0") {
      const transactionTypes =
        selectedSource?.connectionDetails?.transactionTypes;
      config.SalesLedger = transactionTypes?.salesLedger ?? false;
      config.PurchaseLedger = transactionTypes?.purchaseLedger ?? false;
      config.Cashbook = transactionTypes?.cashbook ?? false;
      config.BankAccounts = transactionTypes?.bankAccounts ?? false;
      config.SalesOrders = transactionTypes?.salesOrders ?? false;
      config.PurchaseOrders = transactionTypes?.purchaseOrders ?? false;

      if (
        selectedSource?.parentCategory?.isPrimary &&
        selectedSource?.parentCategory?.categoryField
      ) {
        const levelMatch =
          selectedSource.parentCategory.categoryField.match(/Level (\d+)/);
        if (levelMatch) {
          config.CategoryLevel = parseInt(levelMatch[1], 10);
        }
      }
    }

    return Object.keys(config).length > 0 ? config : null;
  };

  /**
   * Save configuration changes
   */
  const handleSaveConfiguration = async () => {
    if (selectedSource) {
      if (!selectedSource.name || selectedSource.name.trim().length === 0) {
        toast.error("Name Required", {
          description: "Please provide a name for the data source.",
        });
        return;
      }

      if (selectedSource.name.length > 255) {
        toast.error("Name Too Long", {
          description: "Name must not exceed 255 characters.",
        });
        return;
      }

      // Validate transaction types for Access products
      if (
        selectedSource.type === "Access Financials" ||
        selectedSource.type === "Access Dimensions Cloud"
      ) {
        const transactionTypes =
          selectedSource.connectionDetails?.transactionTypes;
        const hasAtLeastOne =
          transactionTypes?.salesLedger ||
          transactionTypes?.purchaseLedger ||
          transactionTypes?.cashbook ||
          transactionTypes?.bankAccounts ||
          transactionTypes?.salesOrders ||
          transactionTypes?.purchaseOrders;

        if (!hasAtLeastOne) {
          toast.error("No Transaction Types Selected", {
            description: "Please select at least one transaction type to sync.",
          });
          return;
        }
      }

      // Validate CategoryLevel when isPrimaryCategorySource is true for af0/adc0
      if (selectedSource.parentCategory?.isPrimary) {
        const typeId =
          editApiTypeId ?? DISPLAY_NAME_TO_API_TYPE_ID[selectedSource.type];
        if (typeId === "af0" || typeId === "adc0") {
          if (!selectedSource.parentCategory?.categoryField) {
            toast.error("Category Level Required", {
              description:
                "CategoryLevel is required when this source is set as the primary category source.",
            });
            return;
          }
        }
      }

      if (mode === "live") {
        setIsSavingConfig(true);
        try {
          const typeId =
            editApiTypeId ?? DISPLAY_NAME_TO_API_TYPE_ID[selectedSource.type];
          const configuration = buildEditApiConfiguration(typeId);
          const request: UpdateDataSourceRequestApi = {
            name: selectedSource.name,
            configuration,
            isPrimaryCategorySource:
              selectedSource.parentCategory?.isPrimary ?? false,
          };

          await updateDataSourceApi(Number(selectedSource.id), request);

          await refreshLiveDataSources();

          toast.success("Configuration Saved", {
            description: `Settings for ${selectedSource.name} have been updated.`,
          });

          if (postSaveActionRef.current === "openSync") {
            postSaveActionRef.current = "none";
            setConfigureDialogOpen(false);
            setSyncScheduleDialogOpen(true);
            setMappingDirty(false);
          } else {
            setConfigureDialogOpen(false);
            setSelectedSource(null);
            setEditApiTypeId(null);
            setMappingDirty(false);
          }
        } catch (error) {
          console.error("Failed to update data source:", error);
          const validationErrors =
            error instanceof ApiError ? error.validationErrors : undefined;
          toast.error("Error", {
            description:
              validationErrors && validationErrors.length > 0
                ? validationErrors.map((e) => e.message).join(" • ")
                : error instanceof ApiError
                  ? error.message
                  : "Failed to update data source. Please try again.",
          });
        } finally {
          setIsSavingConfig(false);
        }
      } else {
        // Save connection settings
        updateDataSource(selectedSource.id, selectedSource);

        toast.success("Configuration Saved", {
          description: `Settings for ${selectedSource.name} have been updated.`,
        });

        if (postSaveActionRef.current === "openSync") {
          postSaveActionRef.current = "none";
          setConfigureDialogOpen(false);
          setSyncScheduleDialogOpen(true);
          setMappingDirty(false);
        } else {
          setConfigureDialogOpen(false);
          setSelectedSource(null);
          setMappingDirty(false);
        }
      }
    }
  };

  /**
   * Handle Test Connection
   */
  const handleTestConnection = (source: DataSourceConfig) => {
    setSelectedSource(source);
    setTestDialogOpen(true);
    setTesting(true);
    setTestResult(null);

    // Simulate connection test
    setTimeout(() => {
      setTesting(false);
      setTestResult("success");
    }, 2000);
  };

  /**
   * Check if a data source has an active schedule
   */
  const hasActiveSchedule = (source: DataSourceConfig): boolean => {
    if (mode === "live") {
      return !!source.scheduleDescription;
    }
    return (
      !!source.syncSchedule?.enabled && source.syncSchedule.frequencyMinutes > 0
    );
  };

  /**
   * Confirm disable schedule (same as modal's disable schedule action)
   * Uses disconnectSource directly to avoid race condition with selectedSource state
   */
  const confirmDisableSchedule = async () => {
    if (!disconnectSource) return;

    const disabledSchedule: SyncSchedule = {
      enabled: false,
      daysOfWeek: [],
      startTime: "00:00",
      endTime: "00:00",
      frequencyMinutes: 0, // Manual only
    };

    if (mode === "live") {
      try {
        const apiRequest = syncScheduleToApiRequest(disabledSchedule);
        await updateRefreshSchedule(Number(disconnectSource.id), apiRequest);
        await refreshLiveDataSources();

        toast.success("Automated Sync Disabled", {
          description: `Automated sync disabled for ${disconnectSource.name}`,
        });
      } catch (error) {
        toast.error("Error", {
          description:
            error instanceof ApiError
              ? error.message
              : "Failed to disable sync schedule. Please try again.",
        });
      }
    } else {
      updateDataSource(disconnectSource.id, {
        syncSchedule: disabledSchedule,
      });

      toast.success("Automated Sync Disabled", {
        description: `Automated sync disabled for ${disconnectSource.name}`,
      });
    }

    setDisconnectSource(null);
  };

  /**
   * Confirm reconnect
   */
  const confirmReconnect = () => {
    if (reconnectSource) {
      updateDataSource(reconnectSource.id, {
        status: "connected",
      });

      toast.success("Data Source Reconnected", {
        description: `${reconnectSource.name} has been reconnected. Syncing will resume.`,
      });

      setReconnectSource(null);
    }
  };

  /**
   * Handle delete - Step 1: Show warning
   */
  const handleDelete = (source: DataSourceConfig) => {
    // Check if this is the last data source
    if (displayDataSources.length === 1) {
      setCannotDeleteLastOpen(true);
      return;
    }

    setDeleteSource(source);
    setDeleteConfirmOpen(true);
  };

  /**
   * Proceed to type DELETE confirmation
   */
  const proceedToTypeConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteTypeConfirmOpen(true);
    setDeleteTypeInput("");
  };

  /**
   * Confirm delete after typing DELETE
   */
  const confirmDelete = async () => {
    if (deleteSource && deleteTypeInput === "DELETE") {
      if (mode === "live") {
        setIsDeleting(true);
        try {
          await deleteDataSource(Number(deleteSource.id));
          setDeleteTypeConfirmOpen(false);
          setDeleteSource(null);
          setDeleteTypeInput("");
          await refreshLiveDataSources();
          queryClient.invalidateQueries({
            queryKey: ["admin-dashboard-data-sources"],
          });
          queryClient.invalidateQueries({
            queryKey: ["admin-dashboard-recent-syncs"],
          });
        } catch (error) {
          console.error("Failed to delete data source:", error);
          if (error instanceof ApiError) {
            toast.error("Error", {
              description:
                error.message ||
                "Failed to delete data source. Please try again.",
            });
          } else {
            toast.error("Error", {
              description: "Failed to delete data source. Please try again.",
            });
          }
        } finally {
          setIsDeleting(false);
        }
      } else {
        removeDataSource(deleteSource.id);
        toast.error("Data Source Deleted", {
          description: `${deleteSource.name} deleted. Forecast has been refreshed.`,
        });
        setDeleteTypeConfirmOpen(false);
        setDeleteSource(null);
        setDeleteTypeInput("");
      }
    }
  };

  /**
   * Build API configuration object based on data source type
   */
  const buildApiConfiguration = (
    typeId: string,
  ): Record<string, unknown> | null => {
    const config: Record<string, unknown> = {};

    if (typeId === "af0" || typeId === "adc0") {
      // Access Financials / Access Dimensions Cloud
      config.SalesLedger = selectedTransactionTypes.salesLedger;
      config.PurchaseLedger = selectedTransactionTypes.purchaseLedger;
      config.Cashbook = selectedTransactionTypes.cashbook;
      config.BankAccounts = selectedTransactionTypes.bankAccounts;
      config.SalesOrders = selectedTransactionTypes.salesOrders;
      config.PurchaseOrders = selectedTransactionTypes.purchaseOrders;

      // CategoryLevel is required if isPrimaryCategorySource is true
      if (newSourceIsPrimary && newSourceCategoryField) {
        // Extract level number from "Nominal Code - Level X" format
        const levelMatch = newSourceCategoryField.match(/Level (\d+)/);
        if (levelMatch) {
          config.CategoryLevel = parseInt(levelMatch[1], 10);
        }
      }
    } else if (typeId === "apc0" || typeId === "plaid0") {
      // Access Paycircle / Plaid - API key/connection string
      if (newSourceApiKey) {
        config.ApiKey = newSourceApiKey;
      }
    } else if (typeId === "ex0") {
      // Excel — send full config so the backend processor can parse the file
      config.PathType = "upload";
      if (newSourceFilePassword) {
        config.FilePassword = newSourceFilePassword;
      }
      if (uploadedFileId) {
        config.UploadedFileId = uploadedFileId;
      }

      // Determine the single active transaction type
      const excelActiveTxKey = (
        Object.keys(selectedTransactionTypes) as TxTypeKey[]
      ).find(
        (k) => (selectedTransactionTypes as Record<TxTypeKey, boolean>)[k],
      );
      if (excelActiveTxKey) {
        config.TransactionType = excelActiveTxKey;

        // Column mappings from wizard state (ExcelColumnMap[])
        const wizardMappings =
          newSourceWizardFieldMappings[excelActiveTxKey] ?? [];
        const mappings = wizardMappings.map((m) => ({
          forecasterId: m.forecasterId,
          columnLetter: m.columnLetter,
          forecasterLabel: m.forecasterLabel,
        }));
        config.ColumnMappings = JSON.stringify(mappings);

        // Format declaration
        const fmtDecl = {
          negativeFormat: newSourceNegativeFormat,
          dateFormat: newSourceDateFormat,
          ...(newSourceDateFormat === "other" && newSourceDateFormatOther
            ? { dateFormatOther: newSourceDateFormatOther }
            : {}),
        };
        config.FormatDeclaration = JSON.stringify(fmtDecl);
      }
    }

    // Return null if no configuration is needed
    return Object.keys(config).length > 0 ? config : null;
  };

  /**
   * Handles file upload — uses real API in live mode, simulation in demo mode.
   * Accepts the actual File object so we can parse real content for the preview.
   */
  const handleFileUpload = async (fileName: string, file?: File) => {
    setUploadFileName(fileName);
    setUploadStage("uploading");
    setUploadProgress(0);
    setValidationStep(0);
    setParsedFileData(null);
    setPreviewExpanded(true);

    if (mode === "live" && file) {
      // Live mode: upload via API
      try {
        setUploadProgress(30);
        const ext = fileName.includes(".")
          ? fileName.substring(fileName.lastIndexOf("."))
          : "";
        const mimeType = file.type || "application/octet-stream";

        const formData = new FormData();
        formData.append("file", file);
        formData.append(
          "metadata",
          JSON.stringify({
            fileName,
            fileExtension: ext,
            mimeType,
            password: newSourceFilePassword || undefined,
          }),
        );

        setUploadProgress(60);
        const uploadResponse = await apiUpload<UploadFileResponse>(
          "upload/file",
          formData,
        );
        setUploadProgress(100);

        // Store uploaded file ID for server-side validation
        setUploadedFileId(uploadResponse.uploadedFileId);

        // Store metadata from the upload response
        if (uploadResponse.metadata) {
          setUploadMetadata(uploadResponse.metadata);
          const wsName = uploadResponse.metadata.WorksheetName_1 || "Sheet1";
          setNewSourceSheetName(wsName);
        }

        // Preview data now fetched from backend via column-mapping-preview endpoint
        // (triggered by useEffect when uploadedFileId + transactionType are both available)

        if (!uploadResponse.isValid) {
          setUploadStage("validating");
          setValidationStep(3);
          toast.error("File validation failed", {
            description:
              uploadResponse.errorMessage ||
              "The uploaded file could not be validated.",
          });
          return;
        }

        // Run validation steps
        setUploadStage("validating");
        setValidationStep(0);
        setTimeout(() => setValidationStep(1), 300);
        setTimeout(() => setValidationStep(2), 600);
        setTimeout(() => setValidationStep(3), 900);
        setTimeout(() => {
          setUploadStage("success");
        }, 1200);
      } catch (err) {
        setUploadStage("idle");
        setUploadProgress(0);
        toast.error("Upload failed", {
          description:
            err instanceof ApiError ? err.message : "Failed to upload file",
        });
      }
    } else {
      // Demo mode: simulation — preview uses EXCEL_PREVIEWS (no client-side parsing)

      // Progress bar: ramp to 100% over ~2s with slight random jitter
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += Math.random() * 18 + 8;
        if (progress >= 100) {
          progress = 100;
          clearInterval(progressInterval);
          setUploadProgress(100);

          setTimeout(() => {
            setUploadStage("validating");
            setValidationStep(0);
            setTimeout(() => setValidationStep(1), 400);
            setTimeout(() => setValidationStep(2), 900);
            setTimeout(() => setValidationStep(3), 1400);
            setTimeout(() => {
              setUploadStage("success");
            }, 1900);
          }, 300);
        } else {
          setUploadProgress(Math.round(progress));
        }
      }, 120);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFileUpload(file.name, file);
    // Reset input so the same file can be re-selected after a reset
    e.target.value = "";
  };

  const handleUploadDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setUploadDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFileUpload(file.name, file);
  };

  const resetUpload = () => {
    setUploadStage("idle");
    setUploadProgress(0);
    setUploadFileName("");
    setValidationStep(0);
    setParsedFileData(null);
    setPreviewExpanded(true);
    setUploadMetadata(null);
    setUploadedFileId(null);
  };

  /**
   * Handle Add New Data Source
   */
  const handleAddNewSource = async () => {
    if (!newSourceName || !newSourceType) {
      toast.error("Missing Information", {
        description: "Please provide a name and type for the data source.",
      });
      return;
    }

    if (newSourceName.length > 255) {
      toast.error("Name Too Long", {
        description: "Name must not exceed 255 characters.",
      });
      return;
    }

    // Validate instance selection for non-Excel sources
    if (newSourceType !== "Excel Spreadsheet") {
      if (!newSourceInstance) {
        toast.error("Missing Instance", {
          description: "Please select an instance to connect.",
        });
        return;
      }

      // Check if selected instance is already connected
      if (mode === "live") {
        // Live mode: Check API instances
        const selectedInstanceData = apiInstances.find(
          (i) => i.id === newSourceInstance,
        );
        if (selectedInstanceData?.isConnected) {
          toast.error("Instance Already Connected", {
            description:
              "This instance is already connected. Please select a different instance.",
          });
          return;
        }
      } else {
        // Demo mode: Check hardcoded instances
        const selectedInstanceData = availableInstances[
          newSourceType as keyof typeof availableInstances
        ]?.find((i) => i.id === newSourceInstance);
        if (selectedInstanceData?.isConnected) {
          toast.error("Instance Already Connected", {
            description:
              "This instance is already connected. Please select a different instance.",
          });
          return;
        }
      }
    }

    // Validate that at least one transaction type is selected (applies to all source types)
    {
      const hasSelectedType = Object.values(selectedTransactionTypes).some(
        Boolean,
      );
      if (!hasSelectedType) {
        toast.error("No Transaction Types Selected", {
          description: "Please select at least one transaction type to sync.",
        });
        return;
      }

      // Validate CategoryLevel for primary category source (frontend validation matching API)
      if (newSourceIsPrimary && !newSourceCategoryField) {
        toast.error("Category Level Required", {
          description:
            "Please select a category level when setting this as the primary category source.",
        });
        return;
      }
    }

    // Validate credentials — varies by product type
    if (
      newSourceProduct === "Paycircle" &&
      (!newSourceClientId || !newSourceClientSecret || !newSourceBureauId)
    ) {
      toast.error("Missing Credentials", {
        description:
          "Please provide your Paycircle Client ID, Client Secret, and Bureau ID.",
      });
      return;
    }
    if (
      (newSourceProduct === "Xero" ||
        newSourceProduct === "QuickBooks Online" ||
        newSourceProduct === "Sage Business Cloud") &&
      (!newSourceClientId || !newSourceClientSecret)
    ) {
      toast.error("Missing Credentials", {
        description: `Please provide your ${newSourceProduct} Client ID and Client Secret.`,
      });
      return;
    }
    if (
      (newSourceProduct === "Other Finance OAuth" ||
        newSourceProduct === "Other Payroll OAuth" ||
        newSourceProduct === "Other Banking OAuth") &&
      (!newSourceClientId || !newSourceClientSecret || !newSourceAuthEndpoint)
    ) {
      toast.error("Missing Credentials", {
        description:
          "Please provide your Client ID, Client Secret, and Authorisation Endpoint URL.",
      });
      return;
    }
    if (
      (newSourceProduct === "Plaid" || newSourceProduct === "TrueLayer") &&
      (!newSourceClientId || !newSourceClientSecret)
    ) {
      toast.error("Missing Credentials", {
        description:
          "Please provide your Client ID and Client Secret to connect via " +
          newSourceProduct +
          ".",
      });
      return;
    }

    // Live mode: Call API to create data source
    if (mode === "live") {
      setIsSubmitting(true);
      try {
        const typeId =
          DISPLAY_NAME_TO_API_TYPE_ID[newSourceType as DataSourceType];
        const configuration = buildApiConfiguration(typeId);

        const request: CreateDataSourceRequestApi = {
          name: newSourceName,
          typeId: typeId,
          instanceId: newSourceInstance || null,
          configuration: configuration,
          isPrimaryCategorySource: newSourceIsPrimary || null,
        };

        await createDataSource(request);

        // Reset form and close dialog
        resetNewSourceForm();
        setAddSourceDialogOpen(false);

        // Refresh the data source list and find the newly created source
        const refreshed = await refreshLiveDataSources();
        queryClient.invalidateQueries({
          queryKey: ["admin-dashboard-data-sources"],
        });
        queryClient.invalidateQueries({
          queryKey: ["admin-dashboard-recent-syncs"],
        });
        let foundSource: DataSourceConfig | null = null;
        if (refreshed) {
          foundSource = refreshed.find((s) => s.name === newSourceName) ?? null;
          setNewlyAddedSource(foundSource);
        }

        // Excel — column mapping done in wizard, no Configure needed
        // All other sources — go straight to Configure
        if (foundSource) {
          if (newSourceType === "Excel Spreadsheet") {
            toast.success("Excel Source Added", {
              description: `${newSourceName} has been added and is ready to use. Column mapping is configured and active.`,
            });
          } else {
            handleConfigure(foundSource);
          }
        }
      } catch (error) {
        console.error("Failed to create data source:", error);
        if (error instanceof ApiError) {
          toast.error("Error", {
            description:
              error.message ||
              "Failed to create data source. Please try again.",
          });
        } else {
          toast.error("Error", {
            description: "Failed to create data source. Please try again.",
          });
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Demo mode: Use existing local storage behavior
    // Build connection details based on type
    const connectionDetails: DataSourceConfig["connectionDetails"] = {};

    // Access products use SSO/native authentication (no API key needed)
    if (
      newSourceType === "Access Financials" ||
      newSourceType === "Access Dimensions Cloud"
    ) {
      connectionDetails.instanceId = newSourceInstance; // Store the instance ID
      // Store selected transaction types
      connectionDetails.transactionTypes = {
        salesLedger: selectedTransactionTypes.salesLedger,
        purchaseLedger: selectedTransactionTypes.purchaseLedger,
        cashbook: selectedTransactionTypes.cashbook,
      };
      // No API key needed - uses SSO authentication
    } else if (newSourceProduct === "Paycircle") {
      // Paycircle — OAuth 2.0 with bureau/company ID
      connectionDetails.clientId = newSourceClientId;
      connectionDetails.clientSecret = newSourceClientSecret;
      connectionDetails.bureauId = newSourceBureauId;
    } else if (
      newSourceProduct === "Xero" ||
      newSourceProduct === "QuickBooks Online" ||
      newSourceProduct === "Sage Business Cloud"
    ) {
      // Named OAuth third-party — Client ID + Secret
      connectionDetails.clientId = newSourceClientId;
      connectionDetails.clientSecret = newSourceClientSecret;
    } else if (
      newSourceProduct === "Other Finance OAuth" ||
      newSourceProduct === "Other Payroll OAuth" ||
      newSourceProduct === "Other Banking OAuth"
    ) {
      // Generic OAuth catchall — Client ID + Secret + Auth endpoint
      connectionDetails.clientId = newSourceClientId;
      connectionDetails.clientSecret = newSourceClientSecret;
      connectionDetails.authEndpoint = newSourceAuthEndpoint;
    } else if (
      newSourceProduct === "Plaid" ||
      newSourceProduct === "TrueLayer"
    ) {
      // Bank feed providers — institution-level OAuth (Client ID + Secret, account selection handled post-auth)
      connectionDetails.clientId = newSourceClientId;
      connectionDetails.clientSecret = newSourceClientSecret;
    } else if (newSourceType === "Excel Spreadsheet") {
      connectionDetails.pathType = "upload";
      if (newSourceFilePassword) {
        connectionDetails.filePassword = newSourceFilePassword;
      }

      // Build the selected transaction type key
      const excelTxKey = (Object.entries(selectedTransactionTypes).find(
        ([, v]) => v,
      )?.[0] ?? "") as TxTypeKey;

      // Build worksheetConfigs from wizard state
      const worksheetConfig: ExcelWorksheetConfig = {
        sheetName: newSourceSheetName || "",
        headerRow: newSourceHasHeaderRow ? newSourceHeaderRow : 0,
        dataStartRow: newSourceDataStartRow,
      };

      // Build columnMappings from wizard field mappings
      const rawMappings: Record<string, string> = (newSourceWizardFieldMappings[
        excelTxKey
      ] ?? {}) as Record<string, string>;
      const schema = columnMappingPreview?.schema ?? [];
      const columnMappings: ExcelColumnMap[] = schema
        .filter((f) => rawMappings[f.forecasterId])
        .map((f) => ({
          forecasterId: f.forecasterId,
          forecasterLabel: f.forecasterLabel,
          columnLetter: rawMappings[f.forecasterId],
          required: f.required,
        }));

      const formatDeclaration: ExcelFormatDeclaration = {
        thousandsSeparator: newSourceThousandsSeparator,
        decimalSeparator: newSourceDecimalSeparator,
        negativeFormat: newSourceNegativeFormat,
        hasCurrencySymbol: newSourceHasCurrencySymbol,
        dateFormat: newSourceDateFormat,
        ...(newSourceDateFormat === "other" && newSourceDateFormatOther
          ? { dateFormatOther: newSourceDateFormatOther }
          : {}),
      };

      connectionDetails.excelConfig = {
        fileMode: "manual_upload",
        dateFormat: newSourceDateFormat,
        amountFormat: newSourceAmountFormat,
        validationBehaviour: newSourceValidationBehaviour,
        worksheetConfigs: excelTxKey ? { [excelTxKey]: worksheetConfig } : {},
        columnMappings: excelTxKey ? { [excelTxKey]: columnMappings } : {},
        formatDeclaration,
      };
    }

    // Add the new data source
    // Calculate priority (next available priority number)
    const maxPriority =
      displayDataSources.length > 0
        ? Math.max(...displayDataSources.map((ds) => ds.priority))
        : 0;

    const newSourceObj: DataSourceConfig = {
      id: `demo-${Date.now()}`,
      name: newSourceName,
      type: newSourceType,
      priority: maxPriority + 1,
      connectionDetails,
      status: "connected",
      lastSync: null,
      recordCount: 0,
      syncFrequency: "hourly",
      autoSync: true,
      dateAdded: new Date().toISOString(),
      parentCategory: newSourceIsPrimary
        ? {
            isPrimary: true,
            categoryField: newSourceCategoryField,
          }
        : undefined,
    };

    addDataSource(newSourceObj);

    // Reset form and close dialog
    resetNewSourceForm();
    setAddSourceDialogOpen(false);

    // Excel — column mapping is complete in the wizard, no Configure needed
    // All other sources — go straight to Configure
    if (newSourceType === "Excel Spreadsheet") {
      toast.success("Excel Source Added", {
        description: `${newSourceName} has been added and is ready to use. Column mapping is configured and active.`,
      });
    } else {
      handleConfigure(newSourceObj);
    }
  };

  /**
   * Reset new source form
   */
  const resetNewSourceForm = () => {
    setNewSourceName("");
    setNewSourceGenericType("");
    setNewSourceProduct("");
    setNewSourceType("");
    setNewSourceInstance("");
    setNewSourceApiKey("");
    setNewSourceClientId("");
    setNewSourceClientSecret("");
    setNewSourceBureauId("");
    setNewSourceAuthEndpoint("");
    setNewSourcePathType("upload");
    setNewSourceCloudProvider("");
    setNewSourceFilePath("");
    setNewSourceFilePassword("");
    setUploadStage("idle");
    setUploadProgress(0);
    setUploadFileName("");
    setValidationStep(0);
    setUploadMetadata(null);
    setUploadedFileId(null);
    setNewSourceDatabaseType("");
    setNewSourceConnectionString("");
    setNewSourceTables([]);
    setSelectedTransactionTypes(getDefaultTransactionTypes(""));
    setNewSourceIsPrimary(false);
    setNewSourceCategoryField("");
    setApiInstances([]);
    // Excel wizard extended state
    setNewSourceWizardStep("config");
    setNewSourceSheetName("");
    setNewSourceHasHeaderRow(true);

    setNewSourceHeaderRow(1);
    setNewSourceDataStartRow(2);
    setNewSourceDateFormat("DD/MM/YYYY");
    setNewSourceDateFormatOther("");
    setNewSourceAmountFormat("negative_outflow");
    setNewSourceValidationBehaviour("skip_and_flag");
    setNewSourceThousandsSeparator("comma");
    setNewSourceDecimalSeparator("period");
    setNewSourceNegativeFormat("minus_sign");
    setNewSourceHasCurrencySymbol(false);
    setNewSourceWizardFieldMappings({});
    setValidationResults(null);
    setIsValidatingData(false);
    setValidationError(null);
    setSplitterModalOpen(false);
    setSplitterColumn("");
    setSplitterFieldId("");
    setSplitterNegativeIs("outgoing");
    setSplitterAlreadyHandled(new Set());
    setOneDirModalOpen(false);
    setOneDirMappedSide(null);
    setTemplateModalOpen(false);
    setTemplateModalMode("individual");
    setTemplateIndividualType("");
    setTemplateCombinedTypes(new Set());
    setExampleRowModalOpen(false);
    setAutoMapApplied(false);
    setAutoMapCount(0);
  };

  // Derived: required fields completion for wizard Save button
  const wizardActiveTxKey = (
    Object.keys(selectedTransactionTypes) as TxTypeKey[]
  ).find((k) => (selectedTransactionTypes as Record<TxTypeKey, boolean>)[k]) as
    | TxTypeKey
    | undefined;
  // Cash movement terminology — used throughout the Excel section
  const cashTerms = getCashMovementTerms();

  const wizardRequiredMapped = wizardActiveTxKey
    ? (columnMappingPreview?.schema ?? [])
        .filter((f) => f.required)
        .every((f) =>
          (newSourceWizardFieldMappings[wizardActiveTxKey] ?? []).some(
            (m) => m.forecasterId === f.forecasterId && m.columnLetter,
          ),
        )
    : false;

  return (
    <div className="space-y-6 p-6">
      {/* Loading State */}
      {isLoadingList && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">
              Loading data sources...
            </span>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isLoadingList && displayDataSources.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="p-4 bg-primary/10 rounded-full mb-4">
              <Database className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              No Data Sources Configured
            </h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              Get started by adding your first data source. Connect to
              accounting systems, payroll providers, or upload Excel
              spreadsheets.
            </p>
            <Button size="lg" onClick={() => setAddSourceDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Data Source
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Data Sources List */}
      {!isLoadingList && displayDataSources.length > 0 && (
        <div className="grid gap-4">
          {displayDataSources.map((source) => (
            <Card key={source.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Database className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{source.name}</CardTitle>
                      <CardDescription>
                        {getGenericSourceType(source.type)} — {source.type}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge
                    variant={
                      source.status === "connected"
                        ? "default"
                        : source.status === "error"
                          ? "destructive"
                          : source.status === "deleting"
                            ? "secondary"
                            : "secondary"
                    }
                    className="gap-1"
                  >
                    {source.status === "deleting" ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : source.status === "connected" ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : source.status === "error" ? (
                      <AlertCircle className="h-3 w-3" />
                    ) : (
                      <AlertCircle className="h-3 w-3" />
                    )}
                    {source.status === "deleting"
                      ? "Deleting..."
                      : source.status === "connected"
                        ? "Connected"
                        : source.status === "error"
                          ? "Error"
                          : "Disconnected"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">
                      Last sync:{" "}
                      <span className="text-foreground font-medium">
                        {formatRelativeSyncTime(source.lastSync)}
                      </span>
                    </p>
                    <p className="text-muted-foreground">
                      Synced:{" "}
                      <span className="text-foreground font-medium">
                        {source.recordCount.toLocaleString()} records
                        {source.status === "disconnected" && (
                          <span className="text-muted-foreground">
                            {" "}
                            (no longer updating)
                          </span>
                        )}
                      </span>
                    </p>
                    <p className="text-muted-foreground">
                      {getScheduleIndicator(source)}
                    </p>
                    {source.parentCategory?.isPrimary && (
                      <p className="text-amber-600 font-medium flex items-center gap-1">
                        <Star className="h-4 w-4 fill-amber-600" />
                        Parent Category Source
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {source.status === "deleting" ? (
                      <span className="text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Deleting data source...
                      </span>
                    ) : (
                      <>
                        {/* Sync Schedule / Disable Schedule — hidden for manual-upload Excel sources (no recurring sync possible) */}
                        {!(
                          source.type === "Excel Spreadsheet" &&
                          (source.connectionDetails?.excelConfig?.fileMode ===
                            "manual_upload" ||
                            !source.connectionDetails?.excelConfig?.fileMode)
                        ) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSyncSchedule(source)}
                            >
                              <Clock className="h-4 w-4 mr-2" />
                              Sync Schedule
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDisconnectSource(source)}
                              disabled={!hasActiveSchedule(source)}
                            >
                              Disable Schedule
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSyncNow(source)}
                          disabled={syncingSources.has(source.id)}
                        >
                          <RefreshCw
                            className={`h-4 w-4 mr-2 ${syncingSources.has(source.id) ? "animate-spin" : ""}`}
                          />
                          {syncingSources.has(source.id)
                            ? "Syncing..."
                            : "Sync Now"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSyncHistorySource(source)}
                        >
                          <History className="h-4 w-4 mr-2" />
                          Sync History
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleConfigure(source)}
                        >
                          <Filter className="h-4 w-4 mr-2" />
                          Configure
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(source)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add New Data Source Button (when sources exist) */}
      {!isLoadingList && displayDataSources.length > 0 && (
        <Card className="border-dashed">
          <CardContent className="flex items-center justify-center py-8">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setAddSourceDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Data Source
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Configure Dialog */}
      <Dialog open={configureDialogOpen} onOpenChange={setConfigureDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>Configure Data Source</DialogTitle>
            <DialogDescription>
              {selectedSource?.name} — connection settings and transaction
              mapping rules
            </DialogDescription>
          </DialogHeader>

          <Tabs
            value={configureActiveTab}
            onValueChange={(v) => {
              setConfigureActiveTab(v);
              setMappingSearchQuery("");
            }}
            className="flex-1 flex flex-col overflow-hidden min-w-0"
          >
            <TabsList className="mx-6 mt-4 shrink-0 flex-wrap justify-start gap-y-1">
              <TabsTrigger value="connection" className="flex-none">
                Connection
              </TabsTrigger>
              {/* Dynamic mapping tabs — one per active transaction type */}
              {selectedSource &&
                (() => {
                  const txTypes =
                    selectedSource.connectionDetails?.transactionTypes ?? {};
                  const genericType = getGenericSourceType(selectedSource.type);
                  // Determine which keys are active
                  const activeKeys: TxTypeKey[] = [];
                  TX_TYPE_GRID.flat().forEach((def) => {
                    if (def.release) return; // skip R2/R3
                    const isRelevant = def.relevantFor.includes(genericType);
                    const isChecked = txTypes[def.key] === true;
                    if (isRelevant && isChecked) activeKeys.push(def.key);
                  });
                  // Fallback for Finance sources with no explicit txTypes
                  if (
                    genericType === "Finance" &&
                    Object.keys(txTypes).length === 0
                  ) {
                    (
                      [
                        "salesLedger",
                        "purchaseLedger",
                        "cashbook",
                      ] as TxTypeKey[]
                    ).forEach((k) => {
                      if (!activeKeys.includes(k)) activeKeys.push(k);
                    });
                  }
                  const isExcelSource =
                    getGenericSourceType(selectedSource.type) === "Excel";
                  return activeKeys.map((key) => {
                    // Compute mapping progress for Excel sources
                    let progressBadge: React.ReactNode = null;
                    if (isExcelSource) {
                      const EXCEL_FIELD_SCHEMAS_FOR_PROGRESS: Partial<
                        Record<
                          TxTypeKey,
                          Array<{ forecasterId: string; required: boolean }>
                        >
                      > = {
                        purchaseOrders: [
                          { forecasterId: "order_number", required: true },
                          { forecasterId: "account_code", required: false },
                          { forecasterId: "account_name", required: true },
                          { forecasterId: "description", required: false },
                          { forecasterId: "gross_outstanding", required: true },
                          { forecasterId: "order_date", required: true },
                          { forecasterId: "required_date", required: true },
                          { forecasterId: "analysis_code", required: false },
                          { forecasterId: "category", required: false },
                        ],
                        salesOrders: [
                          { forecasterId: "order_number", required: true },
                          { forecasterId: "account_code", required: false },
                          { forecasterId: "account_name", required: true },
                          { forecasterId: "description", required: false },
                          { forecasterId: "gross_outstanding", required: true },
                          { forecasterId: "order_date", required: true },
                          { forecasterId: "required_date", required: true },
                          { forecasterId: "analysis_code", required: false },
                          { forecasterId: "category", required: false },
                        ],
                        salesLedger: [
                          { forecasterId: "invoice_number", required: true },
                          { forecasterId: "customer_name", required: true },
                          { forecasterId: "invoice_date", required: true },
                          { forecasterId: "due_date", required: true },
                          { forecasterId: "amount", required: true },
                          { forecasterId: "vat_amount", required: false },
                          { forecasterId: "status", required: true },
                          { forecasterId: "reference", required: false },
                        ],
                        purchaseLedger: [
                          { forecasterId: "account_code", required: false },
                          { forecasterId: "supplier_name", required: true },
                          { forecasterId: "description", required: false },
                          { forecasterId: "gross", required: true },
                          { forecasterId: "transaction_type", required: true },
                          { forecasterId: "outstanding", required: true },
                          { forecasterId: "transaction_date", required: true },
                          { forecasterId: "due_date", required: true },
                          { forecasterId: "anticipated_date", required: false },
                          { forecasterId: "category", required: false },
                        ],
                        payroll: [
                          { forecasterId: "employee_id", required: true },
                          { forecasterId: "employee_name", required: false },
                          { forecasterId: "pay_period", required: true },
                          { forecasterId: "pay_date", required: true },
                          { forecasterId: "gross_pay", required: true },
                          { forecasterId: "employer_ni", required: false },
                          { forecasterId: "pension", required: false },
                          { forecasterId: "net_pay", required: true },
                        ],
                        cashbook: [
                          { forecasterId: "transaction_date", required: true },
                          { forecasterId: "description", required: false },
                          { forecasterId: "reference", required: false },
                          { forecasterId: "debit", required: false },
                          { forecasterId: "credit", required: false },
                          { forecasterId: "category", required: false },
                        ],
                        bankAccounts: [
                          { forecasterId: "account_name", required: true },
                          { forecasterId: "bank_name", required: true },
                          { forecasterId: "sort_code", required: false },
                          { forecasterId: "account_number", required: false },
                          { forecasterId: "currency", required: true },
                          { forecasterId: "balance_date", required: true },
                          { forecasterId: "closing_balance", required: true },
                        ],
                      };
                      const schema =
                        EXCEL_FIELD_SCHEMAS_FOR_PROGRESS[key] ?? [];
                      const requiredFields = schema.filter((f) => f.required);
                      const existingMaps: ExcelColumnMap[] =
                        (selectedSource.connectionDetails?.excelConfig
                          ?.columnMappings?.[key] as
                          | ExcelColumnMap[]
                          | undefined) ?? [];
                      const mappedRequiredCount = requiredFields.filter((f) =>
                        existingMaps.some(
                          (m) =>
                            m.forecasterId === f.forecasterId && m.columnLetter,
                        ),
                      ).length;
                      const total = requiredFields.length;
                      const allMapped =
                        total > 0 && mappedRequiredCount === total;
                      const noneMapped = mappedRequiredCount === 0;
                      progressBadge = (
                        <span
                          className={`inline-flex items-center justify-center rounded-full text-[10px] font-semibold leading-none px-1.5 py-0.5 min-w-[1.25rem] ${
                            allMapped
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                              : noneMapped
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                                : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400"
                          }`}
                        >
                          {allMapped ? (
                            <Check className="h-2.5 w-2.5" />
                          ) : (
                            `${mappedRequiredCount}/${total}`
                          )}
                        </span>
                      );
                    }
                    return (
                      <TabsTrigger
                        key={key}
                        value={`map-${key}`}
                        className="flex-none gap-2"
                      >
                        <GitMerge className="h-3.5 w-3.5" />
                        {TX_TYPE_LABELS[key]}
                        {progressBadge}
                      </TabsTrigger>
                    );
                  });
                })()}
            </TabsList>

            {/* ── Connection Tab ── */}
            <TabsContent
              value="connection"
              className="flex-1 overflow-y-auto px-6 pb-4 mt-4"
            >
              <div className="space-y-4">
                {isLoadingConfig && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Loading configuration...
                    </span>
                  </div>
                )}
                {/* Data Source Name - Editable */}
                <div className="space-y-2">
                  <Label>Data Source Name</Label>
                  <Input
                    value={selectedSource?.name || ""}
                    onChange={(e) =>
                      setSelectedSource(
                        selectedSource
                          ? { ...selectedSource, name: e.target.value }
                          : null,
                      )
                    }
                    placeholder="Enter data source name"
                  />
                </div>

                {/* Source Type - Read-only */}
                <div className="space-y-2">
                  <Label>Source Type</Label>
                  <div className="flex gap-2">
                    <Input
                      value={
                        selectedSource
                          ? getGenericSourceType(selectedSource.type)
                          : ""
                      }
                      disabled
                      className="bg-muted cursor-not-allowed w-32 shrink-0"
                    />
                    <Input
                      value={selectedSource?.type || ""}
                      disabled
                      className="bg-muted cursor-not-allowed flex-1"
                      placeholder="Product"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    To change source type or product, disconnect and create a
                    new connection
                  </p>
                </div>

                {/* Instance - Read-only (for Access products) */}
                {(selectedSource?.type === "Access Financials" ||
                  selectedSource?.type === "Access Dimensions Cloud" ||
                  selectedSource?.type === "Paycircle" ||
                  selectedSource?.type === "Plaid") && (
                  <div className="space-y-2">
                    <Label>Instance</Label>
                    <Input
                      value={
                        mode === "live"
                          ? editInstanceName || "Not specified"
                          : selectedSource?.connectionDetails?.instanceId
                            ? availableInstances[selectedSource.type]?.find(
                                (inst) =>
                                  inst.id ===
                                  selectedSource.connectionDetails?.instanceId,
                              )?.name ||
                              selectedSource.connectionDetails.instanceId
                            : "Not specified"
                      }
                      disabled
                      className="bg-muted cursor-not-allowed"
                    />
                    <p className="text-sm text-muted-foreground">
                      To change instance, disconnect and create new connection
                    </p>
                  </div>
                )}

                {/* Transaction Types - 3×3 grid, shown for any source with transactionTypes support */}
                {selectedSource &&
                  (() => {
                    const genericType = getGenericSourceType(
                      selectedSource.type,
                    );
                    // Show for Finance, Payroll, and Banking sources
                    if (
                      !["Finance", "Payroll", "Banking"].includes(genericType)
                    )
                      return null;
                    return (
                      <div className="space-y-3">
                        <div>
                          <Label>Transaction Types</Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            Select which data to sync from{" "}
                            <strong>{selectedSource.name}</strong> into the cash
                            forecast. Active types will each get their own
                            mapping tab above.
                          </p>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {TX_TYPE_GRID.flat().map((def) => {
                            const isRelevant =
                              def.relevantFor.includes(genericType);
                            const isChecked =
                              selectedSource.connectionDetails
                                ?.transactionTypes?.[def.key] === true;
                            const isPayrollForFinance =
                              def.key === "payroll" &&
                              genericType === "Finance";
                            const effectiveRelease = isPayrollForFinance
                              ? "R2"
                              : def.release;
                            const isDisabled =
                              !!effectiveRelease || !isRelevant;
                            return (
                              <div
                                key={def.key}
                                className={`relative border rounded-lg p-3 transition-colors ${
                                  isDisabled
                                    ? "opacity-40 bg-muted/30 cursor-not-allowed"
                                    : isChecked
                                      ? "border-primary bg-primary/5"
                                      : "hover:bg-muted/30 cursor-pointer"
                                }`}
                                onClick={() => {
                                  if (isDisabled || !selectedSource) return;
                                  setSelectedSource({
                                    ...selectedSource,
                                    connectionDetails: {
                                      ...selectedSource.connectionDetails,
                                      transactionTypes: {
                                        ...selectedSource.connectionDetails
                                          ?.transactionTypes,
                                        [def.key]: !isChecked,
                                      },
                                    },
                                  });
                                  setMappingDirty(true);
                                }}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span
                                        className={`text-sm font-medium ${isDisabled ? "text-muted-foreground" : ""}`}
                                      >
                                        {def.label}
                                      </span>
                                      {effectiveRelease && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs h-4 px-1"
                                        >
                                          {effectiveRelease}
                                        </Badge>
                                      )}
                                      {!isRelevant && !effectiveRelease && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs h-4 px-1"
                                        >
                                          N/A
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                                      {def.description}
                                    </p>
                                  </div>
                                  {!isDisabled && (
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={() => {}}
                                      className="shrink-0 mt-0.5 pointer-events-none"
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                {/* ── Excel Workbook Configuration — shown for Excel sources only ── */}
                {selectedSource?.type === "Excel Spreadsheet" && (
                  <div className="space-y-3 pt-4 border-t">
                    {/* Catch-all messaging */}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-3 flex gap-2">
                      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">
                          Excel — Universal Data Source
                        </p>
                        <p className="text-xs text-blue-800 dark:text-blue-200">
                          Use this source type for any system that doesn't have
                          a direct API integration — including Sage, legacy
                          finance systems, ordering systems, HR platforms, or
                          any spreadsheet your team maintains manually. Each
                          transaction type you enable gets its own mapping tab
                          so the forecaster knows exactly which columns contain
                          which data.
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">
                        Workbook Settings
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        These settings apply to the workbook as a whole.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">File Mode</Label>
                        <Select
                          value={
                            selectedSource.connectionDetails?.excelConfig
                              ?.fileMode ?? "manual_upload"
                          }
                          onValueChange={(v) =>
                            setSelectedSource({
                              ...selectedSource,
                              connectionDetails: {
                                ...selectedSource.connectionDetails,
                                excelConfig: {
                                  ...selectedSource.connectionDetails
                                    ?.excelConfig!,
                                  fileMode: v as ExcelFileMode,
                                  worksheetConfigs:
                                    selectedSource.connectionDetails
                                      ?.excelConfig?.worksheetConfigs ?? {},
                                  columnMappings:
                                    selectedSource.connectionDetails
                                      ?.excelConfig?.columnMappings ?? {},
                                  dateFormat:
                                    selectedSource.connectionDetails
                                      ?.excelConfig?.dateFormat ?? "DD/MM/YYYY",
                                  amountFormat:
                                    selectedSource.connectionDetails
                                      ?.excelConfig?.amountFormat ??
                                    "negative_outflow",
                                  validationBehaviour:
                                    selectedSource.connectionDetails
                                      ?.excelConfig?.validationBehaviour ??
                                    "skip_and_flag",
                                },
                              },
                            })
                          }
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual_upload">
                              Manual Upload
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          File Password{" "}
                          <span className="text-muted-foreground font-normal">
                            (optional)
                          </span>
                        </Label>
                        <div className="relative">
                          <Input
                            className="h-8 text-sm pr-8"
                            type={showEditPassword ? "text" : "password"}
                            placeholder="Leave blank if not password protected"
                            value={
                              selectedSource.connectionDetails?.filePassword ??
                              ""
                            }
                            onChange={(e) =>
                              setSelectedSource({
                                ...selectedSource,
                                connectionDetails: {
                                  ...selectedSource.connectionDetails,
                                  filePassword: e.target.value || undefined,
                                },
                              })
                            }
                          />
                          <button
                            type="button"
                            onClick={() => setShowEditPassword((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            tabIndex={-1}
                            aria-label={
                              showEditPassword
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            {showEditPassword ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Required to open the workbook if it is encrypted
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Parent Category Source Section */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-base">
                          Parent Category Source
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Primary categories define how cash flow is organised.
                          Only one data source can be primary.
                        </p>
                      </div>
                      <Switch
                        checked={
                          selectedSource?.parentCategory?.isPrimary === true
                        }
                        onCheckedChange={handleParentCategoryToggle}
                      />
                    </div>
                  </div>

                  {/* Category Field Configuration - Only show when primary is enabled */}
                  {selectedSource?.parentCategory?.isPrimary && (
                    <div className="space-y-2">
                      <Label>Category Field/Column Name</Label>
                      <p className="text-sm text-muted-foreground mb-2">
                        Which field in this data source contains the category
                        information?
                      </p>

                      {/* For Access Financials - Show dropdown */}
                      {(selectedSource?.type === "Access Financials" ||
                        selectedSource?.type === "Access Dimensions Cloud") && (
                        <Select
                          disabled={
                            mode === "live" &&
                            isLoadingNominalCategoryLabels &&
                            !nominalCategoryLabels
                          }
                          value={
                            selectedSource?.parentCategory?.categoryField ||
                            "Nominal Code - Level 3"
                          }
                          onValueChange={(value) => {
                            if (selectedSource) {
                              setSelectedSource({
                                ...selectedSource,
                                parentCategory: {
                                  ...selectedSource.parentCategory,
                                  isPrimary: true,
                                  categoryField: value,
                                },
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                mode === "live" &&
                                isLoadingNominalCategoryLabels &&
                                !nominalCategoryLabels
                                  ? "Loading category labels..."
                                  : "Select category field"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {mode === "live" &&
                            isLoadingNominalCategoryLabels &&
                            !nominalCategoryLabels ? (
                              <SelectItem value="loading" disabled>
                                Loading...
                              </SelectItem>
                            ) : (
                              [1, 2, 3, 4, 5, 6, 7, 8].map((level) => (
                                <SelectItem
                                  key={level}
                                  value={`Nominal Code - Level ${level}`}
                                >
                                  {getNominalCategoryLabel(level)}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      )}

                      {/* For Excel - Show text input */}
                      {selectedSource?.type === "Excel Spreadsheet" && (
                        <>
                          <Input
                            value={
                              selectedSource?.parentCategory?.categoryField ||
                              ""
                            }
                            onChange={(e) => {
                              if (selectedSource) {
                                setSelectedSource({
                                  ...selectedSource,
                                  parentCategory: {
                                    ...selectedSource.parentCategory,
                                    isPrimary: true,
                                    categoryField: e.target.value,
                                  },
                                });
                              }
                            }}
                            placeholder="e.g., Budget_Category, Cost_Centre, Category"
                          />
                          <p className="text-sm text-muted-foreground">
                            Enter the column name from your spreadsheet
                          </p>
                          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-2.5 mt-1">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                            <p className="text-xs text-amber-800 dark:text-amber-200">
                              Category names must match your spreadsheet values
                              exactly — including capitalisation, abbreviations,
                              and spacing. Variations such as{" "}
                              <em>"Cost Centre"</em> vs <em>"cost_centre"</em>{" "}
                              will not be matched automatically.
                            </p>
                          </div>
                        </>
                      )}

                      {/* For other types - Show text input with generic placeholder */}
                      {selectedSource?.type !== "Access Financials" &&
                        selectedSource?.type !== "Access Dimensions Cloud" &&
                        selectedSource?.type !== "Excel Spreadsheet" && (
                          <Input
                            value={
                              selectedSource?.parentCategory?.categoryField ||
                              ""
                            }
                            onChange={(e) => {
                              if (selectedSource) {
                                setSelectedSource({
                                  ...selectedSource,
                                  parentCategory: {
                                    ...selectedSource.parentCategory,
                                    isPrimary: true,
                                    categoryField: e.target.value,
                                  },
                                });
                              }
                            }}
                            placeholder="e.g., Account_Code, Category, Cost_Centre"
                          />
                        )}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ── Dynamic Mapping Tabs — one per active transaction type ── */}
            {selectedSource &&
              TX_TYPE_GRID.flat()
                .filter((def) => !def.release)
                .map((def) => {
                  const txTypes =
                    selectedSource.connectionDetails?.transactionTypes ?? {};
                  const genericType = getGenericSourceType(selectedSource.type);
                  const isRelevant = def.relevantFor.includes(genericType);
                  // Include in fallback if Finance source with no txTypes set
                  const isFallback =
                    genericType === "Finance" &&
                    Object.keys(txTypes).length === 0 &&
                    (
                      [
                        "salesLedger",
                        "purchaseLedger",
                        "cashbook",
                      ] as TxTypeKey[]
                    ).includes(def.key);
                  const isActive =
                    isRelevant && (txTypes[def.key] === true || isFallback);
                  if (!isActive) return null;

                  const schema = TX_TYPE_MAPPING_SCHEMAS[def.key];
                  const currentFieldMap = fieldMappings[def.key] ?? {};
                  // ── Excel sources get a column mapper, not an API field mapper ──
                  const isExcel = selectedSource.type === "Excel Spreadsheet";
                  const excelConfig =
                    selectedSource.connectionDetails?.excelConfig;
                  const existingColMaps: ExcelColumnMap[] =
                    (excelConfig?.columnMappings?.[def.key] as
                      | ExcelColumnMap[]
                      | undefined) ?? [];

                  // Duplicate column letter detection for edit panel
                  const editColumnLetterCounts = existingColMaps.reduce(
                    (acc, m) => {
                      if (m.columnLetter) {
                        const letter = m.columnLetter.toUpperCase();
                        acc[letter] = (acc[letter] || 0) + 1;
                      }
                      return acc;
                    },
                    {} as Record<string, number>,
                  );
                  const editDuplicateColumnLetters = new Set(
                    Object.entries(editColumnLetterCounts)
                      .filter(([_, count]) => count > 1)
                      .map(([letter]) => letter),
                  );
                  const editHasDuplicateColumns =
                    editDuplicateColumnLetters.size > 0;

                  // Schema from backend preview (wizard) or from EXCEL_FIELD_SCHEMAS_FOR_PROGRESS inline (edit panel)
                  const excelSchema = columnMappingPreview?.schema;
                  const preview = parsedFileData ?? undefined;

                  return (
                    <TabsContent
                      key={def.key}
                      value={`map-${def.key}`}
                      className="flex-1 overflow-y-auto px-6 pb-4 mt-4 space-y-6 min-w-0"
                    >
                      {/* ── EXCEL: Column Mapper (replaces API Field Mapper) ── */}
                      {isExcel ? (
                        <div className="space-y-4 min-w-0">
                          {/* Per-tab Worksheet Settings */}
                          {(() => {
                            const wsConfig =
                              excelConfig?.worksheetConfigs?.[def.key];
                            return (
                              <div className="border rounded-lg p-4 bg-muted/10 space-y-3">
                                <div>
                                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Worksheet Settings — {def.label}
                                  </h4>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    Data is read from the first sheet only. Row
                                    1 is the header row; data begins on row 2.
                                  </p>
                                </div>
                                <div className="grid grid-cols-2 gap-3 hidden">
                                  <div className="space-y-1">
                                    <Input
                                      className="h-8 text-sm"
                                      type="number"
                                      min={1}
                                      value={wsConfig?.dataStartRow ?? 2}
                                      onChange={(e) => {
                                        if (!selectedSource) return;
                                        const val = Math.max(
                                          1,
                                          parseInt(e.target.value) || 2,
                                        );
                                        setSelectedSource({
                                          ...selectedSource,
                                          connectionDetails: {
                                            ...selectedSource.connectionDetails,
                                            excelConfig: {
                                              ...selectedSource
                                                .connectionDetails
                                                ?.excelConfig!,
                                              worksheetConfigs: {
                                                ...(excelConfig?.worksheetConfigs ??
                                                  {}),
                                                [def.key]: {
                                                  sheetName:
                                                    wsConfig?.sheetName ?? "",
                                                  headerRow:
                                                    wsConfig?.headerRow ?? 1,
                                                  dataStartRow: val,
                                                },
                                              },
                                            },
                                          },
                                        });
                                        setMappingDirty(true);
                                      }}
                                    />
                                    {(wsConfig?.dataStartRow ?? 2) <=
                                    (wsConfig?.headerRow ?? 1) ? (
                                      <p className="text-xs text-destructive">
                                        Data start row must be greater than the
                                        header row
                                      </p>
                                    ) : (
                                      <p className="text-xs text-muted-foreground">
                                        First row of actual data (after headers)
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Header */}
                          <div>
                            <h3 className="text-sm font-semibold">
                              Column Mapping
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Map the columns in your spreadsheet to the fields
                              Cash Forecaster expects. Column letters refer to
                              the first sheet in the workbook.
                            </p>
                          </div>

                          {/* Data preview */}
                          {preview && excelSchema && (
                            <div className="space-y-2 min-w-0">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
                                  onClick={() => setPreviewExpanded((v) => !v)}
                                >
                                  {previewExpanded ? (
                                    <ChevronUp className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  )}
                                  File Preview
                                </button>
                                <Badge
                                  variant="outline"
                                  className="text-xs h-4 px-1.5"
                                >
                                  First 5 rows from your file
                                </Badge>
                                {previewExpanded && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground ml-1">
                                    <MousePointerClick className="h-3 w-3" />
                                    Click a column header to assign it
                                  </span>
                                )}
                                <div className="ml-auto">
                                  {((
                                    excelConfig?.columnMappings?.[def.key] as
                                      | ExcelColumnMap[]
                                      | undefined
                                  )?.length ?? 0) > 0 && (
                                    <button
                                      type="button"
                                      className="text-xs text-muted-foreground hover:text-destructive underline underline-offset-2 transition-colors"
                                      onClick={() => {
                                        if (!selectedSource) return;
                                        setSelectedSource({
                                          ...selectedSource,
                                          connectionDetails: {
                                            ...selectedSource.connectionDetails,
                                            excelConfig: {
                                              ...selectedSource
                                                .connectionDetails
                                                ?.excelConfig!,
                                              columnMappings: {
                                                ...(excelConfig?.columnMappings ??
                                                  {}),
                                                [def.key]: [],
                                              },
                                            },
                                          },
                                        });
                                        setMappingDirty(true);
                                      }}
                                    >
                                      Clear all mappings
                                    </button>
                                  )}
                                </div>
                              </div>
                              {previewExpanded && (
                                <div className="border rounded-lg overflow-x-auto max-w-full">
                                  {preview.headers.length > 15 && (
                                    <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-950/30 border-b flex items-center gap-1.5">
                                      <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                      <p className="text-xs text-amber-700 dark:text-amber-400">
                                        Your file has {preview.headers.length}{" "}
                                        columns — showing the first 15. All
                                        columns are available for mapping below.
                                      </p>
                                    </div>
                                  )}
                                  <table className="text-xs min-w-max">
                                    <thead className="bg-muted/40 border-b">
                                      <tr>
                                        <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground w-8">
                                          #
                                        </th>
                                        {preview.headers
                                          .slice(0, 15)
                                          .map((h, i) => {
                                            const colLetter =
                                              String.fromCharCode(65 + i);
                                            const assignedField =
                                              existingColMaps.find(
                                                (m) =>
                                                  m.columnLetter === colLetter,
                                              );
                                            const unmappedFields =
                                              excelSchema.filter(
                                                (f) =>
                                                  !existingColMaps.some(
                                                    (m) =>
                                                      m.forecasterId ===
                                                        f.forecasterId &&
                                                      m.columnLetter,
                                                  ),
                                              );
                                            return (
                                              <th
                                                key={i}
                                                className={`px-0 py-0 text-left font-semibold whitespace-nowrap min-w-[120px] ${assignedField ? "bg-primary/8" : ""}`}
                                              >
                                                <Popover>
                                                  <PopoverTrigger asChild>
                                                    <button
                                                      type="button"
                                                      className={`w-full px-3 py-1.5 text-left flex items-center gap-1.5 group transition-colors cursor-pointer rounded-none hover:bg-primary/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary ${
                                                        assignedField
                                                          ? "bg-primary/8"
                                                          : ""
                                                      }`}
                                                    >
                                                      <span className="text-primary font-mono">
                                                        {colLetter}
                                                      </span>
                                                      <span className="text-muted-foreground">
                                                        {h}
                                                      </span>
                                                      {assignedField && (
                                                        <Badge
                                                          variant="secondary"
                                                          className="text-[10px] h-4 px-1 ml-0.5 shrink-0"
                                                        >
                                                          {
                                                            assignedField.forecasterLabel
                                                          }
                                                        </Badge>
                                                      )}
                                                    </button>
                                                  </PopoverTrigger>
                                                  <PopoverContent
                                                    className="w-56 p-0"
                                                    align="start"
                                                    side="bottom"
                                                  >
                                                    <div className="px-3 py-2 border-b">
                                                      <p className="text-xs font-semibold">
                                                        Assign column{" "}
                                                        <span className="font-mono text-primary">
                                                          {colLetter}
                                                        </span>{" "}
                                                        ({h})
                                                      </p>
                                                      <p className="text-xs text-muted-foreground mt-0.5">
                                                        Which forecaster field
                                                        does this map to?
                                                      </p>
                                                    </div>
                                                    <div className="py-1 max-h-52 overflow-y-auto">
                                                      {/* Currently assigned — show clear option */}
                                                      {assignedField && (
                                                        <button
                                                          type="button"
                                                          className="w-full px-3 py-1.5 text-left text-xs flex items-center justify-between gap-2 hover:bg-muted/60 text-destructive"
                                                          onClick={() => {
                                                            if (!selectedSource)
                                                              return;
                                                            const currentMaps =
                                                              (excelConfig
                                                                ?.columnMappings?.[
                                                                def.key
                                                              ] as
                                                                | ExcelColumnMap[]
                                                                | undefined) ??
                                                              [];
                                                            const updated =
                                                              currentMaps.filter(
                                                                (m) =>
                                                                  m.columnLetter !==
                                                                  colLetter,
                                                              );
                                                            setSelectedSource({
                                                              ...selectedSource,
                                                              connectionDetails:
                                                                {
                                                                  ...selectedSource.connectionDetails,
                                                                  excelConfig: {
                                                                    ...selectedSource
                                                                      .connectionDetails
                                                                      ?.excelConfig!,
                                                                    columnMappings:
                                                                      {
                                                                        ...(excelConfig?.columnMappings ??
                                                                          {}),
                                                                        [def.key]:
                                                                          updated,
                                                                      },
                                                                  },
                                                                },
                                                            });
                                                            setMappingDirty(
                                                              true,
                                                            );
                                                          }}
                                                        >
                                                          <span>
                                                            Clear assignment (
                                                            {
                                                              assignedField.forecasterLabel
                                                            }
                                                            )
                                                          </span>
                                                          <X className="h-3 w-3 shrink-0" />
                                                        </button>
                                                      )}
                                                      {/* Unmapped fields */}
                                                      {unmappedFields.length ===
                                                        0 &&
                                                        !assignedField && (
                                                          <p className="px-3 py-2 text-xs text-muted-foreground">
                                                            All fields are
                                                            already mapped.
                                                          </p>
                                                        )}
                                                      {unmappedFields.map(
                                                        (field) => (
                                                          <button
                                                            key={
                                                              field.forecasterId
                                                            }
                                                            type="button"
                                                            className="w-full px-3 py-1.5 text-left text-xs flex items-center justify-between gap-2 hover:bg-muted/60"
                                                            onClick={() => {
                                                              if (
                                                                !selectedSource
                                                              )
                                                                return;
                                                              const currentMaps =
                                                                (excelConfig
                                                                  ?.columnMappings?.[
                                                                  def.key
                                                                ] as
                                                                  | ExcelColumnMap[]
                                                                  | undefined) ??
                                                                [];
                                                              // Remove any existing mapping for this field or this column letter
                                                              const updated =
                                                                currentMaps.filter(
                                                                  (m) =>
                                                                    m.forecasterId !==
                                                                      field.forecasterId &&
                                                                    m.columnLetter !==
                                                                      colLetter,
                                                                );
                                                              updated.push({
                                                                forecasterId:
                                                                  field.forecasterId,
                                                                forecasterLabel:
                                                                  field.forecasterLabel,
                                                                columnLetter:
                                                                  colLetter,
                                                                required:
                                                                  field.required,
                                                              });
                                                              setSelectedSource(
                                                                {
                                                                  ...selectedSource,
                                                                  connectionDetails:
                                                                    {
                                                                      ...selectedSource.connectionDetails,
                                                                      excelConfig:
                                                                        {
                                                                          ...selectedSource
                                                                            .connectionDetails
                                                                            ?.excelConfig!,
                                                                          columnMappings:
                                                                            {
                                                                              ...(excelConfig?.columnMappings ??
                                                                                {}),
                                                                              [def.key]:
                                                                                updated,
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                              );
                                                              setMappingDirty(
                                                                true,
                                                              );
                                                            }}
                                                          >
                                                            <span>
                                                              {
                                                                field.forecasterLabel
                                                              }
                                                            </span>
                                                            {field.required && (
                                                              <Badge
                                                                variant="secondary"
                                                                className="text-[10px] h-4 px-1 shrink-0"
                                                              >
                                                                Required
                                                              </Badge>
                                                            )}
                                                          </button>
                                                        ),
                                                      )}
                                                    </div>
                                                  </PopoverContent>
                                                </Popover>
                                              </th>
                                            );
                                          })}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {preview.rows
                                        .slice(0, 5)
                                        .map((row, ri) => (
                                          <tr
                                            key={ri}
                                            className={
                                              ri % 2 === 0
                                                ? "bg-background"
                                                : "bg-muted/20"
                                            }
                                          >
                                            <td className="px-2 py-1.5 text-muted-foreground font-mono">
                                              {ri + 2}
                                            </td>
                                            {row
                                              .slice(0, 15)
                                              .map((cell, ci) => {
                                                const colLetter =
                                                  String.fromCharCode(65 + ci);
                                                const isMapped =
                                                  existingColMaps.some(
                                                    (m) =>
                                                      m.columnLetter ===
                                                      colLetter,
                                                  );
                                                return (
                                                  <td
                                                    key={ci}
                                                    className={`px-3 py-1.5 whitespace-nowrap font-mono ${isMapped ? "bg-primary/5" : ""}`}
                                                  >
                                                    {cell}
                                                  </td>
                                                );
                                              })}
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {previewExpanded
                                  ? "Click a column header to assign it, or type the column letter directly in the mapping table below."
                                  : 'Preview collapsed — click "File Preview" above to expand.'}
                              </p>
                            </div>
                          )}

                          {/* Column mapping table */}
                          {excelSchema ? (
                            <div className="border rounded-lg overflow-hidden overflow-x-auto max-w-full">
                              <div className="grid grid-cols-[1fr_120px_auto] gap-0 bg-muted/40 border-b px-4 py-2">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                  Forecaster expects
                                </span>
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                  Column
                                </span>
                                <span className="w-6" />
                              </div>
                              {excelSchema.map((field) => {
                                const existing = existingColMaps.find(
                                  (m) => m.forecasterId === field.forecasterId,
                                );
                                const colLetter = existing?.columnLetter ?? "";
                                const typeWarning = getColumnTypeWarning(
                                  field.fieldType,
                                  colLetter,
                                  preview,
                                );
                                return (
                                  <div
                                    key={field.forecasterId}
                                    className="grid grid-cols-[1fr_120px_auto] gap-3 items-center px-4 py-2.5 border-b last:border-0 hover:bg-muted/20"
                                  >
                                    <div>
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-sm font-medium">
                                          {field.forecasterLabel}
                                        </span>
                                        {field.required && (
                                          <Badge
                                            variant="secondary"
                                            className="text-xs h-4 px-1"
                                          >
                                            Required
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {field.description}
                                      </p>
                                      {typeWarning && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                                          <AlertTriangle className="h-3 w-3 shrink-0" />
                                          {typeWarning}
                                        </p>
                                      )}
                                      {colLetter &&
                                        editDuplicateColumnLetters.has(
                                          colLetter.toUpperCase(),
                                        ) && (
                                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5 flex items-center gap-1">
                                            <AlertCircle className="h-3 w-3 shrink-0" />
                                            Duplicate column mapping — column{" "}
                                            {colLetter} is used by another field
                                          </p>
                                        )}
                                    </div>
                                    <Input
                                      className="h-8 text-sm font-mono uppercase w-24 text-center"
                                      placeholder="A"
                                      maxLength={3}
                                      value={colLetter}
                                      onChange={(e) => {
                                        const letter =
                                          e.target.value.toUpperCase();
                                        if (selectedSource) {
                                          const currentMaps =
                                            (excelConfig?.columnMappings?.[
                                              def.key
                                            ] as
                                              | ExcelColumnMap[]
                                              | undefined) ?? [];
                                          const updated = currentMaps.filter(
                                            (m) =>
                                              m.forecasterId !==
                                              field.forecasterId,
                                          );
                                          if (letter)
                                            updated.push({
                                              forecasterId: field.forecasterId,
                                              forecasterLabel:
                                                field.forecasterLabel,
                                              columnLetter: letter,
                                              required: field.required,
                                            });
                                          setSelectedSource({
                                            ...selectedSource,
                                            connectionDetails: {
                                              ...selectedSource.connectionDetails,
                                              excelConfig: {
                                                ...selectedSource
                                                  .connectionDetails
                                                  ?.excelConfig!,
                                                columnMappings: {
                                                  ...(excelConfig?.columnMappings ??
                                                    {}),
                                                  [def.key]: updated,
                                                },
                                              },
                                            },
                                          });
                                          setMappingDirty(true);
                                        }
                                      }}
                                    />
                                    <div className="w-6 flex items-center justify-center">
                                      {colLetter &&
                                      editDuplicateColumnLetters.has(
                                        colLetter.toUpperCase(),
                                      ) ? (
                                        <AlertCircle className="h-4 w-4 text-orange-500" />
                                      ) : colLetter ? (
                                        <Check className="h-4 w-4 text-green-500" />
                                      ) : field.required ? (
                                        <AlertCircle className="h-4 w-4 text-amber-400" />
                                      ) : (
                                        <span className="h-4 w-4" />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-8 border-2 border-dashed rounded-lg">
                              <GitMerge className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                              <p className="text-sm text-muted-foreground">
                                No column schema defined for {def.label} yet.
                              </p>
                            </div>
                          )}

                          {/* Format config strip */}
                          <div className="border rounded-lg p-4 bg-muted/10 space-y-3">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              Format Settings
                            </h4>
                            <div className="space-y-1 max-w-xs">
                              <Label className="text-xs">Date Format</Label>
                              <Select
                                value={excelConfig?.dateFormat ?? "DD/MM/YYYY"}
                                onValueChange={(v) => {
                                  if (selectedSource)
                                    setSelectedSource({
                                      ...selectedSource,
                                      connectionDetails: {
                                        ...selectedSource.connectionDetails,
                                        excelConfig: {
                                          ...selectedSource.connectionDetails
                                            ?.excelConfig!,
                                          dateFormat: v as ExcelDateFormat,
                                        },
                                      },
                                    });
                                  setMappingDirty(true);
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="DD/MM/YYYY">
                                    DD/MM/YYYY
                                  </SelectItem>
                                  <SelectItem value="MM/DD/YYYY">
                                    MM/DD/YYYY
                                  </SelectItem>
                                  <SelectItem value="YYYY-MM-DD">
                                    YYYY-MM-DD
                                  </SelectItem>
                                  <SelectItem value="DD-MM-YYYY">
                                    DD-MM-YYYY
                                  </SelectItem>
                                  <SelectItem value="excel_serial">
                                    Excel Serial Number
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                How dates are written in this spreadsheet.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* ── API SOURCES: existing field mapper ── */
                        <div className="space-y-3">
                          {(() => {
                            const isAutoMapped = [
                              "Access Financials",
                              "Access Dimensions Cloud",
                              "Paycircle",
                              "Plaid",
                            ].includes(selectedSource.type);
                            return (
                              <div>
                                <h3 className="text-sm font-semibold">
                                  Field Mapping
                                </h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {isAutoMapped ? (
                                    <span className="text-green-600 dark:text-green-400 font-medium">
                                      ✓ Field mapping is automatically managed
                                      by Access — no manual configuration
                                      required.
                                    </span>
                                  ) : schema?.productDefaults[
                                      selectedSource.type
                                    ] ? (
                                    <>
                                      Map the field names from{" "}
                                      <strong>{selectedSource.name}</strong>'s
                                      API to the fields Cash Forecaster expects.
                                      <span className="ml-1 text-amber-600 dark:text-amber-400 font-medium">
                                        Pre-populated with known defaults —
                                        review and adjust if needed.
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      Map the field names from{" "}
                                      <strong>{selectedSource.name}</strong>'s
                                      API to the fields Cash Forecaster expects.
                                      <span className="ml-1 text-amber-600 dark:text-amber-400 font-medium">
                                        No pre-built template for this product —
                                        enter your API field names manually.
                                      </span>
                                    </>
                                  )}
                                </p>
                              </div>
                            );
                          })()}

                          {schema ? (
                            <div className="space-y-2">
                              {/* Search box */}
                              <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                                <Input
                                  className="h-8 pl-8 text-sm"
                                  placeholder="Search fields…"
                                  value={mappingSearchQuery}
                                  onChange={(e) =>
                                    setMappingSearchQuery(e.target.value)
                                  }
                                />
                              </div>
                              <div className="border rounded-lg overflow-hidden">
                                {/* Header row */}
                                <div className="grid grid-cols-[1fr_1fr_auto] gap-0 bg-muted/40 border-b px-4 py-2">
                                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Forecaster expects
                                  </span>
                                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    {[
                                      "Access Financials",
                                      "Access Dimensions Cloud",
                                      "Paycircle",
                                      "Plaid",
                                    ].includes(selectedSource.type)
                                      ? "Source field"
                                      : `${selectedSource.name} field name`}
                                  </span>
                                  <span className="w-6" />
                                </div>
                                {/* Field rows — filtered by search */}
                                {(() => {
                                  const q = mappingSearchQuery
                                    .toLowerCase()
                                    .trim();
                                  const filteredFields = q
                                    ? schema.fields.filter(
                                        (f) =>
                                          f.forecasterLabel
                                            .toLowerCase()
                                            .includes(q) ||
                                          f.description
                                            .toLowerCase()
                                            .includes(q) ||
                                          (
                                            currentFieldMap[f.forecasterId] ??
                                            ""
                                          )
                                            .toLowerCase()
                                            .includes(q),
                                      )
                                    : schema.fields;
                                  if (filteredFields.length === 0) {
                                    return (
                                      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                                        No fields match{" "}
                                        <strong>"{mappingSearchQuery}"</strong>
                                      </div>
                                    );
                                  }
                                  return filteredFields.map((field) => {
                                    const isAutoMapped = [
                                      "Access Financials",
                                      "Access Dimensions Cloud",
                                      "Paycircle",
                                      "Plaid",
                                    ].includes(selectedSource.type);
                                    return (
                                      <div
                                        key={field.forecasterId}
                                        className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center px-4 py-2.5 border-b last:border-0 hover:bg-muted/20"
                                      >
                                        <div>
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-sm font-medium">
                                              {field.forecasterLabel}
                                            </span>
                                            {field.required && (
                                              <Badge
                                                variant="secondary"
                                                className="text-xs h-4 px-1"
                                              >
                                                Required
                                              </Badge>
                                            )}
                                          </div>
                                          <p className="text-xs text-muted-foreground mt-0.5">
                                            {field.description}
                                          </p>
                                        </div>
                                        {isAutoMapped ? (
                                          <div className="flex items-center gap-2">
                                            <span className="h-8 flex items-center px-3 rounded-md border border-border/50 bg-muted/40 text-xs font-medium text-green-600 dark:text-green-400 select-none">
                                              Auto Mapped
                                            </span>
                                          </div>
                                        ) : (
                                          <Input
                                            className="h-8 text-sm font-mono"
                                            placeholder={`Your field name…`}
                                            value={
                                              currentFieldMap[
                                                field.forecasterId
                                              ] ?? ""
                                            }
                                            onChange={(e) => {
                                              setFieldMappings((prev) => ({
                                                ...prev,
                                                [def.key]: {
                                                  ...(prev[def.key] ?? {}),
                                                  [field.forecasterId]:
                                                    e.target.value,
                                                },
                                              }));
                                              setMappingDirty(true);
                                            }}
                                          />
                                        )}
                                        <div className="w-6 flex items-center justify-center">
                                          {isAutoMapped ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                          ) : currentFieldMap[
                                              field.forecasterId
                                            ] ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                          ) : field.required ? (
                                            <AlertCircle className="h-4 w-4 text-amber-400" />
                                          ) : (
                                            <span className="h-4 w-4" />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8 border-2 border-dashed rounded-lg">
                              <GitMerge className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                              <p className="text-sm text-muted-foreground">
                                No field schema defined for {def.label} yet.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
          </Tabs>

          <div className="border-t px-6 py-4 flex items-center justify-between shrink-0 bg-background">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              {mappingDirty && (
                <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 inline-block" />
                  Unsaved changes
                </span>
              )}
              {!mappingDirty &&
                selectedSource?.mappingConfig?.lastUpdated &&
                configureActiveTab !== "connection" && (
                  <>
                    Last mapping saved{" "}
                    {new Date(
                      selectedSource.mappingConfig.lastUpdated,
                    ).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </>
                )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setConfigureDialogOpen(false)}
              >
                Cancel
              </Button>
              {/* Save & Set Up Sync Schedule — hidden for manual-upload Excel sources (no recurring sync possible) */}
              {!(
                selectedSource?.type === "Excel Spreadsheet" &&
                (selectedSource?.connectionDetails?.excelConfig?.fileMode ===
                  "manual_upload" ||
                  !selectedSource?.connectionDetails?.excelConfig?.fileMode)
              ) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    postSaveActionRef.current = "openSync";
                    handleSaveConfiguration();
                  }}
                  disabled={
                    isSavingConfig ||
                    isLoadingConfig ||
                    (() => {
                      const allMaps =
                        selectedSource?.connectionDetails?.excelConfig
                          ?.columnMappings;
                      if (!allMaps) return false;
                      return Object.values(allMaps).some((maps) => {
                        const letters = (maps as ExcelColumnMap[])
                          .filter((m) => m.columnLetter)
                          .map((m) => m.columnLetter.toUpperCase());
                        return letters.length !== new Set(letters).size;
                      });
                    })()
                  }
                >
                  {isSavingConfig &&
                    postSaveActionRef.current === "openSync" && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                  Save & Set Up Sync Schedule
                </Button>
              )}
              <Button
                onClick={handleSaveConfiguration}
                disabled={
                  isSavingConfig ||
                  isLoadingConfig ||
                  (() => {
                    const allMaps =
                      selectedSource?.connectionDetails?.excelConfig
                        ?.columnMappings;
                    if (!allMaps) return false;
                    return Object.values(allMaps).some((maps) => {
                      const letters = (maps as ExcelColumnMap[])
                        .filter((m) => m.columnLetter)
                        .map((m) => m.columnLetter.toUpperCase());
                      return letters.length !== new Set(letters).size;
                    });
                  })()
                }
                variant={mappingDirty ? "default" : "outline"}
              >
                {isSavingConfig && postSaveActionRef.current !== "openSync" && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Parent Category Source Confirmation Dialog */}
      <Dialog
        open={changeParentDialogOpen}
        onOpenChange={setChangePrimaryDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Parent Category Source?</DialogTitle>
            <DialogDescription>
              {
                displayDataSources.find((ds) => ds.parentCategory?.isPrimary)
                  ?.name
              }{" "}
              is currently set as parent category source. Do you want to switch
              to {newParentSource?.name} instead?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setChangePrimaryDialogOpen(false);
                setNewPrimarySource(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmSwitchParent}>
              Switch to {newParentSource?.name}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Parent Category Source Dialog (for Add New Source) */}
      <Dialog
        open={changeParentDialogOpenForAdd}
        onOpenChange={setChangePrimaryDialogOpenForAdd}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Parent Category Source?</DialogTitle>
            <DialogDescription>
              {newParentSourceForAdd?.name} is currently set as parent category
              source. Do you want to switch to the new data source instead?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setChangePrimaryDialogOpenForAdd(false);
                setNewPrimarySourceForAdd(null);
                setNewSourceIsPrimary(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmSwitchParentForAdd}>
              Switch to New Source
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Connection Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Connection</DialogTitle>
            <DialogDescription>{selectedSource?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {testing ? (
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm">Testing connection...</span>
              </div>
            ) : testResult === "success" ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-green-600">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Check className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">Connection successful!</p>
                    <p className="text-sm text-muted-foreground">
                      All systems are operational
                    </p>
                  </div>
                </div>
                <div className="border rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Response Time:
                    </span>
                    <span className="font-medium">142ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">API Version:</span>
                    <span className="font-medium">v2.1</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Last Health Check:
                    </span>
                    <span className="font-medium">Just now</span>
                  </div>
                </div>
              </div>
            ) : testResult === "error" ? (
              <div className="flex items-center gap-3 text-red-600">
                <div className="p-2 bg-red-100 rounded-full">
                  <X className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">Connection failed</p>
                  <p className="text-sm text-muted-foreground">
                    Please check your credentials
                  </p>
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Data Source Dialog */}
      <Dialog
        open={addSourceDialogOpen}
        onOpenChange={(open) => {
          setAddSourceDialogOpen(open);
          if (!open) resetNewSourceForm();
        }}
      >
        <DialogContent
          className={`max-h-[90vh] overflow-y-auto overflow-x-hidden transition-all duration-300 ${newSourceGenericType === "Excel" && (newSourceWizardStep === "mapping" || newSourceWizardStep === "format") ? "max-w-5xl" : "max-w-3xl"} ${newSourceGenericType === "Excel" && newSourceWizardStep === "mapping" ? "max-w-6xl" : ""}`}
        >
          <DialogHeader>
            <DialogTitle>Add New Data Source</DialogTitle>
            <DialogDescription>
              Connect any finance, payroll, banking, or spreadsheet source to
              Cash Forecaster
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4 min-w-0">
            {/* Step 1 — Name */}
            <div className="space-y-2">
              <Label>Data Source Name *</Label>
              <Input
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                placeholder="e.g. BuildRight — Xero"
              />
            </div>

            {/* Step 2 — Generic Source Type */}
            <div className="space-y-2">
              <Label>Source Type *</Label>
              <p className="text-xs text-muted-foreground">
                What category of system are you connecting?
              </p>
              <div className="grid grid-cols-4 gap-3">
                {(
                  [
                    {
                      value: "Finance",
                      icon: "🏦",
                      desc: "Accounting & finance systems",
                      demoOnly: false,
                    },
                    {
                      value: "Payroll",
                      icon: "👥",
                      desc: "Payroll & HR systems",
                      demoOnly: true,
                    },
                    {
                      value: "Banking",
                      icon: "🏧",
                      desc: "Bank feeds & open banking",
                      demoOnly: true,
                    },
                    {
                      value: "Excel",
                      icon: "📊",
                      desc: "Spreadsheet imports",
                      demoOnly: false,
                    },
                  ] as const
                )
                  .filter(({ demoOnly }) => mode === "demo" || !demoOnly)
                  .map(({ value, icon, desc }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setNewSourceGenericType(value);
                        setSelectedTransactionTypes(
                          getDefaultTransactionTypes(value),
                        );
                        setNewSourceProduct("");
                        setNewSourceType(
                          value === "Excel" ? "Excel Spreadsheet" : "",
                        );
                        setNewSourceInstance("");
                      }}
                      className={`rounded-lg border p-3 text-left transition-all ${
                        newSourceGenericType === value
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="text-xl mb-1">{icon}</div>
                      <div className="text-sm font-semibold">{value}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {desc}
                      </div>
                    </button>
                  ))}
              </div>
            </div>

            {/* Step 3 — Product (conditional on generic type, not Excel) */}
            {newSourceGenericType && newSourceGenericType !== "Excel" && (
              <div className="space-y-2">
                <Label>Product *</Label>
                <p className="text-xs text-muted-foreground">
                  Which specific product or system are you connecting?
                </p>
                <Select
                  value={newSourceProduct}
                  onValueChange={(value) => {
                    setNewSourceProduct(value);
                    setNewSourceInstance("");
                    // Map product to the DataSourceType the rest of the form expects
                    if (value === "Access Financials")
                      setNewSourceType("Access Financials");
                    else if (value === "Access Dimensions Cloud")
                      setNewSourceType("Access Dimensions Cloud");
                    else if (value === "Paycircle")
                      setNewSourceType("Paycircle");
                    else if (value === "Plaid") setNewSourceType("Plaid");
                    else setNewSourceType("" as DataSourceType); // Other / third-party — no pre-known type
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {newSourceGenericType === "Finance" && (
                      <>
                        <SelectItem value="Access Financials">
                          Access Financials
                        </SelectItem>
                        <SelectItem value="Access Dimensions Cloud">
                          Access Dimensions Cloud
                        </SelectItem>
                        {mode === "demo" && (
                          <>
                            <SelectItem value="Xero">Xero</SelectItem>
                            <SelectItem value="QuickBooks Online">
                              QuickBooks Online
                            </SelectItem>
                            <SelectItem value="Sage Business Cloud">
                              Sage Business Cloud
                            </SelectItem>
                            <SelectItem value="Other Finance OAuth">
                              Other finance system (OAuth 2.0)
                            </SelectItem>
                            <SelectItem value="Other Finance">
                              Other finance system
                            </SelectItem>
                          </>
                        )}
                      </>
                    )}
                    {newSourceGenericType === "Payroll" && (
                      <>
                        <SelectItem value="Paycircle">Paycircle</SelectItem>
                        <SelectItem value="Other Payroll OAuth">
                          Other payroll system (OAuth 2.0)
                        </SelectItem>
                        <SelectItem value="Other Payroll">
                          Other payroll system
                        </SelectItem>
                      </>
                    )}
                    {newSourceGenericType === "Banking" && (
                      <>
                        <SelectItem value="Plaid">Plaid</SelectItem>
                        <SelectItem value="TrueLayer">TrueLayer</SelectItem>
                        <SelectItem value="Other Banking OAuth">
                          Other bank feed provider (OAuth 2.0)
                        </SelectItem>
                        <SelectItem value="Other Banking">
                          Other bank feed provider
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>

                {/* Access Financials / Dimensions Cloud — auto-discovery banner */}
                {(newSourceProduct === "Access Financials" ||
                  newSourceProduct === "Access Dimensions Cloud") && (
                  <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 p-3 mt-2">
                    <span className="text-green-600 mt-0.5">✓</span>
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Access product detected
                      </p>
                      <p className="text-xs text-green-700 mt-0.5">
                        We can see your Access tenancy. Available instances will
                        be discovered automatically — no credentials needed.
                      </p>
                    </div>
                  </div>
                )}

                {/* Named OAuth third-party (Xero / QBO / Sage) — OAuth 2.0 disclaimer */}
                {(newSourceProduct === "Xero" ||
                  newSourceProduct === "QuickBooks Online" ||
                  newSourceProduct === "Sage Business Cloud") && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 mt-2">
                    <span className="text-amber-600 mt-0.5">⚠</span>
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        OAuth 2.0 connection — cloud version only
                      </p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Cash Forecaster connects to{" "}
                        <strong>{newSourceProduct}</strong> using OAuth 2.0.
                        This requires the cloud/SaaS version of{" "}
                        {newSourceProduct.split(" ")[0]}. Desktop, on-premise,
                        or legacy editions are not supported via this method —
                        use <strong>Excel</strong> as a catch-all data source
                        instead if you are on an unsupported version.
                      </p>
                      <p className="text-xs text-amber-700 mt-1.5">
                        You will need your <strong>Client ID</strong> and{" "}
                        <strong>Client Secret</strong> from your{" "}
                        {newSourceProduct.split(" ")[0]} developer application.
                        You'll then be redirected to authorise the connection.
                      </p>
                    </div>
                  </div>
                )}

                {/* Paycircle — OAuth 2.0 banner with bureau auth note */}
                {newSourceProduct === "Paycircle" && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 mt-2">
                    <span className="text-amber-600 mt-0.5">⚠</span>
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        OAuth 2.0 connection — API credentials required
                      </p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Cash Forecaster connects to <strong>Paycircle</strong>{" "}
                        using OAuth 2.0. You will need your{" "}
                        <strong>Client ID</strong>,{" "}
                        <strong>Client Secret</strong>, and{" "}
                        <strong>Bureau ID</strong> from your Paycircle account.
                        Field mapping will be pre-populated with known Paycircle
                        field names.
                      </p>
                      <p className="text-xs text-amber-700 mt-1.5">
                        Your bureau credentials provide access to all companies
                        within your bureau hierarchy. After connecting, you can
                        select which company to pull into the forecast.
                      </p>
                    </div>
                  </div>
                )}

                {/* Plaid / TrueLayer — institution-level OAuth banner */}
                {(newSourceProduct === "Plaid" ||
                  newSourceProduct === "TrueLayer") && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 mt-2">
                    <span className="text-amber-600 mt-0.5">⚠</span>
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        Institution-level bank connection — OAuth 2.0
                      </p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Cash Forecaster connects to your bank via{" "}
                        <strong>{newSourceProduct}</strong> using
                        institution-level authentication. You authenticate with
                        your bank <strong>once</strong> — {newSourceProduct}{" "}
                        then returns all available accounts at that institution
                        for you to select from.
                      </p>
                      <p className="text-xs text-amber-700 mt-1.5">
                        You will need your <strong>Client ID</strong> and{" "}
                        <strong>Client Secret</strong> from your{" "}
                        {newSourceProduct} developer application. You will then
                        be redirected to authenticate with your bank — no
                        separate login is required per account. Field mapping
                        will be pre-populated with known {newSourceProduct}{" "}
                        field names.
                      </p>
                    </div>
                  </div>
                )}

                {/* OAuth 2.0 catchall — generic OAuth connection */}
                {(newSourceProduct === "Other Finance OAuth" ||
                  newSourceProduct === "Other Payroll OAuth" ||
                  newSourceProduct === "Other Banking OAuth") && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 mt-2">
                    <span className="text-amber-600 mt-0.5">⚠</span>
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        OAuth 2.0 connection — generic
                      </p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Use this option for any third-party system that supports
                        OAuth 2.0. You will need your <strong>Client ID</strong>
                        , <strong>Client Secret</strong>, and the{" "}
                        <strong>Authorisation Endpoint URL</strong> from your
                        system's API documentation. Field mapping will need to
                        be configured manually after connecting.
                      </p>
                    </div>
                  </div>
                )}

                {/* Other / unknown third-party — redirect to Excel with actionable CTA */}
                {newSourceProduct &&
                  newSourceProduct !== "Access Financials" &&
                  newSourceProduct !== "Access Dimensions Cloud" &&
                  newSourceProduct !== "Xero" &&
                  newSourceProduct !== "QuickBooks Online" &&
                  newSourceProduct !== "Sage Business Cloud" &&
                  newSourceProduct !== "Paycircle" &&
                  newSourceProduct !== "Plaid" &&
                  newSourceProduct !== "TrueLayer" &&
                  newSourceProduct !== "Other Finance OAuth" &&
                  newSourceProduct !== "Other Payroll OAuth" &&
                  newSourceProduct !== "Other Banking OAuth" && (
                    <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 mt-2 space-y-2">
                      <div className="flex items-start gap-2">
                        <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">
                            No direct API integration for this system
                          </p>
                          <p className="text-xs text-blue-800 mt-0.5">
                            Cash Forecaster doesn't have a native connection for
                            this product — but you can still bring in your data.
                            Export a spreadsheet from your system and connect it
                            as an <strong>Excel data source</strong>. This works
                            with any system that can produce a .xlsx or .csv
                            export.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setNewSourceGenericType("Excel");
                          setSelectedTransactionTypes(
                            getDefaultTransactionTypes("Excel"),
                          );
                          setNewSourceProduct("");
                          setNewSourceType("");
                          setNewSourceInstance("");
                        }}
                        className="w-full rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-2 transition-colors flex items-center justify-center gap-2"
                      >
                        Set up as Excel data source →
                      </button>
                    </div>
                  )}
              </div>
            )}

            {/* Step 4a — Instance selection (Access products — auto-discovery) */}
            {(newSourceProduct === "Access Financials" ||
              newSourceProduct === "Access Dimensions Cloud") && (
              <div className="space-y-2">
                <Label>Select Instance *</Label>
                <Select
                  value={newSourceInstance}
                  onValueChange={setNewSourceInstance}
                  disabled={mode === "live" && isLoadingInstances}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        mode === "live" && isLoadingInstances
                          ? "Discovering instances..."
                          : newSourceProduct === "Access Financials"
                            ? "Choose your Access Financials instance"
                            : "Choose your Access Dimensions Cloud instance"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {mode === "live"
                      ? apiInstances.length > 0
                        ? [...apiInstances]
                            .sort((a, b) =>
                              a.name.localeCompare(b.name, undefined, {
                                numeric: true,
                                sensitivity: "base",
                              }),
                            )
                            .map((instance) => (
                              <SelectItem
                                key={instance.id}
                                value={instance.id}
                                disabled={instance.isConnected}
                              >
                                <div className="flex items-center justify-between w-full">
                                  <span
                                    className={
                                      instance.isConnected
                                        ? "text-muted-foreground"
                                        : ""
                                    }
                                  >
                                    {instance.name}
                                  </span>
                                  {instance.isConnected && (
                                    <Badge
                                      variant="secondary"
                                      className="ml-2 text-xs"
                                    >
                                      Already connected
                                    </Badge>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                        : !isLoadingInstances && (
                            <SelectItem value="__none__" disabled>
                              No instances available
                            </SelectItem>
                          )
                      : [
                          ...(availableInstances[
                            newSourceType as keyof typeof availableInstances
                          ] ?? []),
                        ]
                          .sort((a, b) =>
                            a.name.localeCompare(b.name, undefined, {
                              numeric: true,
                              sensitivity: "base",
                            }),
                          )
                          .map((instance) => (
                            <SelectItem
                              key={instance.id}
                              value={instance.id}
                              disabled={instance.isConnected}
                            >
                              <div className="flex items-center justify-between w-full">
                                <span
                                  className={
                                    instance.isConnected
                                      ? "text-muted-foreground"
                                      : ""
                                  }
                                >
                                  {instance.name}
                                </span>
                                {instance.isConnected && (
                                  <Badge
                                    variant="secondary"
                                    className="ml-2 text-xs"
                                  >
                                    Already connected
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 4b — Credentials: Paycircle (OAuth 2.0 — 3 fields) */}
            {newSourceProduct === "Paycircle" && (
              <div className="space-y-3">
                <Label>Connection Credentials *</Label>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client ID
                    </p>
                    <Input
                      type="text"
                      value={newSourceClientId}
                      onChange={(e) => setNewSourceClientId(e.target.value)}
                      placeholder="Your Paycircle OAuth Client ID"
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client Secret
                    </p>
                    <Input
                      type="password"
                      value={newSourceClientSecret}
                      onChange={(e) => setNewSourceClientSecret(e.target.value)}
                      placeholder="Your Paycircle OAuth Client Secret"
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Bureau ID
                    </p>
                    <Input
                      type="text"
                      value={newSourceBureauId}
                      onChange={(e) => setNewSourceBureauId(e.target.value)}
                      placeholder="Your Paycircle Bureau ID"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Credentials are encrypted and stored securely
                  </p>
                </div>
              </div>
            )}

            {/* Step 4b — Credentials: Xero / QBO / Sage (OAuth 2.0 — Client ID + Secret + Authorise button) */}
            {(newSourceProduct === "Xero" ||
              newSourceProduct === "QuickBooks Online" ||
              newSourceProduct === "Sage Business Cloud") && (
              <div className="space-y-3">
                <Label>Connection Credentials *</Label>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client ID
                    </p>
                    <Input
                      type="text"
                      value={newSourceClientId}
                      onChange={(e) => setNewSourceClientId(e.target.value)}
                      placeholder={`Your ${newSourceProduct.split(" ")[0]} OAuth Client ID`}
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client Secret
                    </p>
                    <Input
                      type="password"
                      value={newSourceClientSecret}
                      onChange={(e) => setNewSourceClientSecret(e.target.value)}
                      placeholder={`Your ${newSourceProduct.split(" ")[0]} OAuth Client Secret`}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Credentials are encrypted and stored securely
                  </p>
                  {newSourceClientId && newSourceClientSecret && (
                    <button
                      type="button"
                      className="w-full rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium px-3 py-2 transition-colors flex items-center justify-center gap-2 mt-1"
                      onClick={() => {
                        /* Live mode: redirect to OAuth authorisation URL */
                      }}
                    >
                      Authorise with {newSourceProduct} →
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Step 4b — Credentials: OAuth catchall (Client ID + Secret + Auth Endpoint URL) */}
            {(newSourceProduct === "Other Finance OAuth" ||
              newSourceProduct === "Other Payroll OAuth" ||
              newSourceProduct === "Other Banking OAuth") && (
              <div className="space-y-3">
                <Label>Connection Credentials *</Label>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client ID
                    </p>
                    <Input
                      type="text"
                      value={newSourceClientId}
                      onChange={(e) => setNewSourceClientId(e.target.value)}
                      placeholder="OAuth Client ID from your system"
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client Secret
                    </p>
                    <Input
                      type="password"
                      value={newSourceClientSecret}
                      onChange={(e) => setNewSourceClientSecret(e.target.value)}
                      placeholder="OAuth Client Secret from your system"
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Authorisation Endpoint URL
                    </p>
                    <Input
                      type="url"
                      value={newSourceAuthEndpoint}
                      onChange={(e) => setNewSourceAuthEndpoint(e.target.value)}
                      placeholder="https://your-system.com/oauth/authorize"
                    />
                    <p className="text-xs text-muted-foreground">
                      Found in your system's API or developer documentation
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Credentials are encrypted and stored securely
                  </p>
                </div>
              </div>
            )}

            {/* Step 4b — Credentials: Plaid / TrueLayer (institution-level OAuth) */}
            {(newSourceProduct === "Plaid" ||
              newSourceProduct === "TrueLayer") && (
              <div className="space-y-3">
                <Label>Connection Credentials *</Label>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client ID
                    </p>
                    <Input
                      type="text"
                      value={newSourceClientId}
                      onChange={(e) => setNewSourceClientId(e.target.value)}
                      placeholder={`Your ${newSourceProduct} OAuth Client ID`}
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">
                      Client Secret
                    </p>
                    <Input
                      type="password"
                      value={newSourceClientSecret}
                      onChange={(e) => setNewSourceClientSecret(e.target.value)}
                      placeholder={`Your ${newSourceProduct} OAuth Client Secret`}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Credentials are encrypted and stored securely
                  </p>
                  {newSourceClientId && newSourceClientSecret && (
                    <div className="space-y-2 pt-1">
                      <button
                        type="button"
                        className="w-full rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium px-3 py-2 transition-colors flex items-center justify-center gap-2"
                        onClick={() => {
                          /* Live mode: redirect to Plaid Link / TrueLayer OAuth flow — institution-level auth */
                        }}
                      >
                        Connect to Bank via {newSourceProduct} →
                      </button>
                      <p className="text-xs text-muted-foreground text-center">
                        You will be redirected to authenticate with your bank.
                        Once authenticated, {newSourceProduct} will return all
                        available accounts for you to select from.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4c — Plaid / TrueLayer: account selection after institution auth */}
            {(newSourceProduct === "Plaid" ||
              newSourceProduct === "TrueLayer") &&
              newSourceClientId &&
              newSourceClientSecret && (
                <div className="space-y-3">
                  <Label>Available Bank Accounts</Label>
                  <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      After authenticating with your bank via {newSourceProduct}
                      , the accounts available at that institution will appear
                      here. Select which accounts to include in Cash Forecaster.
                    </p>
                    <div className="space-y-2 pt-1">
                      {[
                        {
                          id: "acc-001",
                          name: "Business Current Account",
                          number: "****4821",
                          type: "Current",
                        },
                        {
                          id: "acc-002",
                          name: "Business Savings Account",
                          number: "****2247",
                          type: "Savings",
                        },
                        {
                          id: "acc-003",
                          name: "Reserve Account",
                          number: "****9103",
                          type: "Current",
                        },
                      ].map((account) => (
                        <label
                          key={account.id}
                          className="flex items-center gap-3 rounded-md border border-border bg-background p-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            defaultChecked={account.id === "acc-001"}
                            className="rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">
                              {account.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {account.number} · {account.type}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground pt-1">
                      Each selected account will be added as a separate bank
                      feed. You can add more accounts later by editing this data
                      source.
                    </p>
                  </div>
                </div>
              )}

            {/* Step 4c — Excel file config */}
            {newSourceGenericType === "Excel" && (
              <div className="space-y-3">
                {/* Transaction Type selection — shown in config step before file upload */}
                {newSourceWizardStep === "config" && (
                  <div className="space-y-3">
                    <div>
                      <Label>Transaction Types *</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Select the type of data this spreadsheet contains, then
                        download a template with the correct columns and sample
                        data. One type per data source.
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {TX_TYPE_GRID.flat().map((def) => {
                        const key = def.key as TxTypeKey;
                        const effectiveRelease =
                          def.key === "payroll" ? "R2" : def.release;
                        const isFuture = effectiveRelease;
                        const isChecked =
                          !isFuture &&
                          (selectedTransactionTypes as Record<string, boolean>)[
                            key
                          ];
                        return (
                          <button
                            key={key}
                            type="button"
                            disabled={effectiveRelease !== undefined}
                            onClick={() => {
                              if (isFuture) return;
                              const allOff =
                                getDefaultTransactionTypes("Excel");
                              setSelectedTransactionTypes({
                                ...allOff,
                                [key]: true,
                              });
                            }}
                            className={`relative rounded-lg border p-3 text-left transition-all ${
                              isFuture
                                ? "border-border bg-muted/30 opacity-50 cursor-not-allowed"
                                : isChecked
                                  ? "border-primary bg-primary/5 ring-1 ring-primary cursor-pointer"
                                  : "border-border hover:border-primary/50 cursor-pointer"
                            }`}
                          >
                            {isFuture && (
                              <span className="absolute top-1.5 right-1.5 text-[9px] font-bold bg-muted text-muted-foreground px-1 py-0.5 rounded">
                                {effectiveRelease}
                              </span>
                            )}
                            {!isFuture && (
                              <div
                                className={`absolute top-2 right-2 h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center ${isChecked ? "border-primary" : "border-muted-foreground"}`}
                              >
                                {isChecked && (
                                  <div className="h-2 w-2 rounded-full bg-primary" />
                                )}
                              </div>
                            )}
                            <div className="text-xs font-semibold pr-5">
                              {def.label}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5 pr-5 leading-tight">
                              {def.description}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {/* Download Template buttons */}
                    {(() => {
                      const selectedTxKey = (
                        Object.keys(selectedTransactionTypes) as TxTypeKey[]
                      ).find(
                        (k) =>
                          (
                            selectedTransactionTypes as Record<
                              TxTypeKey,
                              boolean
                            >
                          )[k],
                      ) as TxTypeKey | undefined;
                      const selectedTxLabel = selectedTxKey
                        ? (TX_TYPE_LABELS[selectedTxKey] ?? selectedTxKey)
                        : "";
                      return (
                        <div className="flex gap-2 pt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            disabled={!selectedTxKey}
                            onClick={async () => {
                              if (!selectedTxKey) return;
                              try {
                                const txKey =
                                  mode === "demo"
                                    ? "salesLedger"
                                    : selectedTxKey;
                                const blob = await apiDownloadBlob(
                                  `data-sources/templates/${txKey}?format=csv`,
                                );
                                downloadBlob(
                                  blob,
                                  `${selectedTxLabel}_Template.csv`,
                                );
                              } catch (err) {
                                console.error(
                                  "Failed to download CSV template:",
                                  err,
                                );
                                toast.error("Download failed", {
                                  description:
                                    "Could not download the CSV template. Please try again.",
                                });
                              }
                            }}
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Download CSV Template
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            disabled={!selectedTxKey}
                            onClick={async () => {
                              if (!selectedTxKey) return;
                              try {
                                const txKey =
                                  mode === "demo"
                                    ? "salesLedger"
                                    : selectedTxKey;
                                const blob = await apiDownloadBlob(
                                  `data-sources/templates/${txKey}?format=xlsx`,
                                );
                                downloadBlob(
                                  blob,
                                  `${selectedTxLabel}_Template.xlsx`,
                                );
                              } catch (err) {
                                console.error(
                                  "Failed to download Excel template:",
                                  err,
                                );
                                toast.error("Download failed", {
                                  description:
                                    "Could not download the Excel template. Please try again.",
                                });
                              }
                            }}
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Download Excel Template
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-sm">
                    File Password{" "}
                    <span className="text-muted-foreground font-normal text-xs">
                      (if protected)
                    </span>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showWizardPassword ? "text" : "password"}
                      value={newSourceFilePassword}
                      onChange={(e) => setNewSourceFilePassword(e.target.value)}
                      placeholder="Leave blank if not password protected"
                      className="pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWizardPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                      aria-label={
                        showWizardPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showWizardPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter the password before uploading so the file can be
                    opened and validated.
                  </p>
                </div>
                <Label>File Upload</Label>
                <div>
                  {/* Hidden real file input — triggered by button or drop */}
                  <input
                    id="upload-file-input"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleFileInputChange}
                  />

                  {/* Stage: idle */}
                  {uploadStage === "idle" && (
                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${uploadDragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setUploadDragOver(true);
                      }}
                      onDragLeave={() => setUploadDragOver(false)}
                      onDrop={handleUploadDrop}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm font-medium mb-1">
                        Drop your file here, or
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() =>
                          document.getElementById("upload-file-input")?.click()
                        }
                      >
                        Choose File
                      </Button>
                      <div className="flex items-center justify-center gap-2 mt-3">
                        <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                          .xlsx
                        </span>
                        <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                          .xls
                        </span>
                        <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                          .csv
                        </span>
                        <span className="text-xs text-muted-foreground ml-1">
                          · Max 10MB
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Stage: uploading */}
                  {uploadStage === "uploading" && (
                    <div className="border rounded-lg p-5 space-y-3">
                      <div className="flex items-center gap-3">
                        <File className="h-8 w-8 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {uploadFileName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Uploading...
                          </p>
                        </div>
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                      </div>
                      <div className="space-y-1">
                        <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-100"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground text-right">
                          {uploadProgress}%
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Stage: validating */}
                  {uploadStage === "validating" && (
                    <div className="border rounded-lg p-5 space-y-3">
                      <div className="flex items-center gap-3">
                        <File className="h-8 w-8 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {uploadFileName}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" />{" "}
                            Validating file structure...
                          </p>
                        </div>
                      </div>
                      <div className="space-y-1.5 pl-1">
                        {[
                          "File readable and undamaged",
                          "Column headers detected",
                          "Data rows found",
                        ].map((label, i) => (
                          <div
                            key={label}
                            className="flex items-center gap-2 text-xs"
                          >
                            {validationStep > i ? (
                              <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                            ) : (
                              <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30 shrink-0" />
                            )}
                            <span
                              className={
                                validationStep > i
                                  ? "text-foreground"
                                  : "text-muted-foreground"
                              }
                            >
                              {label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stage: success */}
                  {uploadStage === "success" && (
                    <div className="border border-green-200 dark:border-green-800 rounded-lg p-5 bg-green-50 dark:bg-green-950/30 space-y-3">
                      <div className="flex items-center gap-3">
                        <FileCheck className="h-8 w-8 text-green-600 dark:text-green-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {uploadFileName}
                          </p>
                          <p className="text-xs text-green-700 dark:text-green-400">
                            File uploaded and validated successfully
                          </p>
                        </div>
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                      </div>
                      <div className="grid grid-cols-3 gap-3 pt-1">
                        <div className="rounded bg-white dark:bg-background border px-3 py-2 text-center">
                          <p className="text-xs text-muted-foreground">
                            Sheet used
                          </p>
                          <p className="text-sm font-semibold mt-0.5">
                            {uploadMetadata?.WorksheetName_1 ??
                              (parsedFileData ? "Sheet 1" : "—")}
                          </p>
                        </div>
                        <div className="rounded bg-white dark:bg-background border px-3 py-2 text-center">
                          <p className="text-xs text-muted-foreground">
                            Data rows
                          </p>
                          <p className="text-sm font-semibold mt-0.5">
                            {uploadMetadata?.RowCount_1
                              ? Number(
                                  uploadMetadata.RowCount_1,
                                ).toLocaleString()
                              : parsedFileData
                                ? parsedFileData.rows.length.toLocaleString()
                                : "—"}
                          </p>
                        </div>
                        <div className="rounded bg-white dark:bg-background border px-3 py-2 text-center">
                          <p className="text-xs text-muted-foreground">
                            Columns found
                          </p>
                          <p className="text-sm font-semibold mt-0.5">
                            {uploadMetadata?.ColumnCount_1
                              ? Number(
                                  uploadMetadata.ColumnCount_1,
                                ).toLocaleString()
                              : parsedFileData
                                ? parsedFileData.headers.length
                                : "—"}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={resetUpload}
                        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                      >
                        Upload a different file
                      </button>
                    </div>
                  )}
                </div>
                {/* Sheet config — shown once a file has been uploaded */}
                {uploadStage === "success" && (
                  <div className="space-y-1 border-t pt-3">
                    <p className="text-xs text-muted-foreground">
                      Data will be read from the first sheet. Row 1 is treated
                      as the header row and row 2 onwards as data.
                    </p>
                  </div>
                )}
                {/* Continue to Format Declaration — requires type selected AND file uploaded */}
                {newSourceWizardStep === "config" &&
                  uploadStage === "success" &&
                  Object.values(selectedTransactionTypes).some(Boolean) && (
                    <div className="flex justify-end pt-2">
                      <Button
                        type="button"
                        onClick={() => setNewSourceWizardStep("format")}
                        className="gap-2"
                      >
                        Continue to Format Declaration
                        <span>→</span>
                      </Button>
                    </div>
                  )}
              </div>
            )}

            {/* Step 5 — Transaction Types (non-Excel sources; Excel uses inline grid in Step 4c) */}
            {((newSourceGenericType === "Finance" &&
              (newSourceInstance || newSourceClientId)) ||
              (newSourceGenericType === "Payroll" &&
                (newSourceInstance || newSourceApiKey || newSourceClientId)) ||
              (newSourceGenericType === "Banking" &&
                (newSourceApiKey || newSourceClientId))) && (
              <div className="space-y-3 border-t pt-4">
                <div>
                  <Label>Transaction Types *</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select which transaction types to pull from this source.
                    Only types relevant to this source type are available.
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {TX_TYPE_GRID.flat().map((def) => {
                    const key = def.key as TxTypeKey;
                    const isNA =
                      (newSourceGenericType === "Payroll" &&
                        !["payroll"].includes(key)) ||
                      (newSourceGenericType === "Banking" &&
                        !["bankAccounts", "cashbook"].includes(key)) ||
                      (newSourceGenericType === "Finance" && key === "payroll");
                    const isFuture = def.release; // R2 or R3 = not available yet
                    const isChecked =
                      !isNA &&
                      !isFuture &&
                      (selectedTransactionTypes as Record<string, boolean>)[
                        key
                      ];
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={isNA || isFuture !== undefined}
                        onClick={() => {
                          if (isNA || isFuture) return;
                          setSelectedTransactionTypes((prev) => ({
                            ...prev,
                            [key]: !prev[key as keyof typeof prev],
                          }));
                        }}
                        className={`relative rounded-lg border p-3 text-left transition-all ${
                          isNA || isFuture
                            ? "border-border bg-muted/30 opacity-50 cursor-not-allowed"
                            : isChecked
                              ? "border-primary bg-primary/5 ring-1 ring-primary cursor-pointer"
                              : "border-border hover:border-primary/50 cursor-pointer"
                        }`}
                      >
                        {isFuture && (
                          <span className="absolute top-1.5 right-1.5 text-[9px] font-bold bg-muted text-muted-foreground px-1 py-0.5 rounded">
                            {def.release}
                          </span>
                        )}
                        {isNA && (
                          <span className="absolute top-1.5 right-1.5 text-[9px] font-bold bg-muted text-muted-foreground px-1 py-0.5 rounded">
                            N/A
                          </span>
                        )}
                        {!isNA && !isFuture && (
                          <div
                            className={`absolute top-2 right-2 h-3.5 w-3.5 rounded border flex items-center justify-center ${isChecked ? "bg-primary border-primary" : "border-muted-foreground"}`}
                          >
                            {isChecked && (
                              <span className="text-white text-[9px] leading-none font-bold">
                                ✓
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-xs font-semibold pr-5">
                          {def.label}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 pr-5 leading-tight">
                          {def.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 5b — Format Declaration */}
            {newSourceGenericType === "Excel" &&
              newSourceWizardStep === "format" && (
                <div className="space-y-5 border-t pt-4">
                  {/* Header */}
                  <div>
                    <h3 className="text-sm font-semibold">
                      Format Declaration
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Tell us how numbers and dates are formatted in your
                      spreadsheet. We'll use this to correctly interpret your
                      data — you won't need to reformat your file.
                    </p>
                  </div>

                  {/* Number Format */}
                  <div className="border rounded-lg p-4 bg-muted/10 space-y-4">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Number Format
                    </h4>

                    {/* Negative representation */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">
                        How are negative values shown?
                      </Label>
                      <p className="text-[11px] text-muted-foreground">
                        These are the only three negative number formats we
                        accept. Number columns must contain plain numeric values
                        only — no currency symbols, colour-only indicators, or
                        CR/DR notation.
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {(
                          [
                            {
                              value: "minus_sign",
                              label: "Minus sign",
                              example: "-1234.56",
                            },
                            {
                              value: "brackets",
                              label: "Brackets",
                              example: "(1234.56)",
                            },
                            {
                              value: "trailing_minus",
                              label: "Trailing minus",
                              example: "1234.56-",
                            },
                          ] as {
                            value: ExcelNegativeFormat;
                            label: string;
                            example: string;
                          }[]
                        ).map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setNewSourceNegativeFormat(opt.value)
                            }
                            className={`rounded-lg border p-3 text-left transition-all ${newSourceNegativeFormat === opt.value ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/50"}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium">
                                {opt.label}
                              </span>
                              <div
                                className={`h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center ${newSourceNegativeFormat === opt.value ? "border-primary" : "border-muted-foreground"}`}
                              >
                                {newSourceNegativeFormat === opt.value && (
                                  <div className="h-2 w-2 rounded-full bg-primary" />
                                )}
                              </div>
                            </div>
                            <span className="text-[11px] text-muted-foreground font-mono">
                              {opt.example}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Date Format */}
                  <div className="border rounded-lg p-4 bg-muted/10 space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Date Format
                    </h4>
                    <p className="text-[11px] text-muted-foreground">
                      How are dates written in your file? Select the format that
                      matches your date columns.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {(
                        [
                          {
                            value: "DD/MM/YYYY",
                            label: "DD/MM/YYYY",
                            example: "31/01/2025",
                          },
                          {
                            value: "MM/DD/YYYY",
                            label: "MM/DD/YYYY",
                            example: "01/31/2025",
                          },
                          {
                            value: "YYYY-MM-DD",
                            label: "YYYY-MM-DD",
                            example: "2025-01-31",
                          },
                          {
                            value: "DD-MM-YYYY",
                            label: "DD-MM-YYYY",
                            example: "31-01-2025",
                          },
                          {
                            value: "excel_serial",
                            label: "Excel serial number",
                            example: "45658",
                          },
                          {
                            value: "other",
                            label: "Other format",
                            example: "Specify below",
                          },
                        ] as {
                          value: ExcelDateFormat;
                          label: string;
                          example: string;
                        }[]
                      ).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setNewSourceDateFormat(opt.value)}
                          className={`rounded-lg border p-3 text-left transition-all ${newSourceDateFormat === opt.value ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/50"}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium font-mono">
                              {opt.label}
                            </span>
                            <div
                              className={`h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center ${newSourceDateFormat === opt.value ? "border-primary" : "border-muted-foreground"}`}
                            >
                              {newSourceDateFormat === opt.value && (
                                <div className="h-2 w-2 rounded-full bg-primary" />
                              )}
                            </div>
                          </div>
                          <span className="text-[11px] text-muted-foreground">
                            {opt.example}
                          </span>
                        </button>
                      ))}
                    </div>
                    {/* Other — free text */}
                    {newSourceDateFormat === "other" && (
                      <div className="space-y-1 pt-1">
                        <Label className="text-xs">
                          Describe your date format
                        </Label>
                        <Input
                          placeholder="e.g. MMM DD YYYY (Jan 31 2025)"
                          className="text-xs h-8"
                          value={newSourceDateFormatOther}
                          onChange={(e) =>
                            setNewSourceDateFormatOther(e.target.value)
                          }
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Use a real example from your file so we can confirm
                          it's been read correctly in the next step.
                        </p>
                      </div>
                    )}
                    {/* Excel serial note */}
                    {newSourceDateFormat === "excel_serial" && (
                      <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
                        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-blue-500" />
                        <span>
                          Excel stores dates as serial numbers (e.g. 45658 = 31
                          Jan 2025). This is common when columns are formatted
                          as General or Number in Excel rather than Date.
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Continue button */}
                  <div className="flex justify-end pt-1">
                    <Button
                      type="button"
                      onClick={() => setNewSourceWizardStep("mapping")}
                      className="gap-2"
                    >
                      Continue to Column Mapping
                      <span>→</span>
                    </Button>
                  </div>
                </div>
              )}

            {/* Step 6 — Excel Column Mapping (wizard inline) */}
            {newSourceGenericType === "Excel" &&
              newSourceWizardStep === "mapping" &&
              (() => {
                const wizardTxKey = (
                  Object.keys(selectedTransactionTypes) as TxTypeKey[]
                ).find(
                  (k) =>
                    (selectedTransactionTypes as Record<TxTypeKey, boolean>)[k],
                ) as TxTypeKey | undefined;
                const wizardSchema = columnMappingPreview?.schema;
                const wizardColMaps: ExcelColumnMap[] = wizardTxKey
                  ? (newSourceWizardFieldMappings[wizardTxKey] ?? [])
                  : [];
                const wizardLabel = wizardTxKey
                  ? (TX_TYPE_LABELS[wizardTxKey] ?? wizardTxKey)
                  : "";
                const allRequiredMapped =
                  wizardSchema
                    ?.filter((f) => f.required)
                    .every((f) =>
                      wizardColMaps.some(
                        (m) =>
                          m.forecasterId === f.forecasterId && m.columnLetter,
                      ),
                    ) ?? false;
                const columnLetterCounts = wizardColMaps.reduce(
                  (acc, m) => {
                    if (m.columnLetter) {
                      const letter = m.columnLetter.toUpperCase();
                      acc[letter] = (acc[letter] || 0) + 1;
                    }
                    return acc;
                  },
                  {} as Record<string, number>,
                );
                const duplicateColumnLetters = new Set(
                  Object.entries(columnLetterCounts)
                    .filter(([_, count]) => count > 1)
                    .map(([letter]) => letter),
                );
                const hasDuplicateColumns = duplicateColumnLetters.size > 0;
                return (
                  <div className="space-y-5 border-t pt-4 min-w-0">
                    {/* Header */}
                    <div>
                      <h3 className="text-sm font-semibold">
                        Column Mapping — {wizardLabel}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        Map the columns in your spreadsheet to the fields the
                        forecaster expects. All required fields must be mapped
                        before you can save. Optional fields can be left blank.
                      </p>
                    </div>

                    {/* Worksheet Settings — fixed, no user input needed */}
                    <p className="text-xs text-muted-foreground">
                      Data is read from the first sheet only. Row 1 is the
                      header row; data begins on row 2. Column letters below
                      refer to your file's first sheet.
                    </p>

                    {/* Preview — real data from uploaded file */}
                    {parsedFileData && (
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
                            onClick={() => setPreviewExpanded((v) => !v)}
                          >
                            {previewExpanded ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )}
                            File Preview
                          </button>
                          <Badge
                            variant="outline"
                            className="text-xs h-4 px-1.5"
                          >
                            First 5 rows from your file
                          </Badge>
                        </div>
                        {previewExpanded && (
                          <div className="border rounded-lg overflow-x-auto max-w-full">
                            {parsedFileData.headers.length > 15 && (
                              <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-950/30 border-b flex items-center gap-1.5">
                                <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                  Your file has {parsedFileData.headers.length}{" "}
                                  columns — showing the first 15. All columns
                                  are available for mapping below.
                                </p>
                              </div>
                            )}
                            <table className="text-xs min-w-max">
                              <thead className="bg-muted/40 border-b">
                                <tr>
                                  <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground w-8">
                                    #
                                  </th>
                                  {parsedFileData.headers
                                    .slice(0, 15)
                                    .map((h, i) => (
                                      <th
                                        key={i}
                                        className="px-3 py-1.5 text-left font-semibold text-muted-foreground whitespace-nowrap min-w-[120px]"
                                      >
                                        {String.fromCharCode(65 + i)} — {h}
                                      </th>
                                    ))}
                                </tr>
                              </thead>
                              <tbody>
                                {parsedFileData.rows
                                  .slice(0, 5)
                                  .map((row, ri) => (
                                    <tr
                                      key={ri}
                                      className="border-b last:border-0 hover:bg-muted/20"
                                    >
                                      <td className="px-2 py-1.5 text-muted-foreground font-mono">
                                        {ri + 2}
                                      </td>
                                      {row.slice(0, 15).map((cell, ci) => (
                                        <td
                                          key={ci}
                                          className="px-3 py-1.5 whitespace-nowrap font-mono"
                                        >
                                          {cell}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {previewExpanded
                            ? "Column letters shown in the header — use these when mapping below."
                            : 'Preview collapsed — click "File Preview" above to expand.'}
                        </p>
                      </div>
                    )}

                    {/* Column mapping table */}
                    {wizardSchema ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Field Mapping
                          </p>
                          {wizardColMaps.length > 0 && (
                            <button
                              type="button"
                              className="text-xs text-muted-foreground hover:text-destructive underline underline-offset-2"
                              onClick={() =>
                                wizardTxKey &&
                                setNewSourceWizardFieldMappings((prev) => ({
                                  ...prev,
                                  [wizardTxKey]: [],
                                }))
                              }
                            >
                              Clear all mappings
                            </button>
                          )}
                        </div>
                        <div className="border rounded-lg overflow-hidden overflow-x-auto max-w-full">
                          <div className="grid grid-cols-[1fr_120px_auto] gap-0 bg-muted/40 border-b px-4 py-2">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              Forecaster expects
                            </span>
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              Column letter
                            </span>
                            <span className="w-6" />
                          </div>
                          {wizardSchema.map((field) => {
                            const existing = wizardColMaps.find(
                              (m) => m.forecasterId === field.forecasterId,
                            );
                            const colLetter = existing?.columnLetter ?? "";
                            const typeWarning = getColumnTypeWarning(
                              field.fieldType,
                              colLetter,
                              parsedFileData ?? undefined,
                            );
                            return (
                              <div
                                key={field.forecasterId}
                                className="grid grid-cols-[1fr_120px_auto] gap-3 items-center px-4 py-2.5 border-b last:border-0 hover:bg-muted/20"
                              >
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-sm font-medium">
                                      {field.forecasterLabel}
                                    </span>
                                    {field.required && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs h-4 px-1"
                                      >
                                        Required
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {field.description}
                                  </p>
                                  {typeWarning && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3 shrink-0" />
                                      {typeWarning}
                                    </p>
                                  )}
                                  {colLetter &&
                                    duplicateColumnLetters.has(
                                      colLetter.toUpperCase(),
                                    ) && (
                                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3 shrink-0" />
                                        Duplicate column mapping — column{" "}
                                        {colLetter} is used by another field
                                      </p>
                                    )}
                                </div>
                                <Input
                                  className="h-8 text-sm font-mono uppercase w-24 text-center"
                                  placeholder="A"
                                  maxLength={3}
                                  value={colLetter}
                                  onChange={(e) => {
                                    const letter = e.target.value.toUpperCase();
                                    if (!wizardTxKey) return;
                                    const current =
                                      newSourceWizardFieldMappings[
                                        wizardTxKey
                                      ] ?? [];
                                    const updated = current.filter(
                                      (m) =>
                                        m.forecasterId !== field.forecasterId,
                                    );
                                    if (letter)
                                      updated.push({
                                        forecasterId: field.forecasterId,
                                        forecasterLabel: field.forecasterLabel,
                                        columnLetter: letter,
                                        required: field.required,
                                      });
                                    setNewSourceWizardFieldMappings((prev) => ({
                                      ...prev,
                                      [wizardTxKey]: updated,
                                    }));
                                    setValidationResults(null);
                                    setValidationError(null); // Reset validation when mapping changes
                                  }}
                                />
                                <div className="w-6 flex items-center justify-center">
                                  {colLetter &&
                                  duplicateColumnLetters.has(
                                    colLetter.toUpperCase(),
                                  ) ? (
                                    <AlertCircle className="h-4 w-4 text-orange-500" />
                                  ) : colLetter ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : field.required ? (
                                    <AlertCircle className="h-4 w-4 text-amber-400" />
                                  ) : (
                                    <span className="h-4 w-4" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Required fields completion indicator */}
                        <div
                          className={`flex items-center gap-2 text-xs px-1 ${
                            hasDuplicateColumns
                              ? "text-orange-600 dark:text-orange-400"
                              : allRequiredMapped
                                ? "text-green-600 dark:text-green-400"
                                : "text-amber-600 dark:text-amber-400"
                          }`}
                        >
                          {hasDuplicateColumns ? (
                            <>
                              <AlertCircle className="h-3.5 w-3.5" /> Duplicate
                              column mappings detected — each column letter can
                              only be used once
                            </>
                          ) : allRequiredMapped ? (
                            <>
                              <Check className="h-3.5 w-3.5" /> All required
                              fields mapped — ready to save
                            </>
                          ) : (
                            <>
                              <AlertCircle className="h-3.5 w-3.5" /> Map all
                              required fields before saving
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed rounded-lg">
                        <GitMerge className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          No column schema defined for {wizardLabel} yet.
                        </p>
                      </div>
                    )}

                    {/* Data Validation — runs when all required fields are mapped */}
                    {allRequiredMapped &&
                      !hasDuplicateColumns &&
                      (parsedFileData || (mode === "live" && uploadedFileId)) &&
                      (() => {
                        const runValidation = async () => {
                          if (
                            mode === "live" &&
                            uploadedFileId &&
                            wizardTxKey
                          ) {
                            // Live mode: call the server-side validation API
                            setIsValidatingData(true);
                            setValidationError(null);
                            try {
                              const dateFormat =
                                newSourceDateFormat === "other" &&
                                newSourceDateFormatOther
                                  ? newSourceDateFormatOther
                                  : newSourceDateFormat;
                              const response: ValidateExcelDataSourceResponse =
                                await validateExcelDataSource({
                                  uploadedFileId,
                                  transactionType: wizardTxKey,
                                  columnMappings: wizardColMaps.map((m) => ({
                                    forecasterId: m.forecasterId,
                                    columnLetter: m.columnLetter,
                                    forecasterLabel: m.forecasterLabel,
                                  })),
                                  formatDeclaration: {
                                    negativeFormat: newSourceNegativeFormat,
                                    dateFormat,
                                  },
                                });
                              const issues: ValidationIssue[] =
                                response.errors.map((e) => ({
                                  rowNumber: e.rowNumber,
                                  columnLetter: e.columnLetter,
                                  fieldLabel: e.fieldName,
                                  expectedType: e.expected,
                                  cellValue: e.found,
                                }));
                              setValidationResults({
                                invalidRows: issues,
                                totalRows: response.totalRows,
                              });
                            } catch (err) {
                              const msg =
                                err instanceof ApiError
                                  ? err.message
                                  : "Validation request failed. Please try again.";
                              setValidationError(msg);
                            } finally {
                              setIsValidatingData(false);
                            }
                          } else if (parsedFileData) {
                            // Demo mode: local preview-based validation
                            const issues: ValidationIssue[] = [];
                            const totalRows = parsedFileData.rows.length;
                            parsedFileData.rows.forEach((row, ri) => {
                              wizardColMaps.forEach((mapping) => {
                                const schemaField = wizardSchema?.find(
                                  (f) =>
                                    f.forecasterId === mapping.forecasterId,
                                );
                                const ft = schemaField?.fieldType;
                                if (!ft || ft === "Text") return;
                                const colIndex =
                                  mapping.columnLetter
                                    .toUpperCase()
                                    .charCodeAt(0) - 65;
                                if (colIndex < 0 || colIndex >= row.length)
                                  return;
                                const cellValue = row[colIndex] ?? "";
                                if (cellValue.trim() === "") return;
                                const dateRe =
                                  /^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2}|\d{2}-\d{2}-\d{4}|\d{5})$/;
                                const currencyRe =
                                  /^-?[£$€]?[\d,]+(\.\d{1,2})?$|-?\([\d,]+(\.\d{1,2})?\)|-?[\d,]+(\.\d{1,2})?-$/;
                                let valid = true;
                                if (ft === "Date")
                                  valid = dateRe.test(cellValue.trim());
                                if (ft === "Currency" || ft === "Number")
                                  valid = currencyRe.test(
                                    cellValue.replace(/\s/g, ""),
                                  );
                                if (!valid) {
                                  issues.push({
                                    rowNumber: ri + 2,
                                    columnLetter: mapping.columnLetter,
                                    fieldLabel: mapping.forecasterLabel,
                                    expectedType:
                                      ft === "Date" ? "Date" : "Number",
                                    cellValue,
                                  });
                                }
                              });
                            });
                            setValidationResults({
                              invalidRows: issues,
                              totalRows,
                            });
                          }
                        };

                        if (validationResults === null) {
                          return (
                            <div className="space-y-2">
                              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                                <div className="flex items-start gap-3">
                                  <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                      Ready to validate
                                    </p>
                                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                                      All required fields are mapped. Run
                                      validation to check your data before
                                      saving — any rows with unexpected values
                                      will be shown for review.
                                    </p>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100"
                                    onClick={() => void runValidation()}
                                    disabled={isValidatingData}
                                  >
                                    {isValidatingData ? (
                                      <>
                                        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                                        Validating...
                                      </>
                                    ) : (
                                      "Validate data"
                                    )}
                                  </Button>
                                </div>
                              </div>
                              {validationError && (
                                <div className="border rounded-lg p-4 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800">
                                  <div className="flex items-start gap-3">
                                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-red-900 dark:text-red-100">
                                        Validation failed
                                      </p>
                                      <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                                        {validationError}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }

                        if (validationResults.invalidRows.length === 0) {
                          return (
                            <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 flex items-center gap-3">
                              <Check className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                                  All{" "}
                                  {validationResults.totalRows.toLocaleString()}{" "}
                                  rows passed validation
                                </p>
                                <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                                  No unexpected values found. Your data is ready
                                  to save.
                                </p>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div className="border rounded-lg overflow-hidden border-amber-200 dark:border-amber-800">
                            <div className="px-4 py-3 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 flex items-start gap-3">
                              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                                  {validationResults.invalidRows.length} row
                                  {validationResults.invalidRows.length !== 1
                                    ? "s"
                                    : ""}{" "}
                                  contain unexpected values
                                </p>
                                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                                  Out of{" "}
                                  {validationResults.totalRows.toLocaleString()}{" "}
                                  total rows. Choose how to proceed — invalid
                                  rows will be excluded from the import. To fix
                                  them, go back and re-upload a corrected file.
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100 text-xs"
                                onClick={() => {
                                  setValidationResults(null);
                                  setValidationError(null);
                                }}
                              >
                                Re-validate
                              </Button>
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              <table className="text-xs w-full">
                                <thead className="bg-muted/40 border-b sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                                      Row
                                    </th>
                                    <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                                      Field
                                    </th>
                                    <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                                      Column
                                    </th>
                                    <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                                      Expected
                                    </th>
                                    <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                                      Found
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {validationResults.invalidRows
                                    .slice(0, 50)
                                    .map((issue, i) => (
                                      <tr
                                        key={i}
                                        className="border-b last:border-0 hover:bg-muted/20"
                                      >
                                        <td className="px-3 py-1.5 font-mono text-muted-foreground">
                                          {issue.rowNumber}
                                        </td>
                                        <td className="px-3 py-1.5 font-medium">
                                          {issue.fieldLabel}
                                        </td>
                                        <td className="px-3 py-1.5 font-mono">
                                          {issue.columnLetter}
                                        </td>
                                        <td className="px-3 py-1.5 text-muted-foreground">
                                          {issue.expectedType}
                                        </td>
                                        <td className="px-3 py-1.5 font-mono text-amber-700 dark:text-amber-400 max-w-[160px] truncate">
                                          {issue.cellValue}
                                        </td>
                                      </tr>
                                    ))}
                                  {validationResults.invalidRows.length >
                                    50 && (
                                    <tr>
                                      <td
                                        colSpan={5}
                                        className="px-3 py-2 text-center text-muted-foreground"
                                      >
                                        … and{" "}
                                        {validationResults.invalidRows.length -
                                          50}{" "}
                                        more rows not shown
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            <div className="px-4 py-3 bg-muted/10 border-t border-amber-200 dark:border-amber-800 flex items-center justify-between gap-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setValidationResults(null);
                                  setValidationError(null);
                                  setUploadStage("idle");
                                  setParsedFileData(null);
                                  setPreviewExpanded(true);
                                  setNewSourceWizardStep("config");
                                }}
                              >
                                ← Back and fix file
                              </Button>
                              <p className="text-xs text-muted-foreground flex-1 text-right">
                                Proceeding will import{" "}
                                {(
                                  validationResults.totalRows -
                                  validationResults.invalidRows.length
                                ).toLocaleString()}{" "}
                                valid row
                                {validationResults.totalRows -
                                  validationResults.invalidRows.length !==
                                1
                                  ? "s"
                                  : ""}
                                , excluding the{" "}
                                {validationResults.invalidRows.length} shown
                                above.
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                );
              })()}

            {/* Step 6 — Parent Category Source (Finance only) */}
            {newSourceGenericType === "Finance" && newSourceInstance && (
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">
                      Primary Category Source
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Primary categories define how cash flow is organised. Only
                      one data source can be primary.
                    </p>
                  </div>
                  <Switch
                    checked={newSourceIsPrimary}
                    onCheckedChange={handleToggleNewSourceParent}
                  />
                </div>
                {newSourceIsPrimary && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Category Level *
                    </Label>
                    <Select
                      value={newSourceCategoryField}
                      onValueChange={setNewSourceCategoryField}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select nominal code level" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((level) => (
                          <SelectItem
                            key={level}
                            value={`Nominal Code - Level ${level}`}
                          >
                            {getNominalCategoryLabel(level)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Info tip — hidden for Excel in mapping step (mapping is done inline) */}
            {!(
              newSourceGenericType === "Excel" &&
              newSourceWizardStep === "mapping"
            ) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-900">
                  💡 <strong>After connecting</strong> — use the{" "}
                  <strong>Configure</strong> button on your new source to set up
                  field mapping, telling the forecaster exactly how to interpret
                  data from this system.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            {/* Back button — format step returns to config, mapping step returns to format */}
            {newSourceGenericType === "Excel" &&
              newSourceWizardStep === "format" && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setNewSourceWizardStep("config")}
                  disabled={isSubmitting}
                  className="mr-auto"
                >
                  ← Back
                </Button>
              )}
            {newSourceGenericType === "Excel" &&
              newSourceWizardStep === "mapping" && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setNewSourceWizardStep("format")}
                  disabled={isSubmitting}
                  className="mr-auto"
                >
                  ← Back
                </Button>
              )}
            <Button
              variant="outline"
              onClick={() => {
                setAddSourceDialogOpen(false);
                resetNewSourceForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNewSource}
              disabled={
                !newSourceName ||
                !newSourceGenericType ||
                isSubmitting ||
                (newSourceGenericType === "Excel" &&
                  newSourceWizardStep === "config") ||
                (newSourceGenericType === "Excel" &&
                  newSourceWizardStep === "format") ||
                (newSourceGenericType === "Excel" &&
                  newSourceWizardStep === "mapping" &&
                  !wizardRequiredMapped) ||
                (newSourceGenericType === "Excel" &&
                  newSourceWizardStep === "mapping" &&
                  validationResults === null)
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {newSourceGenericType === "Excel" ? "Saving..." : "Adding..."}
                </>
              ) : newSourceGenericType === "Excel" &&
                newSourceWizardStep === "mapping" ? (
                "Save Data Source"
              ) : (
                "Add Data Source"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Schedule Confirmation Dialog */}
      <Dialog
        open={!!disconnectSource}
        onOpenChange={(open) => !open && setDisconnectSource(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Disable Automated Sync?</DialogTitle>
            <DialogDescription>
              This will stop automated syncing for{" "}
              <strong>{disconnectSource?.name}</strong>. You'll need to use
              'Sync Now' manually.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setDisconnectSource(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDisableSchedule}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
            >
              Disable Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconnect Confirmation Dialog */}
      <Dialog
        open={!!reconnectSource}
        onOpenChange={(open) => !open && setReconnectSource(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reconnect {reconnectSource?.name}?</DialogTitle>
            <DialogDescription>
              This will resume syncing transactions from this data source.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setReconnectSource(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button onClick={confirmReconnect} className="w-full sm:w-auto">
              Reconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Warning Dialog - Step 1 */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete {deleteSource?.name}?</DialogTitle>
            <DialogDescription>
              This will permanently remove all data from this source
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Alert variant="destructive">
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>All synced transactions will be deleted</li>
                  <li>The forecast will refresh immediately</li>
                  <li>This action CANNOT be undone</li>
                </ul>
              </AlertDescription>
            </Alert>
            <p className="text-sm font-medium">Are you absolutely sure?</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteSource(null);
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={proceedToTypeConfirm}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              Continue to Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Type Confirmation Dialog - Step 2 */}
      <Dialog
        open={deleteTypeConfirmOpen}
        onOpenChange={setDeleteTypeConfirmOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Final Confirmation Required</DialogTitle>
            <DialogDescription>
              To confirm deletion of {deleteSource?.name}, type DELETE below:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Input
              value={deleteTypeInput}
              onChange={(e) => setDeleteTypeInput(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="font-mono"
            />
            <Alert>
              <AlertDescription>
                <p className="text-sm font-medium mb-1">
                  This will permanently remove:
                </p>
                <p className="text-sm">
                  {deleteSource?.recordCount?.toLocaleString() || 0} synced
                  transactions from {deleteSource?.name}
                </p>
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteTypeConfirmOpen(false);
                setDeleteSource(null);
                setDeleteTypeInput("");
              }}
              disabled={isDeleting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteTypeInput !== "DELETE" || isDeleting}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Source"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cannot Delete Last Source Dialog */}
      <Dialog
        open={cannotDeleteLastOpen}
        onOpenChange={setCannotDeleteLastOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Cannot Delete Last Data Source</DialogTitle>
            <DialogDescription>
              You must have at least one connected data source for cash flow
              forecasting to work.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              To delete {deleteSource?.name || "this data source"}, please add
              another data source first.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setCannotDeleteLastOpen(false)}
              className="w-full sm:w-auto"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Schedule Modal */}
      {selectedSource && (
        <SyncScheduleModal
          open={syncScheduleDialogOpen}
          onOpenChange={setSyncScheduleDialogOpen}
          dataSource={selectedSource}
          onSave={handleSaveSyncSchedule}
        />
      )}

      {/* Sync History Modal */}
      {syncHistorySource && (
        <SyncHistoryModal
          isOpen={!!syncHistorySource}
          onClose={() => setSyncHistorySource(null)}
          dataSourceName={syncHistorySource.name}
          dataSourceId={syncHistorySource.id}
        />
      )}
    </div>
  );
}
